import { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileEdit, UserPlus, AlertTriangle, CheckCircle, Send, 
  Building2, Users, ClipboardList, Eye, Mail, FileText,
  User, MapPin, Briefcase, GraduationCap, CreditCard, Phone,
  Calendar, ExternalLink, Download, X, UserCheck, Upload, Clock, StopCircle,
  FormInput, Globe, Scale, Edit, XCircle, Save, FileInput, History, LogOut, Search, RefreshCw, Inbox, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Papa from 'papaparse';
import Sidebar from '@/components/Sidebar';
import { formatDateDDMMYYYY, formatDateDDMonYYYY, formatPhoneMax10, isValidEmail } from '@/utils/textFormatting';
import NotificationCenter from '@/components/shared/NotificationCenter';
import { generateDashboardPDF } from '@/utils/pdfExport';
import CheckDetailsEntry from '@/components/qc/CheckDetailsEntry';
import QuickApproveAssign from '@/components/qc/QuickApproveAssign';
import CandidateDraftsTab from '@/components/qc/CandidateDraftsTab';
import PendingApprovalsTab from '@/components/qc/PendingApprovalsTab';
import SubmittedTab from '@/components/qc/SubmittedTab';
import PendingAssignTab from '@/components/qc/PendingAssignTab';
import CasesTab from '@/components/agency/CasesTab';
import { isStoppedCase } from '@/utils/caseFilters';

// Helper function to extract error message from API responses
const getErrorMessage = (error, defaultMsg) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message).join(', ');
  }
  return typeof detail === 'string' ? detail : defaultMsg;
};

// Check types with sub-options for customization
const CHECK_TYPES_WITH_SUB_OPTIONS = {
  address_check: {
    name: 'Address Verification',
    subOptions: [
      { id: 'present', name: 'Present Address' },
      { id: 'permanent', name: 'Permanent Address' },
      { id: 'past_1', name: 'Past Address 1' },
      { id: 'past_2', name: 'Past Address 2' },
      { id: 'past_3', name: 'Past Address 3' }
    ]
  },
  criminal_court_check: {
    name: 'Criminal Court Check',
    subOptions: [
      { id: 'present', name: 'Present' },
      { id: 'permanent', name: 'Permanent' },
      { id: 'past_1', name: 'Past 1' },
      { id: 'past_2', name: 'Past 2' },
      { id: 'past_3', name: 'Past 3' }
    ]
  },
  criminal_advocate_check: {
    name: 'Criminal Advocate Check',
    subOptions: [
      { id: 'present', name: 'Present' },
      { id: 'permanent', name: 'Permanent' },
      { id: 'past_1', name: 'Past 1' },
      { id: 'past_2', name: 'Past 2' },
      { id: 'past_3', name: 'Past 3' }
    ]
  },
  id_check: {
    name: 'ID Verification',
    subOptions: [
      { id: 'pan', name: 'PAN' },
      { id: 'aadhar', name: 'Aadhar' },
      { id: 'driving_license', name: 'Driving License' },
      { id: 'passport', name: 'Passport' },
      { id: 'others', name: 'Others' }
    ]
  },
  employment_check: {
    name: 'Employment Verification',
    subOptions: [
      { id: 'current', name: 'Current Employment' },
      { id: 'past_1', name: 'Past Employment 1' },
      { id: 'past_2', name: 'Past Employment 2' },
      { id: 'past_3', name: 'Past Employment 3' }
    ]
  },
  education_check: {
    name: 'Education Verification',
    subOptions: [
      { id: 'highest', name: 'Highest Qualification' },
      { id: 'prior_ssc', name: 'Prior to Highest - SSC' },
      { id: 'prior_hsc', name: 'Prior to Highest - HSC' },
      { id: 'diploma', name: 'Diploma' },
      { id: 'professional_course', name: 'Professional Course' }
    ]
  }
};

const CHECK_DISPLAY_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Check',
  'criminal_advocate_check': 'Criminal Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'global_database_check': 'Global Database Check'
};

export default function DataEntryDashboard({ user, onLogout, onSwitchAccount, embeddedMode = false }) {
  const [activeTab, setActiveTab] = useState(embeddedMode ? 'data-entry' : 'dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  const [packages, setPackages] = useState([]);
  const [entityContacts, setEntityContacts] = useState([]);
  const [insufficiencies, setInsufficiencies] = useState([]);
  const [insufficiencyFilter, setInsufficiencyFilter] = useState('all');
  const [insufficiencyCheckTypeFilter, setInsufficiencyCheckTypeFilter] = useState('all');
  // Age filter: 'all' | '<15' | '15-29' | '30-44' | '>=45'
  // Added May 24, 2026 to surface insufficiencies eligible for Amber Notice.
  const [insufficiencyAgeFilter, setInsufficiencyAgeFilter] = useState('all');
  const [insufficiencyClientSearch, setInsufficiencyClientSearch] = useState('');
  const [insufficiencyPage, setInsufficiencyPage] = useState(1);
  const [insufficiencyPageSize, setInsufficiencyPageSize] = useState(20);
  const [selectedInsufficiencies, setSelectedInsufficiencies] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingAmber, setSendingAmber] = useState(false);
  const [showAmberConfirm, setShowAmberConfirm] = useState(false);
  const [showCandidateEmailDialog, setShowCandidateEmailDialog] = useState(false);
  const [candidateEmailSelections, setCandidateEmailSelections] = useState({});
  const [pendingCases, setPendingCases] = useState([]);
  const [finalReportCases, setFinalReportCases] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showCaseDetail, setShowCaseDetail] = useState(null);
  const [showRaiseInsuff, setShowRaiseInsuff] = useState(null);
  const [showAssignAgent, setShowAssignAgent] = useState(null);
  const [showDrillDown, setShowDrillDown] = useState(null);
  const [drillDownData, setDrillDownData] = useState([]);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedContact, setSelectedContact] = useState('');
  const [caseDetailData, setCaseDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Pending QC Filters
  const [pendingQCSearch, setPendingQCSearch] = useState('');
  const [pendingQCClientFilter, setPendingQCClientFilter] = useState('all');
  const [pendingQCEntityFilter, setPendingQCEntityFilter] = useState('all');
  const [pendingQCDateFilter, setPendingQCDateFilter] = useState('all');
  const [pendingQCSortBy, setPendingQCSortBy] = useState('recent');
  const [pendingQCPage, setPendingQCPage] = useState(1);
  const [pendingQCPageSize] = useState(20);
  
  // Final Report QC Filters
  const [finalReportSearch, setFinalReportSearch] = useState('');
  const [finalReportClientFilter, setFinalReportClientFilter] = useState('all');
  const [finalReportEntityFilter, setFinalReportEntityFilter] = useState('all');
  const [finalReportDateFilter, setFinalReportDateFilter] = useState('all');
  const [finalReportSortBy, setFinalReportSortBy] = useState('recent');
  const [finalReportPage, setFinalReportPage] = useState(1);
  const [finalReportPageSize] = useState(20);
  
  // Edit Mode for Case Review
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Identity-change confirmation (anti-data-corruption guard)
  // Triggered when name / father_name / dob / email is about to change on a
  // submitted candidate. Prevents the RTX290426_00973 / 08:52:53 pattern
  // where an address-edit save silently wrote another candidate's identity.
  const [identityConfirm, setIdentityConfirm] = useState({
    open: false,
    diffs: [],          // [{field, from, to}]
    reason: '',
  });
  // Mirror-unlink confirmation (May 21, 2026) — surfaces when QC's edit
  // will diverge present and permanent addresses on a previously-mirrored
  // candidate, requiring the permanent address check to be reset for
  // independent vendor work.
  const [mirrorUnlinkConfirm, setMirrorUnlinkConfirm] = useState({ open: false });
  
  // Package search state for Add Employee form
  const [packageSearch, setPackageSearch] = useState('');
  
  // Confirmation before Assign
  const [showAssignConfirm, setShowAssignConfirm] = useState(null);
  const [assignConfirmData, setAssignConfirmData] = useState(null);
  const [bypassInsuffAssign, setBypassInsuffAssign] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  
  // Stop Check states
  const [showStopCheck, setShowStopCheck] = useState(null);
  const [stopCheckReason, setStopCheckReason] = useState('');
  
  // Case for edit check details
  const [caseForCheckEntry, setCaseForCheckEntry] = useState(null);
  const [dataEntrySubTab, setDataEntrySubTab] = useState('add-employee');
  const [stopCheckDescription, setStopCheckDescription] = useState('');
  const [stoppingCheck, setStoppingCheck] = useState(false);
  
  // Document preview state
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Audit Trail state
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  
  // Document replacement state
  const [replacingDoc, setReplacingDoc] = useState(null);  // {type: 'pan_card', entryIndex: null}
  const [docUploadProgress, setDocUploadProgress] = useState(false);
  
  // Entry-level insufficiency state
  const [showEntryInsuff, setShowEntryInsuff] = useState(null);
  
  // Check-wise pending data for QC Dashboard
  const [checkWisePending, setCheckWisePending] = useState({
    pending_approval_checks: [],
    pending_assign_checks: [],
    in_progress_checks: [],
    completed_checks: [],
    qc_completed_cases: [],
    check_type_stats: {},
    total_pending_approval: 0,
    total_pending_assign: 0,
    total_in_progress: 0,
    total_completed: 0,
    total_qc_completed_cases: 0
  });
  
  // Insufficiency aging buckets for QC dashboard widget
  const [insuffAging, setInsuffAging] = useState({
    total_cases_with_open_insuff: 0,
    buckets: {
      "0_20": { label: "0-20 days", count: 0, cases: [] },
      "21_45": { label: "21-45 days", count: 0, cases: [] },
      "over_45": { label: "Above 45 days", count: 0, cases: [] },
    },
  });
  const [insuffAgingDrilldown, setInsuffAgingDrilldown] = useState(null);

  // QC Completed cases filters & pagination
  const [qcCompletedSearch, setQcCompletedSearch] = useState('');
  const [qcCompletedClientFilter, setQcCompletedClientFilter] = useState('all');
  const [qcCompletedDateFilter, setQcCompletedDateFilter] = useState('all');
  
  // Check Wise Status filters
  const [cwsSearch, setCwsSearch] = useState('');
  const [cwsClientFilter, setCwsClientFilter] = useState('all');
  const [cwsStatusFilter, setCwsStatusFilter] = useState('all');
  const [cwsDateFilter, setCwsDateFilter] = useState('all');
  const [cwsAgentFilter, setCwsAgentFilter] = useState('all');
  const [cwsVendorFilter, setCwsVendorFilter] = useState('all');
  const [cwsPage, setCwsPage] = useState(1);
  const cwsPerPage = 20;
  const [qcCompletedSortOrder, setQcCompletedSortOrder] = useState('latest');
  const [qcCompletedPage, setQcCompletedPage] = useState(1);
  const qcCompletedPageSize = 20;
  
  // Bulk upload states
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkEntityId, setBulkEntityId] = useState('');
  const [bulkClientId, setBulkClientId] = useState('');
  const [bulkPackageId, setBulkPackageId] = useState('');
  const [sendBulkEmails, setSendBulkEmails] = useState(true);
  // Duplicate-confirmation state for Multi-Client Bulk Import
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  // Multi-Client Bulk Import mode — when ON, the rows themselves contain
  // Client Name / Entity name / Package, no top-level selectors are required,
  // and one row = one address (multiple rows per candidate fold into one case).
  // Default ON (Veecheck Feb 2026) — primary import flow for QC team.
  const [multiClientMode, setMultiClientMode] = useState(true);
  const fileInputRef = useRef(null);

  const [newEmployee, setNewEmployee] = useState({
    name: '',
    father_name: '',
    dob: '',
    email: '',
    phone: '',
    employee_code: '',
    entity_id: '',
    entity_contact_id: '',
    bypass_declaration: false,
    send_offline_signing: false,
    auto_create_case: true,
    package_id: '',
    custom_checks: [],
    send_invitation: false,
    generate_password: true,
    password: ''
  });

  const [insuffData, setInsuffData] = useState({
    check_id: '',
    check_type: '',
    entry_type: '',
    entry_index: -1,
    entry_name: '',
    reason: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Bulk upload template CSV
  // `same_as_present` (May 21, 2026) — when set to yes/y/true/1, the
  // candidate's permanent address is treated as a mirror of present
  // and the permanent address_check is auto-synced from present (no
  // duplicate vendor work). Leave blank to let the importer auto-detect
  // when present and permanent fields are pairwise identical.
  const BULK_TEMPLATE_HEADERS = [
    'employee_code', 'name', 'joining_company_name', 'father_name', 'dob', 'gender', 'email', 'phone', 'alternate_contact',
    'pan_number', 'aadhar_number',
    'present_address_line1', 'present_address_line2', 'present_city', 'present_state', 'present_pincode', 'present_duration',
    'permanent_address_line1', 'permanent_address_line2', 'permanent_city', 'permanent_state', 'permanent_pincode',
    'same_as_present'
  ];

  const downloadBulkTemplate = () => {
    if (multiClientMode) {
      // Multi-Client Bulk Import template (Veecheck Feb 2026):
      // - All fields optional
      // - One row = one address. Multiple rows for the same candidate
      //   (same Client + employee_code or Name) collapse into ONE case.
      // - Address Type values: Present / Permanent / Past 1 / Past 2
      const headers = [
        'Client Name', 'Entity name', 'Package',
        'employee_code', 'joining_company_name',
        'name', 'father_name', 'dob', 'phone', 'alternate_number',
        'case_received_date',
        'address_type',
        'address_line1', 'address_line2', 'city', 'state', 'pincode', 'duration'
      ].join(',');
      const sample = [
        '"Bizdev Consulting"', '"Bizdev Consulting"', '"BIzdev Address"',
        'BIZ001', '"Bizdev Consulting"',
        '"Ashik P Jose"', '"Jose Pazhukkayil"', '1990-05-15', '9876543210', '9123456780',
        '13-06-2026',
        'Present',
        '"Pazhiyankal (H), Mulakuvally"', '"Velladevi P.O"', 'Karnataka', 'Karnataka', '670571', '5 years'
      ].join(',');
      const sample2 = [
        '"Bizdev Consulting"', '"Bizdev Consulting"', '"BIzdev Address"',
        'BIZ001', '"Bizdev Consulting"',
        '"Ashik P Jose"', '"Jose Pazhukkayil"', '1990-05-15', '9876543210', '9123456780',
        '13-06-2026',
        'Permanent',
        '"Ancestral Home"', '"Kannur District"', 'Kannur', 'Kerala', '670001', '20 years'
      ].join(',');
      const csv = `${headers}\n${sample}\n${sample2}`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'veecheck_multi_client_bulk_import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    const headers = BULK_TEMPLATE_HEADERS.join(',');
    const sampleRow = [
      'EMP001', 'John Doe', 'Veecheck Pvt Ltd', 'James Doe', '1990-05-15', 'Male', 'john@example.com', '9876543210', '9876543211',
      'ABCDE1234F', '1234-5678-9012',
      '123 Main Street', 'Apt 4B', 'Mumbai', 'Maharashtra', '400001', '2 years',
      '456 Village Road', 'Near Temple', 'Pune', 'Maharashtra', '411001'
    ].join(',');
    
    const csvContent = `${headers}\n${sampleRow}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_employee_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Multi-Client bulk import submit — sends rows AS-IS to the new endpoint.
  // Duplicate flow:
  //  1) First call sends `duplicate_action: 'prompt'`. Backend imports
  //     non-duplicates and returns `pending_duplicates` for groups that match
  //     an existing candidate in the same entity.
  //  2) If any pending_duplicates → open a confirm dialog. User picks
  //     "Import Anyway" (re-call with `import_all` + only the duplicate rows)
  //     or "Skip" (close, keep the already-imported non-duplicates).
  const submitBulkImportMulti = async (rowsToSend, duplicateAction = 'prompt') => {
    setBulkUploading(true);
    try {
      const response = await axios.post('/data-entry/bulk-import-multi', {
        rows: rowsToSend,
        send_invite_emails: false,
        duplicate_action: duplicateAction,
      });
      const imported = response.data.imported || 0;
      const skipped = response.data.skipped_groups || [];
      const rowErrors = response.data.row_errors || [];
      const pendingDups = response.data.pending_duplicates || [];

      if (imported > 0) {
        toast.success(
          `Imported ${imported} candidates / ${response.data.total_groups} groups across all clients`,
          { duration: 8000 }
        );
      }
      if (skipped.length || rowErrors.length) {
        const errs = [
          ...rowErrors.map(e => `Row ${e.row}: ${e.error}`),
          ...skipped.map(s => `Rows ${(s.rows || []).join(',')}: ${s.error}`)
        ].slice(0, 6).join('\n');
        toast.error(
          `${rowErrors.length + skipped.length} group(s)/row(s) failed:\n${errs}`,
          { duration: 10000 }
        );
      }

      if (pendingDups.length > 0 && duplicateAction === 'prompt') {
        // Hold these for the user-confirmation dialog
        setDuplicateGroups(pendingDups);
        setShowDuplicateDialog(true);
      } else {
        // Done — close importer
        setShowBulkImport(false);
        setBulkData([]);
        setBulkErrors([]);
        setMultiClientMode(false);
        fetchData();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Bulk import failed: ${detail}`);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkImportMulti = async () => {
    const validRows = bulkData.filter(r => r.valid !== false);
    if (validRows.length === 0) {
      toast.error('No rows to import');
      return;
    }
    await submitBulkImportMulti(validRows, 'prompt');
  };

  // User clicked "Import Anyway" in the duplicate-confirmation dialog.
  // Re-send ONLY the rows that belong to pending duplicate groups, with
  // duplicate_action='import_all' so the backend creates fresh employees+cases.
  const handleImportDuplicatesAnyway = async () => {
    const dupRowNumbers = new Set();
    (duplicateGroups || []).forEach(d => (d.rows || []).forEach(r => dupRowNumbers.add(r)));
    const dupRows = (bulkData || []).filter(r => dupRowNumbers.has(r.row_number));
    setShowDuplicateDialog(false);
    setDuplicateGroups([]);
    if (dupRows.length === 0) {
      // Nothing to re-import — just close.
      setShowBulkImport(false);
      setBulkData([]);
      setMultiClientMode(false);
      fetchData();
      return;
    }
    await submitBulkImportMulti(dupRows, 'import_all');
  };

  const handleSkipDuplicates = () => {
    setShowDuplicateDialog(false);
    setDuplicateGroups([]);
    setShowBulkImport(false);
    setBulkData([]);
    setBulkErrors([]);
    setMultiClientMode(false);
    fetchData();
  };

  const handleBulkFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Veecheck Jun 2026 — Pre-flight check for duplicate CSV headers.
    // PapaParse silently renames the second occurrence of a duplicate
    // header (e.g. two columns both named `employee_code`) by appending
    // "_1". The original value of the FIRST column then gets stored
    // under the un-suffixed key. This caused real-world bulk imports to
    // (a) merge every row into one group when `employee_code` was the
    // grouping key, and (b) overwrite the actual employee_code field
    // with date strings like "16-06-2026". Reject the file upfront so
    // the user fixes their CSV instead of seeing silent data corruption.
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || '');
      const firstLine = text.split(/\r?\n/, 1)[0] || '';
      // Naive header split: respect double-quoted cells (good enough for
      // header rows which never contain newlines).
      const headerCells = [];
      let buf = '';
      let inQuotes = false;
      for (let i = 0; i < firstLine.length; i++) {
        const ch = firstLine[i];
        if (ch === '"') inQuotes = !inQuotes;
        else if (ch === ',' && !inQuotes) {
          headerCells.push(buf);
          buf = '';
        } else buf += ch;
      }
      headerCells.push(buf);
      const normalised = headerCells.map(h => (h || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/^"|"$/g, ''));
      const seen = new Map();
      const dupes = [];
      normalised.forEach((h, idx) => {
        if (!h) return;
        if (seen.has(h)) {
          dupes.push({ header: h, cols: [seen.get(h) + 1, idx + 1] });
        } else {
          seen.set(h, idx);
        }
      });
      if (dupes.length > 0) {
        const msg = dupes
          .map(d => `'${d.header}' at columns ${d.cols.join(' & ')}`)
          .join('; ');
        toast.error(
          `Duplicate column headers detected: ${msg}. Please rename or remove duplicate headers in your CSV before uploading. Common cause: one of these is meant to be 'case_received_date'.`,
          { duration: 12000 }
        );
        // Reset the file input so the user can re-pick after fixing
        if (e?.target) e.target.value = '';
        return;
      }
      // Headers OK → proceed to actual PapaParse
      runPapaParse(file);
    };
    reader.onerror = () => {
      toast.error('Could not read the file. Please try again.');
    };
    reader.readAsText(file);
  };

  const runPapaParse = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          toast.error('File must contain header row and at least one data row');
          return;
        }

        const data = [];
        const errors = [];

        results.data.forEach((row, index) => {
          // Clean all values - strip quotes and whitespace
          const cleanRow = { row_number: index + 2 };
          Object.keys(row).forEach(key => {
            cleanRow[key] = (row[key] || '').toString().trim().replace(/^"|"$/g, '');
          });

          // Validate required fields
          const rowErrors = [];
          if (multiClientMode) {
            // Multi-client mode: at minimum require Client Name + (name OR employee_code)
            if (!cleanRow.client_name) rowErrors.push('Client Name required');
            if (!cleanRow.name && !cleanRow.employee_code) rowErrors.push('Name or employee_code required');
          } else {
            if (!cleanRow.name) rowErrors.push('Name required');
            if (!cleanRow.email) rowErrors.push('Email required');
            if (!cleanRow.phone) rowErrors.push('Phone required');
            // Validate email format only for single-client mode
            if (cleanRow.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanRow.email)) {
              rowErrors.push('Invalid email');
            }
          }

          cleanRow.errors = rowErrors;
          cleanRow.valid = rowErrors.length === 0;

          // Check for insufficiencies (missing optional but important fields)
          const insufficiencies = [];
          if (multiClientMode) {
            if (!cleanRow.address_line1 && !cleanRow.address) insufficiencies.push('Address missing');
            if (!cleanRow.package && !cleanRow.entity_name) insufficiencies.push('Package & Entity blank — fallback to entity default');
          } else {
            if (!cleanRow.father_name) insufficiencies.push('Father name missing');
            if (!cleanRow.dob) insufficiencies.push('DOB missing');
            if (!cleanRow.present_address_line1) insufficiencies.push('Present address missing');
          }
          cleanRow.insufficiencies = insufficiencies;

          data.push(cleanRow);
        });

        setBulkData(data);
        setBulkErrors(errors);
      },
      error: (error) => {
        toast.error('Failed to parse CSV file: ' + error.message);
      }
    });
  };

  const handleBulkImport = async () => {
    if (!bulkEntityId) {
      toast.error('Please select an entity');
      return;
    }

    const validRows = bulkData.filter(r => r.valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setBulkUploading(true);
    try {
      const employeesToImport = validRows.map(row => ({
        name: row.name,
        father_name: row.father_name || '',
        dob: row.dob || '',
        email: row.email,
        phone: row.phone,
        employee_code: row.employee_code || '',
        joining_company_name: row.joining_company_name || row.joining_company || '',
        gender: row.gender || '',
        alternate_contact: row.alternate_contact || '',
        pan_number: row.pan_number || '',
        aadhar_number: row.aadhar_number || '',
        present_address: row.present_address_line1 ? {
          line1: row.present_address_line1 || '',
          line2: row.present_address_line2 || '',
          city: row.present_city || '',
          state: row.present_state || '',
          pincode: row.present_pincode || '',
          duration: row.present_duration || ''
        } : null,
        permanent_address: row.permanent_address_line1 ? {
          line1: row.permanent_address_line1 || '',
          line2: row.permanent_address_line2 || '',
          city: row.permanent_city || '',
          state: row.permanent_state || '',
          pincode: row.permanent_pincode || ''
        } : null,
        entity_id: bulkEntityId,
        bypass_declaration: true,
        auto_create_case: true
      }));

      const response = await axios.post('/data-entry/bulk-import', {
        entity_id: bulkEntityId,
        package_id: bulkPackageId || undefined,
        employees: employeesToImport,
        send_invite_emails: sendBulkEmails
      });

      const imported = response.data.imported || 0;
      const importErrors = response.data.errors || [];
      
      if (imported > 0) {
        toast.success(
          <div>
            <p>Successfully imported {imported} of {validRows.length} employees</p>
            {sendBulkEmails && <p className="text-xs mt-1">Invitation emails sent with login credentials</p>}
          </div>,
          { duration: 8000 }
        );
      }
      if (importErrors.length > 0) {
        const errorMsg = importErrors.slice(0, 3).map(e => `Row ${e.row}: ${e.error}`).join('\n');
        toast.error(`${importErrors.length} row(s) failed:\n${errorMsg}`, { duration: 8000 });
      }
      if (imported === 0 && importErrors.length === 0) {
        toast.warning('No employees were imported');
      }
      setShowBulkImport(false);
      setBulkData([]);
      setBulkEntityId('');
      setBulkClientId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import employees'));
    } finally {
      setBulkUploading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes, entitiesRes, packagesRes, insuffRes, casesRes, finalReportRes, allCasesRes, teamRes, checkWiseRes, agingRes] = await Promise.allSettled([
        axios.get('/dashboard/stats'),
        axios.get('/clients'),
        axios.get('/entities'),
        axios.get('/packages'),
        axios.get('/insufficiencies/consolidated'),
        axios.get('/cases?status=pending_qc'),
        axios.get('/cases?status=final_report_qc'),
        axios.get('/cases'),
        axios.get('/team-members'),
        axios.get('/qc/check-wise-pending'),
        axios.get('/qc/insufficiency-aging')
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data);
      if (entitiesRes.status === 'fulfilled') setEntities(entitiesRes.value.data);
      if (packagesRes.status === 'fulfilled') setPackages(packagesRes.value.data);
      if (insuffRes.status === 'fulfilled') setInsufficiencies(insuffRes.value.data);
      if (casesRes.status === 'fulfilled') setPendingCases(casesRes.value.data);
      if (finalReportRes.status === 'fulfilled') setFinalReportCases(finalReportRes.value.data);
      if (allCasesRes.status === 'fulfilled') setAllCases(allCasesRes.value.data);
      if (teamRes.status === 'fulfilled') setTeamMembers(teamRes.value.data);
      if (checkWiseRes.status === 'fulfilled') setCheckWisePending(checkWiseRes.value.data);
      if (agingRes.status === 'fulfilled') setInsuffAging(agingRes.value.data);
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openDrillDown = (title, data) => {
    setDrillDownTitle(title);
    setDrillDownData(data);
    setShowDrillDown(true);
  };

  const fetchEntityContacts = async (entityId) => {
    if (!entityId) {
      setEntityContacts([]);
      return;
    }
    try {
      const response = await axios.get(`/entities/${entityId}/contacts`);
      setEntityContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch entity contacts');
      setEntityContacts([]);
    }
  };

  const fetchCaseDetail = async (caseId) => {
    // Handle both string case_id and object with id property
    const actualCaseId = typeof caseId === 'string' ? caseId : caseId?.id;
    if (!actualCaseId) return;
    
    setLoadingDetail(true);
    try {
      const response = await axios.get(`/cases/${actualCaseId}/full-details`);
      const caseData = response.data;
      
      // Load documents for each check
      const checkDocuments = {};
      if (caseData?.case?.checks) {
        for (const check of caseData.case.checks) {
          try {
            const checkRes = await axios.get(`/cases/${actualCaseId}/checks/${check.id || check.check_id}/details`);
            checkDocuments[check.id || check.check_id] = checkRes.data?.documents || [];
          } catch {
            checkDocuments[check.id || check.check_id] = [];
          }
        }
      }
      
      setCaseDetailData({ ...caseData, checkDocuments });
    } catch (error) {
      // Only show error for non-404 errors (404 means case was deleted/doesn't exist)
      if (error.response?.status !== 404) {
        console.error('Failed to load case details:', error);
        // Don't show toast for brief network issues or race conditions
        // toast.error('Failed to load case details');
      }
      setCaseDetailData(null);
    } finally {
      setLoadingDetail(false);
    }
  };
  
  // Auto-fetch case details when showCaseDetail changes (handles both string and object)
  useEffect(() => {
    if (showCaseDetail) {
      const caseId = typeof showCaseDetail === 'string' ? showCaseDetail : showCaseDetail?.id;
      if (caseId) {
        fetchCaseDetail(caseId);
      }
    } else {
      setCaseDetailData(null);
    }
  }, [showCaseDetail]);

  const handleEntityChange = (entityId) => {
    setSelectedEntity(entityId);
    setSelectedContact('');
    
    // Auto-select entity's default package and initialize custom checks
    const entity = entities.find(e => e.id === entityId);
    const defaultPkgId = entity?.package_id || entity?.default_package_id || '';
    let initialChecks = [];
    
    if (defaultPkgId) {
      const pkg = packages.find(p => p.id === defaultPkgId);
      if (pkg?.checks) {
        initialChecks = pkg.checks.map(c => ({
          check_type: typeof c === 'string' ? c : c.check_type,
          enabled: true,
          quantity: (typeof c === 'object' ? c.quantity : 1) || 1,
          selectedSubOptions: typeof c === 'object' ? (c.sub_options || []) : [],
          sub_checks: typeof c === 'object' ? (c.sub_options || []) : []
        }));
      }
    }
    
    setNewEmployee({
      ...newEmployee, 
      entity_id: entityId, 
      entity_contact_id: '', 
      package_id: defaultPkgId,
      custom_checks: initialChecks
    });
    fetchEntityContacts(entityId);
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!newEmployee.entity_id) {
      toast.error('Please select an entity');
      return;
    }
    try {
      const payload = { ...newEmployee };
      
      // Process custom checks - only include enabled checks
      if (payload.custom_checks?.length > 0) {
        payload.custom_checks = payload.custom_checks
          .filter(c => c.enabled)
          .map(c => ({
            check_type: c.check_type,
            quantity: c.quantity || 1,
            sub_checks: c.sub_checks || []
          }));
      } else {
        delete payload.custom_checks;
      }
      
      // Handle password generation
      if (payload.send_invitation || payload.generate_password) {
        if (!payload.password && payload.generate_password) {
          const namePart = (payload.name || 'user').split(' ')[0].toLowerCase();
          const phonePart = (payload.phone || '1234').slice(-4);
          payload.password = `${namePart}@${phonePart}`;
        }
      }
      
      // Remove internal state fields (keep send_invitation for backend logic)
      delete payload.generate_password;
      
      const response = await axios.post('/data-entry/employees', payload);
      
      // Show password to user
      const createdPassword = payload.password;
      if (createdPassword && newEmployee.send_invitation) {
        toast.success(
          <div>
            <p>Employee added successfully!</p>
            <p className="text-xs mt-1">Login: <strong>{payload.email}</strong></p>
            <p className="text-xs">Password: <strong>{createdPassword}</strong></p>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.success('Employee added successfully');
      }
      
      if (newEmployee.send_offline_signing && response.data.employee_id) {
        await axios.post('/data-entry/send-offline-signing', {
          employee_id: response.data.employee_id
        });
        toast.success('Offline signing email sent');
      }
      
      // Send invitation email if opted
      if (newEmployee.send_invitation && response.data.employee_id) {
        try {
          await axios.post(`/employees/${response.data.employee_id}/send-invite`);
          toast.success('Invitation email sent to candidate');
        } catch (inviteErr) {
          toast.error('Employee created but invitation email failed to send');
        }
      }
      
      setShowAddEmployee(false);
      setSelectedClient('');
      setSelectedEntity('');
      setSelectedContact('');
      setEntityContacts([]);
      setNewEmployee({
        name: '', father_name: '', dob: '', email: '', phone: '',
        employee_code: '', entity_id: '', entity_contact_id: '',
        bypass_declaration: false, send_offline_signing: false, auto_create_case: true,
        package_id: '', custom_checks: [], send_invitation: false,
        generate_password: true, password: ''
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add employee'));
    }
  };

  const handleOpenCaseDetail = (caseData) => {
    setShowCaseDetail(caseData);
    fetchCaseDetail(caseData.id);
  };

  const handleRaiseInsufficiency = async () => {
    if (!insuffData.reason || !insuffData.check_id) {
      toast.error('Please select check and provide reason');
      return;
    }
    try {
      const caseId = showRaiseInsuff.id;
      await axios.post(`/cases/${caseId}/checks/${insuffData.check_id}/insufficiency`, {
        reason: insuffData.reason,
        description: insuffData.description
      });
      toast.success('Insufficiency raised successfully');
      setShowRaiseInsuff(null);
      setInsuffData({ check_id: '', check_type: '', entry_type: '', entry_index: -1, entry_name: '', reason: '', description: '' });
      // Refresh case detail to show updated insufficiency status
      await fetchCaseDetail(caseId);
      fetchData();
    } catch (error) {
      // Handle Pydantic validation errors (array format) vs regular errors (string)
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to raise insufficiency';
      if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message).join(', ');
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      }
      toast.error(errorMsg);
    }
  };

  const handleClearInsufficiency = async (caseId, checkId) => {
    try {
      await axios.put(`/cases/${caseId}/checks/${checkId}/clear-insufficiency`);
      toast.success('Insufficiency cleared');
      setSelectedInsufficiencies(prev => prev.filter(id => id !== checkId));
      fetchData();
    } catch (error) {
      toast.error('Failed to clear insufficiency');
    }
  };

  const handleBulkSendClientReminders = async () => {
    if (selectedInsufficiencies.length === 0) return;
    setSendingReminders(true);
    try {
      const res = await axios.post('/insufficiencies/bulk-reminder', {
        insufficiency_ids: selectedInsufficiencies,
        target: 'clients_only'
      });
      toast.success(res.data.message || 'Client reminders sent successfully');
      setSelectedInsufficiencies([]);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send client reminders'));
    } finally {
      setSendingReminders(false);
    }
  };

  const openCandidateEmailDialog = () => {
    // Build unique candidate emails from selected insufficiencies
    const selected = insufficiencies.filter(i => selectedInsufficiencies.includes(i.insufficiency_id));
    const emailMap = {};
    selected.forEach(insuff => {
      const email = insuff.employee_email;
      if (email) {
        if (!emailMap[email]) {
          emailMap[email] = { email, employee_name: insuff.employee_name, count: 0 };
        }
        emailMap[email].count += 1;
      }
    });
    const selections = {};
    Object.keys(emailMap).forEach(email => { selections[email] = true; });
    setCandidateEmailSelections(selections);
    setShowCandidateEmailDialog(true);
  };

  const handleSendCandidateEmails = async () => {
    const selectedEmails = Object.entries(candidateEmailSelections)
      .filter(([_, checked]) => checked)
      .map(([email]) => email);
    if (selectedEmails.length === 0) {
      toast.error('No candidate emails selected');
      return;
    }
    setSendingReminders(true);
    setShowCandidateEmailDialog(false);
    try {
      const res = await axios.post('/insufficiencies/bulk-reminder', {
        insufficiency_ids: selectedInsufficiencies,
        target: 'candidates_only',
        candidate_emails: selectedEmails
      });
      toast.success(res.data.message || 'Candidate emails sent successfully');
      setSelectedInsufficiencies([]);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send candidate emails'));
    } finally {
      setSendingReminders(false);
    }
  };

  // Send AMBER Closure Notice (May 24, 2026)
  //   Mirrors handleBulkSendClientReminders but routes to the
  //   /insufficiencies/bulk-amber-notice endpoint and filters the
  //   selection to items aged >= 45 calendar days. The backend
  //   re-validates the age threshold so a stale client can't bypass it.
  const eligibleAmberCount = useMemo(() => {
    if (!Array.isArray(selectedInsufficiencies) || selectedInsufficiencies.length === 0) return 0;
    const selected = insufficiencies.filter(i => selectedInsufficiencies.includes(i.insufficiency_id));
    const now = new Date();
    return selected.filter(i => {
      // Match the same ageing math used by the table row so the count
      // shown on the button matches what the user sees in the "Ageing"
      // column. Backend re-validates >=45 days before sending.
      const raised = new Date(i.date_raised || i.raised_at || i.created_at);
      if (isNaN(raised.getTime())) return false;
      const ageDays = Math.floor((now - raised) / (1000 * 60 * 60 * 24));
      return ageDays >= 45;
    }).length;
  }, [selectedInsufficiencies, insufficiencies]);

  const handleSendAmberNotice = async () => {
    setShowAmberConfirm(false);
    setSendingAmber(true);
    try {
      const res = await axios.post('/insufficiencies/bulk-amber-notice', {
        insufficiency_ids: selectedInsufficiencies,
        min_age_days: 45,
      });
      toast.success(res.data.message || 'Amber notice sent');
      setSelectedInsufficiencies([]);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send amber notice'));
    } finally {
      setSendingAmber(false);
    }
  };


  const prepareAssignConfirmation = async (caseItem) => {
    try {
      // Normalize input — caseItem can be a string (case_id) or object with .id
      const caseId = typeof caseItem === 'string' ? caseItem : caseItem?.id;
      if (!caseId) {
        toast.error('No case selected');
        return;
      }
      if (typeof caseItem === 'string') {
        caseItem = { id: caseItem };
      }
      
      // Fetch full case details for confirmation preview
      const res = await axios.get(`/cases/${caseId}/full-details`);
      const data = res.data;
      
      // Check for any insufficiencies
      const insufficiencies = data.case?.entry_insufficiencies || [];
      const checkInsufficiencies = data.case?.checks?.filter(c => c.has_insufficiency) || [];
      
      // Data completeness check
      const employee = data.employee || {};
      const missingFields = [];
      if (!employee.name) missingFields.push('Name');
      if (!employee.dob) missingFields.push('Date of Birth');
      if (!employee.pan_number) missingFields.push('PAN Number');
      if (!employee.aadhar_number) missingFields.push('Aadhar Number');
      // Check for present address in both flat and nested structure
      const presentAddr = employee?.present_address || {};
      const hasAddress = employee?.present_city || presentAddr.city;
      if (!hasAddress) missingFields.push('Present Address');
      
      setAssignConfirmData({
        caseItem,
        employee: data.employee,
        caseInfo: data.case,
        clientName: data.client_name,
        entityName: data.entity_name,
        insufficiencies,
        checkInsufficiencies,
        missingFields,
        checks: data.case?.checks || [],
        hasIssues: insufficiencies.length > 0 || checkInsufficiencies.length > 0 || missingFields.length > 0,
        hasOpenInsufficiency: insufficiencies.length > 0 || checkInsufficiencies.length > 0
      });
      setBypassInsuffAssign(false);
      setShowAssignConfirm(caseItem);
    } catch (error) {
      // Only log, don't show toast for brief errors
      console.error('Failed to load case details for assignment:', error);
    }
  };

  const handleApproveAndAssign = async (caseId, agentId) => {
    try {
      const res = await axios.put(`/cases/${caseId}/approve-qc`, { assigned_to: agentId });
      toast.success(res.data?.message || 'Case approved and assigned');
      setShowAssignAgent(null);
      setShowAssignConfirm(null);
      setAssignConfirmData(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve case'));
    }
  };

  const handleApproveAll = async (caseId) => {
    try {
      const res = await axios.put(`/cases/${caseId}/approve-qc`, { auto_assign: true });
      toast.success(res.data?.message || 'Case approved with auto-assignment');
      setShowAssignConfirm(null);
      setAssignConfirmData(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve case');
    }
  };

  const handleStopCheck = async () => {
    if (!showStopCheck || !stopCheckReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setStoppingCheck(true);
    try {
      const response = await axios.put(`/cases/${showStopCheck.id}/stop-check`, {
        reason: stopCheckReason,
        description: stopCheckDescription
      });
      toast.success(`Verification stopped - ${response.data.checks_stopped} checks stopped`);
      setShowStopCheck(null);
      setStopCheckReason('');
      setStopCheckDescription('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to stop check'));
    } finally {
      setStoppingCheck(false);
    }
  };

  const canStopCheck = (caseItem) => {
    // Can stop check if case is not already stopped or completed
    return !['stopped', 'completed', 'report_approved'].includes(caseItem.status);
  };

  const getClientEntities = (clientId) => entities.filter(e => e.client_id === clientId);
  const getEntityName = (entityId) => entities.find(e => e.id === entityId)?.entity_name || 'N/A';
  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.company_name || 'N/A';
  const getPackageName = (packageId) => packages.find(p => p.id === packageId)?.name || 'N/A';
  
  // Get entity's default package
  const getEntityDefaultPackage = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.default_package_id || '';
  };
  
  // Get packages for client (filter by client_id) with search
  const getFilteredPackages = (clientId, searchTerm = '') => {
    let filtered = packages;
    // Optionally filter by client if packages have client_id
    if (clientId) {
      filtered = packages.filter(p => !p.client_id || p.client_id === clientId);
    }
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(term) || 
        p.description?.toLowerCase().includes(term)
      );
    }
    return filtered;
  };

  // Helper function to format address from different data structures
  const formatAddress = (employee, addressType = 'present') => {
    // Check for nested address object (new format: present_address, permanent_address)
    const addressObj = employee?.[`${addressType}_address`];
    if (addressObj && typeof addressObj === 'object') {
      const parts = [
        addressObj.complete_address || addressObj.house_no,
        addressObj.landmark,
        addressObj.area,
        addressObj.city,
        addressObj.state,
        addressObj.pincode
      ].filter(Boolean);
      return parts.join(', ') || 'Not provided';
    }
    
    // Fallback to flat fields (old format: present_house_no, present_city, etc.)
    const parts = [
      employee?.[`${addressType}_house_no`],
      employee?.[`${addressType}_area`],
      employee?.[`${addressType}_city`],
      employee?.[`${addressType}_state`],
      employee?.[`${addressType}_pincode`]
    ].filter(Boolean);
    return parts.join(', ') || 'Not provided';
  };

  const CHECK_TYPE_LABELS = {
    'employment_check': 'Employment Check',
    'education_check': 'Education Check',
    'address_check': 'Address Check',
    'id_check': 'ID Verification',
    'criminal_court_check': 'Criminal Court Check',
    'court_advocate_check': 'Criminal Advocate Check',
    'criminal_advocate_check': 'Criminal Advocate Check',
    'credit_check': 'Credit Check',
    'world_criminal_db': 'World Database Check',
    'india_criminal_db': 'India Criminal DB',
    'reference_check': 'Reference Check',
    'drug_test': 'Drug Test',
    'professional_reference': 'Professional Reference',
    'personal_reference': 'Personal Reference'
  };

  const handleApproveSingleCheck = async (caseId, checkId) => {
    try {
      await axios.put(`/cases/${caseId}/checks/${checkId}/approve?bypass_insufficiency=true`);
      toast.success('Check approved successfully');
      // Reload case details
      fetchCaseDetail(caseId);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve check'));
    }
  };

  // Entry-level insufficiency handlers
  const handleRaiseEntryInsufficiency = async (caseId) => {
    if (!insuffData.reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      await axios.post(`/cases/${caseId}/entry-insufficiency`, {
        entry_type: insuffData.entry_type,
        entry_index: insuffData.entry_index,
        entry_name: insuffData.entry_name,
        reason: insuffData.reason,
        description: insuffData.description
      });
      toast.success(`Insufficiency raised for ${insuffData.entry_name}`);
      setShowEntryInsuff(null);
      setInsuffData({ check_id: '', check_type: '', entry_type: '', entry_index: -1, entry_name: '', reason: '', description: '' });
      fetchCaseDetail(caseId);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to raise insufficiency'));
    }
  };

  const handleClearEntryInsufficiency = async (caseId, entryType, entryIndex) => {
    try {
      await axios.put(`/cases/${caseId}/entry-insufficiency/clear`, {
        entry_type: entryType,
        entry_index: entryIndex
      });
      toast.success('Insufficiency cleared');
      fetchCaseDetail(caseId);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to clear insufficiency'));
    }
  };

  const handleApproveEntry = async (caseId, entryType, entryIndex, entryName) => {
    try {
      await axios.post(`/cases/${caseId}/entry-approve`, {
        entry_type: entryType,
        entry_index: entryIndex,
        entry_name: entryName
      });
      toast.success(`${entryName} approved and ready for assignment`);
      fetchCaseDetail(caseId);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve entry'));
    }
  };

  // Initialize edit form data from caseDetailData
  const initEditForm = () => {
    if (caseDetailData) {
      const employee = caseDetailData.employee || {};
      setEditFormData({
        // Personal details
        name: employee.name || '',
        father_name: employee.father_name || '',
        dob: employee.dob || '',
        gender: employee.gender || '',
        email: employee.email || '',
        phone: employee.phone || '',
        alternate_phone: employee.alternate_phone || '',
        pan_number: employee.pan_number || '',
        aadhar_number: employee.aadhar_number || '',
        uan_number: employee.uan_number || '',
        passport_number: employee.passport_number || '',
        driving_license: employee.driving_license || '',
        voter_id: employee.voter_id || '',
        dl_number: employee.dl_number || '',
        // Present Address
        present_house_no: employee.present_house_no || '',
        present_landmark: employee.present_landmark || '',
        present_area: employee.present_area || '',
        present_city: employee.present_city || '',
        present_state: employee.present_state || '',
        present_pincode: employee.present_pincode || '',
        // Permanent Address
        permanent_house_no: employee.permanent_house_no || '',
        permanent_landmark: employee.permanent_landmark || '',
        permanent_area: employee.permanent_area || '',
        permanent_city: employee.permanent_city || '',
        permanent_state: employee.permanent_state || '',
        permanent_pincode: employee.permanent_pincode || '',
        permanent_same_as_present: employee.permanent_same_as_present || false,
        // Employment & Education arrays - deep copy to allow editing
        employment_entries: JSON.parse(JSON.stringify(employee.employment_entries || [])),
        education_entries: JSON.parse(JSON.stringify(employee.education_entries || [])),
        highest_education: employee.highest_education ? JSON.parse(JSON.stringify(employee.highest_education)) : null,
        prior_educations: JSON.parse(JSON.stringify(employee.prior_educations || [])),
        personal_references: JSON.parse(JSON.stringify(employee.personal_references || [])),
        professional_references: JSON.parse(JSON.stringify(employee.professional_references || [])),
      });
    }
  };

  // Helper to update employment entry field
  const updateEmploymentEntry = (index, field, value) => {
    const employee = caseDetailData?.employee || {};
    const originalEntries = employee.employment_entries || [];
    const updatedEntries = [...(editFormData.employment_entries || [])];
    
    // Ensure the array has enough entries (copy from original if needed)
    while (updatedEntries.length <= index && originalEntries[updatedEntries.length]) {
      updatedEntries.push(JSON.parse(JSON.stringify(originalEntries[updatedEntries.length])));
    }
    
    if (index < updatedEntries.length) {
      updatedEntries[index] = { ...updatedEntries[index], [field]: value };
      setEditFormData({ ...editFormData, employment_entries: updatedEntries });
    }
  };

  // Helper to update education entry field
  const updateEducationEntry = (index, field, value) => {
    const employee = caseDetailData?.employee || {};
    const originalEntries = employee.education_entries || [];
    const updatedEntries = [...(editFormData.education_entries || [])];
    
    // Ensure the array has enough entries (copy from original if needed)
    while (updatedEntries.length <= index && originalEntries[updatedEntries.length]) {
      updatedEntries.push(JSON.parse(JSON.stringify(originalEntries[updatedEntries.length])));
    }
    
    if (index < updatedEntries.length) {
      updatedEntries[index] = { ...updatedEntries[index], [field]: value };
      setEditFormData({ ...editFormData, education_entries: updatedEntries });
    }
  };

  // Fetch audit trail for the employee
  const fetchAuditTrail = async (employeeId) => {
    if (!employeeId) return;
    setAuditLoading(true);
    try {
      const response = await axios.get(`/employees/${employeeId}/audit-trail`);
      setAuditTrailData(response.data);
      setShowAuditTrail(true);
    } catch (error) {
      toast.error('Failed to load audit trail');
    } finally {
      setAuditLoading(false);
    }
  };

  // Replace document for employee (QC Quick Edit)
  const replaceDocument = async (file, documentType, entryIndex = null) => {
    if (!caseDetailData?.employee?.id || !file) return;
    setDocUploadProgress(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (entryIndex !== null) {
        formData.append('entry_index', entryIndex.toString());
      }
      
      const response = await axios.post(`/employees/${caseDetailData.employee.id}/replace-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Document replaced successfully`);
      
      // Refresh case detail to show new document
      if (showCaseDetail) {
        await fetchCaseDetail(showCaseDetail);
      }
      setReplacingDoc(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to replace document'));
    } finally {
      setDocUploadProgress(false);
    }
  };

  // Save edited employee data — with identity-change confirmation guard
  const IDENTITY_FIELDS = ['name', 'father_name', 'dob', 'email'];

  const detectIdentityChanges = () => {
    const emp = caseDetailData?.employee || {};
    const diffs = [];
    for (const f of IDENTITY_FIELDS) {
      if (!(f in editFormData)) continue;
      const incoming = editFormData[f];
      const current = emp[f];
      // Treat null/undefined/"" as equivalent
      const norm = (v) => (v === null || v === undefined ? '' : String(v).trim());
      if (norm(incoming) !== norm(current) && norm(incoming) !== '') {
        diffs.push({ field: f, from: current ?? '', to: incoming });
      }
    }
    return diffs;
  };

  // Mirror-unlink detection (May 21, 2026) — when QC's edit will cause the
  // permanent address to differ from present on a candidate that previously
  // had `permanent_same_as_present=True`, the backend will auto-unlink the
  // mirror and reset the permanent address check to pending. We surface a
  // confirmation dialog BEFORE submit so QC isn't surprised — accidental
  // unlinking would force a vendor to redo address work that didn't change.
  const detectMirrorUnlink = () => {
    const emp = caseDetailData?.employee || {};
    if (!emp.permanent_same_as_present) return null; // not currently mirrored — nothing to unlink
    const norm = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase().replace(/\s+/g, ' '));
    const FLAT = ['house_no', 'area', 'city', 'state', 'pincode'];
    let willDiverge = false;
    for (const k of FLAT) {
      const finalP = norm(`present_${k}` in editFormData ? editFormData[`present_${k}`] : emp[`present_${k}`]);
      const finalR = norm(`permanent_${k}` in editFormData ? editFormData[`permanent_${k}`] : emp[`permanent_${k}`]);
      if (finalP !== finalR) {
        willDiverge = true;
        break;
      }
    }
    // Also evaluate nested address objects when present.
    const presentObj = ('present_address' in editFormData ? editFormData.present_address : emp.present_address) || {};
    const permanentObj = ('permanent_address' in editFormData ? editFormData.permanent_address : emp.permanent_address) || {};
    const OBJ_KEYS = ['complete_address', 'city', 'state', 'pincode', 'house_no', 'area'];
    for (const k of OBJ_KEYS) {
      if ((presentObj[k] || permanentObj[k]) && norm(presentObj[k]) !== norm(permanentObj[k])) {
        willDiverge = true;
        break;
      }
    }
    // Skip the dialog if the slider was explicitly toggled — that's an
    // intentional change the user already understands.
    if (editFormData?.permanent_same_as_present === false) return { willDiverge, sliderOff: true };
    return willDiverge ? { willDiverge: true } : null;
  };

  const saveEditedData = async () => {
    if (!caseDetailData?.employee?.id) return;
    // If this edit is about to mutate identity fields on a submitted candidate,
    // force a two-step confirmation. This turns the backend 400 (identity guard)
    // into a friendly dialog instead of a raw error toast, AND makes accidental
    // cross-candidate state leaks impossible to save silently.
    const isSubmitted = !!caseDetailData?.employee?.declaration_signed;
    if (isSubmitted) {
      const diffs = detectIdentityChanges();
      if (diffs.length > 0) {
        setIdentityConfirm({ open: true, diffs, reason: '' });
        return;
      }
    }
    // Mirror-unlink confirmation gate — only when this edit will actually
    // unlink an existing mirror, not on every address edit.
    const mirrorPreview = detectMirrorUnlink();
    if (mirrorPreview && mirrorPreview.willDiverge && !mirrorPreview.sliderOff) {
      setMirrorUnlinkConfirm({ open: true });
      return;
    }
    await performSave();
  };

  const performSave = async (changeReason = null) => {
    if (!caseDetailData?.employee?.id) return;
    setSavingEdit(true);
    try {
      const payload = changeReason ? { ...editFormData, change_reason: changeReason } : editFormData;
      const response = await axios.put(`/employees/${caseDetailData.employee.id}`, payload);
      toast.success('Employee data updated successfully');
      if (response.data?.warning) {
        toast.info(response.data.warning, { duration: 5000 });
      }
      if (response.data?.mirror_unlinked && response.data.mirror_unlinked.reset_check_ids?.length) {
        const resetCount = response.data.mirror_unlinked.reset_check_ids.length;
        toast.info(
          <div data-testid="mirror-unlinked-toast">
            <p className="font-semibold">Permanent address mirror unlinked</p>
            <p className="text-xs mt-1">{resetCount} permanent address check(s) reset to <strong>pending</strong> for independent vendor assignment. The agent dashboard now shows an &quot;Updated address&quot; hint on the affected check.</p>
          </div>,
          { duration: 9000 }
        );
      }
      // Small delay to ensure backend commits changes before refresh
      await new Promise(resolve => setTimeout(resolve, 300));
      // Refresh case detail silently (don't show error toast for refresh failures after successful save)
      if (showCaseDetail) {
        try {
          await fetchCaseDetail(showCaseDetail);
        } catch (refreshError) {
          // Silent fail for refresh - data was saved successfully, user can manually refresh if needed
          console.log('Auto-refresh failed after save, data was saved successfully');
        }
      }
      setEditMode(false);
      setIdentityConfirm({ open: false, diffs: [], reason: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save changes'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Upload document for case/check
  const uploadCheckDocument = async (file, checkType, documentType) => {
    if (!caseDetailData?.case?.id || !file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('case_id', caseDetailData.case.id);
      formData.append('document_type', documentType);
      formData.append('check_type', checkType);
      
      await axios.post('/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      // Small delay to ensure backend commits changes
      await new Promise(resolve => setTimeout(resolve, 300));
      // Refresh case detail silently
      if (showCaseDetail) {
        try {
          await fetchCaseDetail(showCaseDetail);
        } catch (refreshError) {
          console.log('Auto-refresh failed after upload, document was saved successfully');
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload document'));
    } finally {
      setUploadingDoc(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending': { class: 'bg-slate-100 text-slate-700', label: 'Pending' },
      'pending_qc': { class: 'bg-amber-100 text-amber-700', label: 'Pending QC' },
      'qc_approved': { class: 'bg-emerald-100 text-emerald-700', label: 'QC Approved' },
      'approved': { class: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
      'pending_ops_assignment': { class: 'bg-blue-100 text-blue-700', label: 'Pending Assign' },
      'agent_assigned': { class: 'bg-blue-100 text-blue-700', label: 'Agent Assigned' },
      'assigned': { class: 'bg-blue-100 text-blue-700', label: 'Assigned' },
      'vendor_assigned': { class: 'bg-blue-100 text-blue-700', label: 'Vendor Assigned' },
      'in_progress': { class: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      'completed': { class: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      'verified': { class: 'bg-emerald-100 text-emerald-700', label: 'Verified' },
      'clear': { class: 'bg-emerald-100 text-emerald-700', label: 'Clear' },
      'failed': { class: 'bg-rose-100 text-rose-700', label: 'Failed' },
      'discrepant': { class: 'bg-rose-100 text-rose-700', label: 'Discrepant' },
      'insufficient': { class: 'bg-amber-100 text-amber-700', label: 'Insufficient' },
      'unable_to_verify': { class: 'bg-orange-100 text-orange-700', label: 'Unable to Verify' },
      'not_applicable': { class: 'bg-slate-200 text-slate-700', label: 'N/A' },
      'not_applicable_by_declaration': { class: 'bg-amber-100 text-amber-800', label: 'N/A by Declaration' },
      'stop_check': { class: 'bg-red-100 text-red-700', label: 'Stopped' },
      'stopped': { class: 'bg-red-100 text-red-700', label: 'Stopped' },
      'final_report_qc': { class: 'bg-purple-100 text-purple-700', label: 'Final Report QC' },
      'report_approved': { class: 'bg-emerald-100 text-emerald-700', label: 'Report Approved' },
      'on_hold': { class: 'bg-orange-100 text-orange-700', label: 'On Hold' }
    };
    const c = config[status] || config['pending'];
    return <Badge className={c.class}>{c.label}</Badge>;
  };

  // Grouped Menu Items (matching Agency Admin style)
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FileEdit },
    
    // QC Tasks Group
    {
      id: 'qc-tasks-group',
      label: 'QC Tasks',
      icon: CheckCircle,
      isGroup: true,
      items: [
        { id: 'submitted', label: 'Submitted', icon: Inbox, badge: checkWisePending?.total_submitted > 0 ? checkWisePending.total_submitted : null },
        { id: 'quick-approve', label: 'Quick Approve & Assign', icon: CheckCircle },
        { id: 'pending-approvals', label: 'Pending Approvals', icon: Clock },
        { id: 'pending-assign', label: 'Pending Assign', icon: UserCheck },
        { id: 'qc-cases', label: 'Pending QC', icon: ClipboardList },
        { id: 'final-report-qc', label: 'Final Report QC', icon: FileText },
      ]
    },
    
    // Cases Group
    {
      id: 'cases-group',
      label: 'Cases',
      icon: FileText,
      isGroup: true,
      items: [
        { id: 'all-cases', label: 'All Cases', icon: FileText },
        { id: 'in-progress', label: 'Check Wise Status', icon: FileText },
        { id: 'completed-cases', label: 'QC Completed', icon: CheckCircle },
        { id: 'stopped-cases', label: 'Stopped Cases', icon: StopCircle },
        { id: 'insufficiencies', label: 'Insufficiencies', icon: AlertTriangle },
      ]
    },
    
    // Data Entry Group
    {
      id: 'data-entry-group',
      label: 'Data Entry',
      icon: UserPlus,
      isGroup: true,
      items: [
        { id: 'data-entry', label: 'Add New Case', icon: UserPlus },
        { id: 'candidate-drafts', label: 'Candidate Drafts', icon: FileInput },
        { id: 'edit-requests', label: 'Edit Requests', icon: Edit },
      ]
    },
  ];

  // Filter stopped cases
  const stoppedCases = allCases.filter(isStoppedCase);
  const [stoppedReasonFilter, setStoppedReasonFilter] = useState('all');
  const [stoppedDateFrom, setStoppedDateFrom] = useState('');
  const [stoppedDateTo, setStoppedDateTo] = useState('');

  // Edit Requests state
  const [editRequests, setEditRequests] = useState([]);
  const [editRequestsLoading, setEditRequestsLoading] = useState(false);
  const [editRequestFilter, setEditRequestFilter] = useState('pending');
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchEditRequests = async () => {
    setEditRequestsLoading(true);
    try {
      const res = await axios.get('/edit-requests');
      setEditRequests(res.data || []);
    } catch (e) { 
      console.log('Edit requests load failed'); 
    } finally {
      setEditRequestsLoading(false);
    }
  };

  useEffect(() => { 
    if (activeTab === 'edit-requests') {
      fetchEditRequests();
    }
    // Reset client/entity selection and form when leaving data-entry tab
    if (activeTab !== 'data-entry') {
      setSelectedClient('');
      setSelectedEntity('');
      setSelectedContact('');
      setPackageSearch('');
      setNewEmployee({
        name: '',
        father_name: '',
        dob: '',
        email: '',
        phone: '',
        employee_code: '',
        entity_id: '',
        entity_contact_id: '',
        bypass_declaration: false,
        send_offline_signing: false,
        auto_create_case: true,
        package_id: '',
        send_invitation: false,
        generate_password: true,
        password: ''
      });
    }
  }, [activeTab]);

  const handleReviewEditRequest = async (requestId, action) => {
    try {
      await axios.put(`/edit-requests/${requestId}/review?action=${action}${reviewNotes ? '&notes=' + encodeURIComponent(reviewNotes) : ''}`);
      toast.success(`Edit request ${action}d successfully`);
      setReviewingRequest(null);
      setReviewNotes('');
      fetchEditRequests();
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to ${action} request`));
    }
  };

  const getFilteredEditRequests = () => {
    if (editRequestFilter === 'all') return editRequests;
    return editRequests.filter(r => r.status === editRequestFilter);
  };

  const getFilteredStoppedCases = () => {
    let filtered = stoppedCases;
    
    if (stoppedReasonFilter !== 'all') {
      filtered = filtered.filter(c => c.stop_reason === stoppedReasonFilter);
    }
    
    if (stoppedDateFrom) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) >= new Date(stoppedDateFrom));
    }
    
    if (stoppedDateTo) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) <= new Date(stoppedDateTo + 'T23:59:59'));
    }
    
    return filtered;
  };

  const getStopReasonLabel = (reason) => {
    const labels = {
      'candidate_no_show': 'Candidate No Show',
      'terminated': 'Terminated',
      'candidate_withdrew': 'Candidate Withdrew',
      'offer_revoked': 'Offer Revoked',
      'other': 'Other'
    };
    return labels[reason] || reason || 'N/A';
  };

  // Date filter helper
  const getDateFilteredCases = (cases) => {
    if (dateFilter === 'all') return cases;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return cases.filter(c => {
      const caseDate = new Date(c.created_at);
      switch (dateFilter) {
        case 'today':
          return caseDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return caseDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return caseDate >= monthAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return caseDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  const dateFilterLabel = {
    'all': 'All Time',
    'today': 'Today',
    'week': 'This Week',
    'month': 'This Month',
    'year': 'This Year'
  };

  const handleExportPDF = () => {
    try {
      // Apply date filter to all cases
      const filteredAllCases = getDateFilteredCases(allCases);
      const filteredPendingCases = getDateFilteredCases(pendingCases);
      const filteredFinalReportCases = getDateFilteredCases(finalReportCases);
      
      generateDashboardPDF({
        title: 'QC Dashboard Report',
        subtitle: user.name,
        dateRange: dateFilterLabel[dateFilter],
        generatedBy: user.name,
        stats: [
          { label: 'Pending QC', value: filteredPendingCases.length, color: 'amber' },
          { label: 'Final Report QC', value: filteredFinalReportCases.length, color: 'purple' },
          { label: 'Open Insufficiencies', value: insufficiencies.length, color: 'rose' },
          { label: 'QC Approved', value: filteredAllCases.filter(c => c.status === 'qc_approved' || c.status === 'in_progress').length, color: 'emerald' },
          { label: 'Completed', value: filteredAllCases.filter(c => c.status === 'completed' || c.status === 'report_approved').length, color: 'blue' },
          { label: 'In Progress', value: filteredAllCases.filter(c => c.status === 'in_progress').length, color: 'slate' },
          { label: 'On Hold', value: filteredAllCases.filter(c => c.status === 'on_hold').length, color: 'amber' },
          { label: 'Total Cases', value: filteredAllCases.length, color: 'slate' }
        ],
        tables: [
          {
            title: 'Pending QC Cases',
            headers: ['Case ID', 'Employee', 'Client', 'Status', 'Created'],
            rows: filteredPendingCases.slice(0, 15).map(c => [
              c.id || '-',
              c.employee_name || '-',
              c.client_name || '-',
              c.status || '-',
              c.created_at ? formatDateDDMMYYYY(c.created_at) : '-'
            ])
          }
        ]
      });
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
      console.error('PDF generation error:', error);
    }
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') {
      // Apply date filter to all cases
      const filteredAllCases = getDateFilteredCases(allCases);
      const filteredPendingCases = getDateFilteredCases(pendingCases);
      const filteredFinalReportCases = getDateFilteredCases(finalReportCases);
      const filteredInsufficiencies = dateFilter === 'all' ? insufficiencies : insufficiencies.filter(i => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const insDate = new Date(i.date_raised);
        switch (dateFilter) {
          case 'today': return insDate >= today;
          case 'week': const w = new Date(today); w.setDate(w.getDate() - 7); return insDate >= w;
          case 'month': const m = new Date(today); m.setMonth(m.getMonth() - 1); return insDate >= m;
          case 'year': const y = new Date(today); y.setFullYear(y.getFullYear() - 1); return insDate >= y;
          default: return true;
        }
      });

      return (
        <div>
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">QC & Approval Dashboard</h1>
              <p className="text-slate-600">Data Entry & QC Operations - {user.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={handleExportPDF}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="qc-export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Filter:</Label>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-36" data-testid="qc-date-filter">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {dateFilter !== 'all' && (
            <div className="mb-4 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
              Showing data for: <strong>{dateFilterLabel[dateFilter]}</strong>
            </div>
          )}

          {loading ? (
            <div>Loading...</div>
          ) : (
            <>
              {/* NEW: Submitted, Pending Approvals & Pending Assign Cards - Most Important */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <Card className="border-violet-300 bg-violet-50 shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('submitted')} data-testid="submitted-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-violet-700 flex items-center gap-2">
                      <Inbox className="w-4 h-4" />
                      Submitted
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-violet-600">{checkWisePending.total_submitted || 0}</div>
                    <p className="text-xs text-violet-600 mt-1">Awaiting QC review</p>
                  </CardContent>
                </Card>

                <Card className="border-orange-300 bg-orange-50 shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('pending-approvals')} data-testid="pending-approvals-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Pending Approvals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">{checkWisePending.total_pending_approval}</div>
                    <p className="text-xs text-orange-600 mt-1">Checks awaiting QC</p>
                  </CardContent>
                </Card>

                <Card className="border-cyan-300 bg-cyan-50 shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('pending-assign')} data-testid="pending-assign-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-cyan-700 flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Pending Assign
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-cyan-600">{checkWisePending.total_pending_assign}</div>
                    <p className="text-xs text-cyan-600 mt-1">Approved, not assigned</p>
                  </CardContent>
                </Card>

                <Card 
                  className="border-blue-300 bg-blue-50 shadow-md cursor-pointer hover:shadow-lg transition-shadow" 
                  data-testid="in-progress-card"
                  onClick={() => setActiveTab('in-progress')}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Check Wise Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">{checkWisePending.total_all_checks || 0}</div>
                    <p className="text-xs text-blue-600 mt-1">All checks</p>
                  </CardContent>
                </Card>

                <Card 
                  className="border-emerald-300 bg-emerald-50 shadow-md cursor-pointer hover:shadow-lg transition-shadow" 
                  data-testid="completed-qc-card"
                  onClick={() => setActiveTab('completed-cases')}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-emerald-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      QC Completed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-emerald-600">{checkWisePending.total_qc_completed_cases || 0}</div>
                    <p className="text-xs text-emerald-600 mt-1">All checks assigned</p>
                  </CardContent>
                </Card>
              </div>

              {/* Check-wise Breakdown */}
              {Object.keys(checkWisePending.check_type_stats || {}).length > 0 && (
                <Card className="border-slate-200 shadow-sm mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      Check-wise Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {Object.entries(checkWisePending.check_type_stats || {}).map(([checkType, stats]) => (
                        <div key={checkType} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="text-xs font-medium text-slate-600 uppercase mb-2 truncate" title={checkType.replace(/_/g, ' ')}>
                            {checkType.replace(/_check/g, '').replace(/_/g, ' ')}
                          </p>
                          <div className="flex justify-between text-xs">
                            <span className="text-orange-600">Approve: {stats.pending_approval}</span>
                          </div>
                          <div className="flex justify-between text-xs mt-1">
                            <span className="text-cyan-600">Assign: {stats.pending_assign}</span>
                          </div>
                          <div className="flex justify-between text-xs mt-1">
                            <span className="text-emerald-600">In Progress: {stats.in_progress}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Main Stats Row */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                <Card className="border-amber-200 bg-amber-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Pending QC Cases', filteredPendingCases)}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Pending QC (Cases)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-600">{filteredPendingCases.length}</div>
                    <p className="text-xs text-amber-600 mt-1">Click to view details</p>
                  </CardContent>
                </Card>

                <Card className="border-purple-200 bg-purple-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Final Report QC Cases', filteredFinalReportCases)}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Final Report QC
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">{filteredFinalReportCases.length}</div>
                    <p className="text-xs text-purple-600 mt-1">Reports ready for review</p>
                  </CardContent>
                </Card>

                <Card className="border-rose-200 bg-rose-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Open Insufficiencies', filteredInsufficiencies)}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-rose-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Open Insufficiencies
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-rose-600">{filteredInsufficiencies.length}</div>
                    <p className="text-xs text-rose-600 mt-1">Click to view details</p>
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 bg-emerald-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('completed-cases')}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      QC Completed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-emerald-600">{checkWisePending.total_qc_completed_cases || 0}</div>
                    <p className="text-xs text-emerald-600 mt-1">All checks approved & assigned</p>
                  </CardContent>
                </Card>

                <Card className="border-blue-200 bg-blue-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Completed Cases', filteredAllCases.filter(c => c.status === 'completed' || c.status === 'report_approved'))}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-blue-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Completed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">{filteredAllCases.filter(c => c.status === 'completed' || c.status === 'report_approved').length}</div>
                    <p className="text-xs text-blue-600 mt-1">Click to view details</p>
                  </CardContent>
                </Card>
              </div>

              {/* Insufficiency Aging Widget */}
              {insuffAging.total_cases_with_open_insuff > 0 && (
                <Card className="border-rose-200 shadow-sm mb-6" data-testid="insuff-aging-widget">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-600" />
                      Insufficiency Aging
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {insuffAging.total_cases_with_open_insuff} case{insuffAging.total_cases_with_open_insuff === 1 ? '' : 's'} blocked by open insufficiency
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { key: '0_20', tone: 'emerald', borderCls: 'border-emerald-300 bg-emerald-50', textCls: 'text-emerald-700', numCls: 'text-emerald-700', hint: 'Fresh — address soon' },
                        { key: '21_45', tone: 'amber', borderCls: 'border-amber-300 bg-amber-50', textCls: 'text-amber-700', numCls: 'text-amber-700', hint: 'Needs attention' },
                        { key: 'over_45', tone: 'rose', borderCls: 'border-rose-300 bg-rose-50', textCls: 'text-rose-700', numCls: 'text-rose-700', hint: 'Breached 45-day SLA' }
                      ].map(b => {
                        const bucket = insuffAging.buckets?.[b.key] || { label: '', count: 0, cases: [] };
                        return (
                          <Card
                            key={b.key}
                            className={`${b.borderCls} border cursor-pointer hover:shadow-md transition-shadow`}
                            onClick={() => bucket.count > 0 && setInsuffAgingDrilldown({ bucket, key: b.key })}
                            data-testid={`insuff-aging-bucket-${b.key}`}
                          >
                            <CardContent className="pt-5">
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className={`text-xs font-medium ${b.textCls} uppercase`}>{bucket.label}</p>
                                  <div className={`text-4xl font-bold ${b.numCls} mt-1`}>{bucket.count}</div>
                                  <p className="text-xs text-slate-500 mt-1">{b.hint}</p>
                                </div>
                                <Clock className={`w-8 h-8 ${b.textCls} opacity-40`} />
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Secondary Stats Row */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                <Card className="border-slate-300 bg-slate-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => {setActiveTab('data-entry'); setDataEntrySubTab('check-details');}}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <FileInput className="w-4 h-4" />
                      Draft Cases
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-800">{filteredAllCases.filter(c => ['initiated', 'pending_qc'].includes(c.status)).length}</div>
                    <p className="text-xs text-slate-500 mt-1">Not yet submitted</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('In Progress Cases', filteredAllCases.filter(c => c.status === 'in_progress'))}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      In Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-700">{filteredAllCases.filter(c => c.status === 'in_progress').length}</div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('On Hold Cases', filteredAllCases.filter(c => c.status === 'on_hold'))}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      On Hold
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">{filteredAllCases.filter(c => c.status === 'on_hold').length}</div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('All Cases', filteredAllCases)}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Total Cases
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{filteredAllCases.length}</div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Total Employees
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stats?.total_employees || 0}</div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-7 gap-4">
                <Button onClick={() => setActiveTab('submitted')} className="h-20 flex-col gap-2 bg-violet-600 hover:bg-violet-700" data-testid="quick-action-submitted">
                  <Inbox className="w-6 h-6" />
                  Submitted
                </Button>
                <Button onClick={() => setActiveTab('pending-approvals')} className="h-20 flex-col gap-2 bg-orange-600 hover:bg-orange-700" data-testid="quick-action-pending-approvals">
                  <Clock className="w-6 h-6" />
                  Pending Approvals
                </Button>
                <Button onClick={() => setActiveTab('pending-assign')} className="h-20 flex-col gap-2 bg-cyan-600 hover:bg-cyan-700" data-testid="quick-action-pending-assign">
                  <UserCheck className="w-6 h-6" />
                  Pending Assign
                </Button>
                <Button onClick={() => setActiveTab('qc-cases')} className="h-20 flex-col gap-2 bg-amber-600 hover:bg-amber-700">
                  <ClipboardList className="w-6 h-6" />
                  Pending QC
                </Button>
                <Button onClick={() => setActiveTab('final-report-qc')} className="h-20 flex-col gap-2 bg-purple-600 hover:bg-purple-700">
                  <FileText className="w-6 h-6" />
                  Final Report QC
                </Button>
                <Button onClick={() => setActiveTab('data-entry')} className="h-20 flex-col gap-2 bg-slate-900 hover:bg-slate-800">
                  <UserPlus className="w-6 h-6" />
                  Data Entry
                </Button>
                <Button onClick={() => setActiveTab('insufficiencies')} variant="outline" className="h-20 flex-col gap-2">
                  <AlertTriangle className="w-6 h-6" />
                  Insufficiencies
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (activeTab === 'submitted') {
      return (
        <SubmittedTab 
          checkWisePending={checkWisePending}
          onRefresh={fetchData}
          onShowCaseDetail={setShowCaseDetail}
        />
      );
    }

    if (activeTab === 'quick-approve') {
      return (
        <div>
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Quick Approve & Assign</h1>
              <p className="text-slate-600">Bulk approve checks and auto-assign to vendors</p>
            </div>
          </div>
          <QuickApproveAssign clients={clients} />
        </div>
      );
    }

    if (activeTab === 'pending-approvals') {
      return (
        <PendingApprovalsTab 
          checkWisePending={checkWisePending}
          onRefresh={fetchData}
          onShowCaseDetail={setShowCaseDetail}
        />
      );
    }

    if (activeTab === 'pending-assign') {
      return (
        <PendingAssignTab 
          checkWisePending={checkWisePending}
          onRefresh={fetchData}
          onShowCaseDetail={setShowCaseDetail}
        />
      );
    }
    
    if (activeTab === 'all-cases') {
      return <CasesTab />;
    }

    if (activeTab === 'in-progress') {
      const allChecks = checkWisePending.all_checks || [];
      
      // Get unique clients for filter
      const cwsClients = [...new Set(allChecks.map(c => c.client_name).filter(Boolean))].sort();
      const cwsAgents = [...new Set(allChecks.map(c => c.agent_name).filter(Boolean))].sort();
      const cwsVendors = [...new Set(allChecks.map(c => c.vendor_name).filter(Boolean))].sort();
      
      // Filter
      const cwsFiltered = allChecks
        .filter(c => {
          const term = cwsSearch.toLowerCase();
          const searchMatch = !term ||
            (c.case_id || '').toLowerCase().includes(term) ||
            (c.employee_name || '').toLowerCase().includes(term) ||
            (c.client_name || '').toLowerCase().includes(term) ||
            (c.check_type || '').replace(/_/g, ' ').toLowerCase().includes(term) ||
            (c.agent_name || '').toLowerCase().includes(term) ||
            (c.vendor_name || '').toLowerCase().includes(term);
          const clientMatch = cwsClientFilter === 'all' || c.client_name === cwsClientFilter;
          const statusMatch = cwsStatusFilter === 'all' || c.display_status === cwsStatusFilter;
          const agentMatch = cwsAgentFilter === 'all' || c.agent_name === cwsAgentFilter;
          const vendorMatch = cwsVendorFilter === 'all' || c.vendor_name === cwsVendorFilter;
          let dateMatch = true;
          if (cwsDateFilter !== 'all') {
            const d = new Date(c.updated_at || c.created_at);
            const now = new Date();
            const diffDays = (now - d) / (1000 * 60 * 60 * 24);
            if (cwsDateFilter === 'today') dateMatch = diffDays < 1;
            else if (cwsDateFilter === 'last_7_days') dateMatch = diffDays <= 7;
            else if (cwsDateFilter === 'last_30_days') dateMatch = diffDays <= 30;
          }
          return searchMatch && clientMatch && statusMatch && agentMatch && vendorMatch && dateMatch;
        })
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      
      // Pagination
      const cwsTotalPages = Math.ceil(cwsFiltered.length / cwsPerPage);
      const cwsPaginated = cwsFiltered.slice((cwsPage - 1) * cwsPerPage, cwsPage * cwsPerPage);
      
      // Status badge colors
      const statusColors = {
        'In Progress': 'bg-blue-100 text-blue-700',
        'Not Initiated': 'bg-slate-100 text-slate-600',
        'Completed': 'bg-green-100 text-green-700',
        'Insufficient': 'bg-red-100 text-red-700'
      };
      
      // Export to Excel
      const handleCwsExport = () => {
        const headers = ['Case ID', 'Employee', 'Client', 'Check Type', 'Agent', 'Vendor', 'Status', 'Updated At'];
        const rows = cwsFiltered.map(c => [
          c.case_id,
          c.employee_name,
          c.client_name,
          (c.check_type || '').replace(/_/g, ' '),
          c.agent_name || '-',
          c.vendor_name || '-',
          c.display_status,
          c.updated_at ? new Date(c.updated_at).toLocaleDateString('en-IN') : '-'
        ]);
        
        let csv = headers.join(',') + '\n';
        rows.forEach(row => {
          csv += row.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',') + '\n';
        });
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `check_wise_status_${cwsStatusFilter !== 'all' ? cwsStatusFilter.replace(/\s/g, '_').toLowerCase() + '_' : ''}${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Exported successfully');
      };
      
      return (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Check Wise Status</h1>
              <p className="text-slate-500 text-sm">All verification checks across cases</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-slate-100 text-slate-700 text-sm px-3 py-1">
                {cwsFiltered.length} Checks
              </Badge>
              <Button size="sm" variant="outline" onClick={handleCwsExport} data-testid="cws-export-btn">
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
          
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="pt-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <Input
                  placeholder="Search case, employee, client..."
                  value={cwsSearch}
                  onChange={(e) => { setCwsSearch(e.target.value); setCwsPage(1); }}
                  className="w-64"
                  data-testid="cws-search"
                />
                <select
                  value={cwsClientFilter}
                  onChange={(e) => { setCwsClientFilter(e.target.value); setCwsPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="cws-client-filter"
                >
                  <option value="all">All Clients</option>
                  {cwsClients.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                  value={cwsStatusFilter}
                  onChange={(e) => { setCwsStatusFilter(e.target.value); setCwsPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="cws-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Not Initiated">Not Initiated</option>
                  <option value="Completed">Completed</option>
                  <option value="Insufficient">Insufficient</option>
                </select>
                <select
                  value={cwsDateFilter}
                  onChange={(e) => { setCwsDateFilter(e.target.value); setCwsPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="cws-date-filter"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_30_days">Last 30 Days</option>
                </select>
                <select
                  value={cwsAgentFilter}
                  onChange={(e) => { setCwsAgentFilter(e.target.value); setCwsPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="cws-agent-filter"
                >
                  <option value="all">All Agents</option>
                  {cwsAgents.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select
                  value={cwsVendorFilter}
                  onChange={(e) => { setCwsVendorFilter(e.target.value); setCwsPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="cws-vendor-filter"
                >
                  <option value="all">All Vendors</option>
                  {cwsVendors.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              
              {/* Pagination - above table */}
              <div className="flex items-center justify-between mb-3 text-sm text-slate-500">
                <span>Showing {Math.min((cwsPage - 1) * cwsPerPage + 1, cwsFiltered.length)}-{Math.min(cwsPage * cwsPerPage, cwsFiltered.length)} of {cwsFiltered.length}</span>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" disabled={cwsPage === 1} onClick={() => setCwsPage(1)}>First</Button>
                  <Button size="sm" variant="outline" disabled={cwsPage === 1} onClick={() => setCwsPage(p => p - 1)}>Prev</Button>
                  <span className="px-3">Page {cwsPage} of {cwsTotalPages || 1}</span>
                  <Button size="sm" variant="outline" disabled={cwsPage >= cwsTotalPages} onClick={() => setCwsPage(p => p + 1)}>Next</Button>
                  <Button size="sm" variant="outline" disabled={cwsPage >= cwsTotalPages} onClick={() => setCwsPage(cwsTotalPages)}>Last</Button>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cwsPaginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No checks found for the selected filters</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    cwsPaginated.map((check, idx) => (
                      <TableRow key={`${check.case_id}-${check.check_id}-${idx}`} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{check.case_id}</TableCell>
                        <TableCell className="font-medium">{check.employee_name}</TableCell>
                        <TableCell className="text-sm">{check.client_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {(check.check_type || '').replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{check.agent_name || '-'}</TableCell>
                        <TableCell className="text-sm">{check.vendor_name || '-'}</TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[check.display_status] || 'bg-slate-100 text-slate-600'} text-xs`}>
                            {check.display_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="outline"
                            data-testid={`cws-view-${check.case_id}`}
                            onClick={() => {
                              const caseItem = allCases.find(c => c.id === check.case_id);
                              if (caseItem) setShowCaseDetail(caseItem);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (activeTab === 'completed-cases') {
      const qcCompletedCases = checkWisePending.qc_completed_cases || [];

      // Date filter logic
      const filterQcCompletedByDate = (caseItem) => {
        if (qcCompletedDateFilter === 'all') return true;
        const d = new Date(caseItem.created_at || caseItem.updated_at);
        const now = new Date();
        const diffDays = (now - d) / (1000 * 60 * 60 * 24);
        switch (qcCompletedDateFilter) {
          case 'today': return diffDays < 1;
          case 'yesterday': return diffDays >= 1 && diffDays < 2;
          case 'last_7_days': return diffDays <= 7;
          case 'last_30_days': return diffDays <= 30;
          default: return true;
        }
      };

      const filteredQcCompleted = qcCompletedCases
        .filter(c => {
          const term = qcCompletedSearch.toLowerCase();
          const searchMatch = !term ||
            (c.case_id || '').toLowerCase().includes(term) ||
            (c.employee_name || '').toLowerCase().includes(term) ||
            (c.client_name || '').toLowerCase().includes(term) ||
            (c.entity_name || '').toLowerCase().includes(term);
          const clientMatch = qcCompletedClientFilter === 'all' || c.client_name === qcCompletedClientFilter;
          const dateMatch = filterQcCompletedByDate(c);
          return searchMatch && clientMatch && dateMatch;
        })
        .sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return qcCompletedSortOrder === 'latest' ? dateB - dateA : dateA - dateB;
        });

      const totalQcCompletedPages = Math.ceil(filteredQcCompleted.length / qcCompletedPageSize);
      const pagedQcCompleted = filteredQcCompleted.slice(
        (qcCompletedPage - 1) * qcCompletedPageSize,
        qcCompletedPage * qcCompletedPageSize
      );

      const uniqueCompletedClients = [...new Set(qcCompletedCases.map(c => c.client_name).filter(Boolean))].sort();

      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">QC Completed Cases</h1>
              <p className="text-slate-600">Cases with all checks QC approved and assigned to vendors</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-2">
              {filteredQcCompleted.length} Cases
            </Badge>
          </div>

          {/* Filters */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by Case ID, employee, client..."
                      value={qcCompletedSearch}
                      onChange={(e) => { setQcCompletedSearch(e.target.value); setQcCompletedPage(1); }}
                      className="pl-10"
                      data-testid="qc-completed-search-input"
                    />
                  </div>
                </div>
                <Select value={qcCompletedClientFilter} onValueChange={(v) => { setQcCompletedClientFilter(v); setQcCompletedPage(1); }}>
                  <SelectTrigger className="w-[200px]" data-testid="qc-completed-client-filter">
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {uniqueCompletedClients.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={qcCompletedDateFilter} onValueChange={(v) => { setQcCompletedDateFilter(v); setQcCompletedPage(1); }}>
                  <SelectTrigger className="w-[180px]" data-testid="qc-completed-date-filter">
                    <SelectValue placeholder="Date Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={qcCompletedSortOrder} onValueChange={(v) => { setQcCompletedSortOrder(v); setQcCompletedPage(1); }}>
                  <SelectTrigger className="w-[150px]" data-testid="qc-completed-sort-order">
                    <SelectValue placeholder="Sort By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Latest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                QC Completed Cases (All Checks Approved & Assigned)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client / Entity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Checks Progress</TableHead>
                    <TableHead>Assigned Vendors</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedQcCompleted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>{qcCompletedSearch || qcCompletedClientFilter !== 'all' || qcCompletedDateFilter !== 'all' ? 'No matching cases found' : 'No QC completed cases yet'}</p>
                        {!qcCompletedSearch && qcCompletedClientFilter === 'all' && qcCompletedDateFilter === 'all' && (
                          <p className="text-sm mt-2">Cases will appear here when all checks are QC approved and assigned</p>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedQcCompleted.map((caseItem, idx) => (
                      <TableRow key={`${caseItem.case_id}-${idx}`} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{caseItem.case_id}</TableCell>
                        <TableCell className="font-medium">{caseItem.employee_name}</TableCell>
                        <TableCell>
                          <div>{caseItem.client_name}</div>
                          <div className="text-xs text-slate-500">{caseItem.entity_name}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            caseItem.case_status === 'qc_approved' ? 'bg-emerald-100 text-emerald-700' :
                            caseItem.case_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {(caseItem.case_status || '').replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-emerald-500 h-2 rounded-full" 
                                style={{ width: `${caseItem.total_checks > 0 ? (caseItem.completed_checks / caseItem.total_checks) * 100 : 0}%` }}
                              />
                            </div>
                            <span className="text-sm text-slate-600">
                              {caseItem.completed_checks}/{caseItem.total_checks} verified
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(caseItem.checks || []).slice(0, 3).map((c, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {c.assigned_vendor}
                              </Badge>
                            ))}
                            {(caseItem.checks || []).length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{caseItem.checks.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              const fullCase = allCases.find(c => c.id === caseItem.case_id || c.case_id === caseItem.case_id);
                              const caseToOpen = fullCase || { ...caseItem, id: caseItem.case_id };
                              handleOpenCaseDetail(caseToOpen);
                            }}
                            data-testid={`qc-completed-view-${caseItem.case_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalQcCompletedPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Showing {(qcCompletedPage - 1) * qcCompletedPageSize + 1} - {Math.min(qcCompletedPage * qcCompletedPageSize, filteredQcCompleted.length)} of {filteredQcCompleted.length} cases
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setQcCompletedPage(1)} disabled={qcCompletedPage === 1}>First</Button>
                <Button variant="outline" size="sm" onClick={() => setQcCompletedPage(p => Math.max(1, p - 1))} disabled={qcCompletedPage === 1}>Previous</Button>
                <span className="px-3 py-1 text-sm font-medium">Page {qcCompletedPage} of {totalQcCompletedPages}</span>
                <Button variant="outline" size="sm" onClick={() => setQcCompletedPage(p => Math.min(totalQcCompletedPages, p + 1))} disabled={qcCompletedPage >= totalQcCompletedPages}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => setQcCompletedPage(totalQcCompletedPages)} disabled={qcCompletedPage >= totalQcCompletedPages}>Last</Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'qc-cases') {
      // Apply filters to pending cases
      let filteredPendingQC = [...pendingCases];
      
      // Search
      if (pendingQCSearch) {
        const search = pendingQCSearch.toLowerCase();
        filteredPendingQC = filteredPendingQC.filter(c =>
          c.id?.toLowerCase().includes(search) ||
          c.employee_name?.toLowerCase().includes(search) ||
          c.client_name?.toLowerCase().includes(search) ||
          c.entity_name?.toLowerCase().includes(search)
        );
      }
      
      // Client filter
      if (pendingQCClientFilter !== 'all') {
        filteredPendingQC = filteredPendingQC.filter(c => c.client_name === pendingQCClientFilter);
      }
      
      // Entity filter
      if (pendingQCEntityFilter !== 'all') {
        filteredPendingQC = filteredPendingQC.filter(c => c.entity_name === pendingQCEntityFilter);
      }
      
      // Date filter
      if (pendingQCDateFilter !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        filteredPendingQC = filteredPendingQC.filter(c => {
          if (!c.created_at) return false;
          const caseDate = new Date(c.created_at);
          switch (pendingQCDateFilter) {
            case 'today': return caseDate >= today;
            case 'week':
              const weekAgo = new Date(today);
              weekAgo.setDate(weekAgo.getDate() - 7);
              return caseDate >= weekAgo;
            case 'month':
              const monthAgo = new Date(today);
              monthAgo.setMonth(monthAgo.getMonth() - 1);
              return caseDate >= monthAgo;
            default: return true;
          }
        });
      }
      
      // Sort
      if (pendingQCSortBy === 'recent') {
        filteredPendingQC.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      } else {
        filteredPendingQC.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      }
      
      // Pagination
      const totalPendingQCPages = Math.ceil(filteredPendingQC.length / pendingQCPageSize);
      const pendingQCStartIndex = (pendingQCPage - 1) * pendingQCPageSize;
      const paginatedPendingQC = filteredPendingQC.slice(pendingQCStartIndex, pendingQCStartIndex + pendingQCPageSize);
      
      // Get unique clients and entities for filters
      const uniquePendingQCClients = [...new Set(pendingCases.map(c => c.client_name).filter(Boolean))];
      const uniquePendingQCEntities = [...new Set(pendingCases.map(c => c.entity_name).filter(Boolean))];
      
      return (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Pending QC Cases</h1>
              <p className="text-slate-600">Review employee data and documents, approve or raise insufficiency</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Badge className="bg-red-600 text-white text-lg px-4 py-2 font-semibold">
                {pendingCases.length} Cases Pending
              </Badge>
            </div>
          </div>

          {/* Search and Filters */}
          <Card className="border-slate-200 shadow-sm mb-4">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-[250px]">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Search by Case ID, Employee, Client..."
                      value={pendingQCSearch}
                      onChange={(e) => { setPendingQCSearch(e.target.value); setPendingQCPage(1); }}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Client:</label>
                  <Select value={pendingQCClientFilter} onValueChange={(v) => { setPendingQCClientFilter(v); setPendingQCPage(1); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {uniquePendingQCClients.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Entity:</label>
                  <Select value={pendingQCEntityFilter} onValueChange={(v) => { setPendingQCEntityFilter(v); setPendingQCPage(1); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Entities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Entities</SelectItem>
                      {uniquePendingQCEntities.map(e => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Received:</label>
                  <Select value={pendingQCDateFilter} onValueChange={(v) => { setPendingQCDateFilter(v); setPendingQCPage(1); }}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Sort:</label>
                  <Select value={pendingQCSortBy} onValueChange={(v) => setPendingQCSortBy(v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Latest First" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Latest First</SelectItem>
                      <SelectItem value="oldest">Oldest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setPendingQCSearch(''); setPendingQCClientFilter('all'); setPendingQCEntityFilter('all'); setPendingQCDateFilter('all'); setPendingQCSortBy('recent'); setPendingQCPage(1); }}
                  className="text-slate-500"
                >
                  <X className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client / Entity</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPendingQC.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                        <p>No pending QC cases</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPendingQC.map((caseItem) => (
                      <TableRow key={caseItem.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm font-medium">{caseItem.id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{caseItem.employee_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{caseItem.employee_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{caseItem.client_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{caseItem.entity_name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{caseItem.package_name || getPackageName(caseItem.package_id)}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={caseItem.source === 'data_entry' ? 'bg-purple-100 text-purple-700' : 
                                           caseItem.source === 'hr_upload' ? 'bg-blue-100 text-blue-700' : 
                                           'bg-slate-100 text-slate-700'}>
                            {caseItem.source === 'data_entry' ? 'Data Entry' : 
                             caseItem.source === 'hr_upload' ? 'HR Upload' : 'Employee'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {caseItem.created_at ? formatDateDDMMYYYY(caseItem.created_at) : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(caseItem.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => handleOpenCaseDetail(caseItem)}>
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => prepareAssignConfirmation(caseItem)}>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            {canStopCheck(caseItem) && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setShowStopCheck(caseItem)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                data-testid={`stop-check-btn-${caseItem.id}`}
                              >
                                <StopCircle className="w-4 h-4 mr-1" />
                                Stop
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
            
            {/* Pagination */}
            {filteredPendingQC.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                <span className="text-sm text-slate-600">
                  Showing {pendingQCStartIndex + 1} to {Math.min(pendingQCStartIndex + pendingQCPageSize, filteredPendingQC.length)} of {filteredPendingQC.length} cases
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPendingQCPage(1)} disabled={pendingQCPage === 1}>First</Button>
                  <Button variant="outline" size="sm" onClick={() => setPendingQCPage(p => Math.max(1, p - 1))} disabled={pendingQCPage === 1}>Previous</Button>
                  <span className="px-3 py-1 text-sm font-medium">Page {pendingQCPage} of {totalPendingQCPages || 1}</span>
                  <Button variant="outline" size="sm" onClick={() => setPendingQCPage(p => Math.min(totalPendingQCPages, p + 1))} disabled={pendingQCPage >= totalPendingQCPages}>Next</Button>
                  <Button variant="outline" size="sm" onClick={() => setPendingQCPage(totalPendingQCPages)} disabled={pendingQCPage >= totalPendingQCPages}>Last</Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      );
    }

    if (activeTab === 'data-entry') {
      return (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Data Entry</h1>
              <p className="text-slate-600">Add employees and enter verification check details</p>
            </div>
          </div>

          <Tabs value={dataEntrySubTab} onValueChange={setDataEntrySubTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="add-employee" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </TabsTrigger>
              <TabsTrigger value="check-details" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <FormInput className="w-4 h-4 mr-2" />
                Check Details Entry
              </TabsTrigger>
            </TabsList>

            {/* Add Employee Tab */}
            <TabsContent value="add-employee">
              <div className="space-y-6">
                <div className="flex justify-end gap-3">
                  <Button onClick={() => setShowBulkImport(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Import
                  </Button>
                  <Button onClick={() => setShowAddEmployee(true)} className="bg-slate-900 hover:bg-slate-800">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Employee
                  </Button>
                </div>

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle>Select Client & Entity for Entry</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <Label>Client</Label>
                        <Select value={selectedClient} onValueChange={(v) => { setSelectedClient(v); setSelectedEntity(''); }}>
                          <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                          <SelectContent>
                            {clients.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Entity</Label>
                        <Select value={selectedEntity} onValueChange={setSelectedEntity} disabled={!selectedClient}>
                          <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                          <SelectContent>
                            {getClientEntities(selectedClient).map(e => (
                              <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {selectedEntity && (
                      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-emerald-700 font-medium">Ready for data entry</p>
                        <p className="text-sm text-emerald-600">
                          Click "Add Employee" to add candidates for {getClientName(selectedClient)} - {getEntityName(selectedEntity)}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Check Details Entry Tab */}
            <TabsContent value="check-details">
              {caseForCheckEntry?._fromDrafts && (
                <div className="mb-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setCaseForCheckEntry(null);
                      setActiveTab('candidate-drafts');
                    }}
                    data-testid="back-to-drafts-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to Candidate Drafts
                  </Button>
                </div>
              )}
              <CheckDetailsEntry 
                initialCase={caseForCheckEntry}
                onClearInitialCase={() => setCaseForCheckEntry(null)}
              />
            </TabsContent>
          </Tabs>
        </div>
      );
    }

    if (activeTab === 'insufficiencies') {
      // Get unique check types for filter dropdown
      const uniqueCheckTypes = [...new Set(insufficiencies.map(i => i.check_type))].filter(Boolean);
      
      // Apply all filters and sorting
      const filteredInsufficiencies = insufficiencies
        .filter(insuff => {
          // Response filter
          if (insufficiencyFilter === 'pending' && insuff.candidate_response) return false;
          if (insufficiencyFilter === 'received' && !insuff.candidate_response) return false;
          
          // Check type filter
          if (insufficiencyCheckTypeFilter !== 'all' && insuff.check_type !== insufficiencyCheckTypeFilter) return false;

          // Age filter (calendar days since raised). Added May 24, 2026
          // primarily to make the Amber Notice flow ergonomic.
          if (insufficiencyAgeFilter !== 'all') {
            const raisedDt = new Date(insuff.date_raised || insuff.raised_at || insuff.created_at);
            const ageDays = isNaN(raisedDt.getTime())
              ? null
              : Math.floor((new Date() - raisedDt) / (1000 * 60 * 60 * 24));
            if (ageDays === null) return false;
            if (insufficiencyAgeFilter === '<15' && !(ageDays < 15)) return false;
            if (insufficiencyAgeFilter === '15-29' && !(ageDays >= 15 && ageDays < 30)) return false;
            if (insufficiencyAgeFilter === '30-44' && !(ageDays >= 30 && ageDays < 45)) return false;
            if (insufficiencyAgeFilter === '>=45' && !(ageDays >= 45)) return false;
          }
          
          // Client/Entity search
          if (insufficiencyClientSearch) {
            const search = insufficiencyClientSearch.toLowerCase();
            const matchesClient = insuff.client_name?.toLowerCase().includes(search);
            const matchesEntity = insuff.entity_name?.toLowerCase().includes(search);
            const matchesEmployee = insuff.employee_name?.toLowerCase().includes(search);
            const matchesCaseId = insuff.case_id?.toLowerCase().includes(search);
            if (!matchesClient && !matchesEntity && !matchesEmployee && !matchesCaseId) return false;
          }
          
          return true;
        })
        // Sort by raised date (recent first)
        .sort((a, b) => {
          const dateA = new Date(a.date_raised || a.raised_at || 0);
          const dateB = new Date(b.date_raised || b.raised_at || 0);
          return dateB - dateA;
        });
      
      // Pagination
      const totalItems = filteredInsufficiencies.length;
      const totalPages = Math.ceil(totalItems / insufficiencyPageSize);
      const startIndex = (insufficiencyPage - 1) * insufficiencyPageSize;
      const endIndex = startIndex + insufficiencyPageSize;
      const paginatedInsufficiencies = filteredInsufficiencies.slice(startIndex, endIndex);
      
      // Reset page when filters change
      const resetPage = () => setInsufficiencyPage(1);

      // Build candidate email map for dialog
      const selectedInsuffData = insufficiencies.filter(i => selectedInsufficiencies.includes(i.insufficiency_id));
      const candidateEmailMap = {};
      selectedInsuffData.forEach(insuff => {
        const email = insuff.employee_email;
        if (email) {
          if (!candidateEmailMap[email]) {
            candidateEmailMap[email] = { email, employee_name: insuff.employee_name, count: 0 };
          }
          candidateEmailMap[email].count += 1;
        }
      });
      const candidateEmailList = Object.values(candidateEmailMap);
      
      return (
        <div>
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Insufficiencies</h1>
            <p className="text-slate-600">View and clear open insufficiencies</p>
          </div>
          
          {/* Filters Row */}
          <Card className="mb-4 border-slate-200">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <Input
                    placeholder="Search by Case ID, Client, Entity, or Employee..."
                    value={insufficiencyClientSearch}
                    onChange={(e) => { setInsufficiencyClientSearch(e.target.value); resetPage(); }}
                    className="w-full"
                    data-testid="insufficiency-search-input"
                  />
                </div>
                
                {/* Check Type Filter */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600 whitespace-nowrap">Check Type:</Label>
                  <Select value={insufficiencyCheckTypeFilter} onValueChange={(v) => { setInsufficiencyCheckTypeFilter(v); resetPage(); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Check Types</SelectItem>
                      {uniqueCheckTypes.map(type => (
                        <SelectItem key={type} value={type}>{type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Response Filter */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600 whitespace-nowrap">Response:</Label>
                  <Select value={insufficiencyFilter} onValueChange={(v) => { setInsufficiencyFilter(v); resetPage(); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending Response</SelectItem>
                      <SelectItem value="received">Response Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Age Filter — May 24, 2026 — surfaces >=45 day items for Amber Notice */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600 whitespace-nowrap">Age:</Label>
                  <Select
                    value={insufficiencyAgeFilter}
                    onValueChange={(v) => { setInsufficiencyAgeFilter(v); resetPage(); }}
                  >
                    <SelectTrigger className="w-[170px]" data-testid="insufficiency-age-filter">
                      <SelectValue placeholder="All ages" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All ages</SelectItem>
                      <SelectItem value="<15">&lt; 15 days</SelectItem>
                      <SelectItem value="15-29">15–29 days</SelectItem>
                      <SelectItem value="30-44">30–44 days</SelectItem>
                      <SelectItem value=">=45">≥ 45 days (Amber)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                {/* Send Client Reminders Button */}
                <Button
                  data-testid="send-client-reminders-btn"
                  variant={selectedInsufficiencies.length > 0 ? "default" : "outline"}
                  className={selectedInsufficiencies.length > 0 ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
                  disabled={selectedInsufficiencies.length === 0 || sendingReminders}
                  onClick={handleBulkSendClientReminders}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingReminders ? 'Sending...' : `Send Client Reminders${selectedInsufficiencies.length > 0 ? ` (${selectedInsufficiencies.length})` : ''}`}
                </Button>

                {/* Send Candidate Emails Button */}
                <Button
                  data-testid="send-candidate-emails-btn"
                  variant={selectedInsufficiencies.length > 0 ? "default" : "outline"}
                  className={selectedInsufficiencies.length > 0 ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
                  disabled={selectedInsufficiencies.length === 0 || sendingReminders}
                  onClick={openCandidateEmailDialog}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {`Send Candidate Emails${selectedInsufficiencies.length > 0 ? ` (${candidateEmailList.length} emails)` : ''}`}
                </Button>

                {/* Send Amber Notice Button — May 24, 2026 — 45-day closure warning */}
                <Button
                  data-testid="send-amber-notice-btn"
                  variant={eligibleAmberCount > 0 ? "default" : "outline"}
                  className={eligibleAmberCount > 0
                    ? "bg-gradient-to-r from-orange-600 to-amber-700 hover:from-orange-700 hover:to-amber-800 text-white border-0"
                    : "text-amber-700 border-amber-300"}
                  disabled={eligibleAmberCount === 0 || sendingAmber || sendingReminders}
                  onClick={() => setShowAmberConfirm(true)}
                  title="Send AMBER closure notice for items aged ≥ 45 days"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {sendingAmber
                    ? 'Sending...'
                    : `Send Amber Notice${eligibleAmberCount > 0 ? ` (${eligibleAmberCount})` : ''}`}
                </Button>
                
                {selectedInsufficiencies.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Client emails are consolidated per entity. Candidate emails let you select valid email IDs before sending.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pagination & Page Size - MOVED TO TOP */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between mb-3 px-2">
              <div className="flex items-center gap-4">
                <p className="text-sm text-slate-600">
                  Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} insufficiencies
                </p>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Per page:</Label>
                  <Select value={String(insufficiencyPageSize)} onValueChange={(v) => { setInsufficiencyPageSize(Number(v)); setInsufficiencyPage(1); }}>
                    <SelectTrigger className="w-[80px] h-8 text-sm" data-testid="insufficiency-page-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInsufficiencyPage(1)}
                  disabled={insufficiencyPage === 1}
                  data-testid="insufficiency-first-btn"
                  title="First page"
                >
                  « First
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setInsufficiencyPage(p => Math.max(1, p - 1))}
                  disabled={insufficiencyPage === 1}
                  data-testid="insufficiency-prev-btn"
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {insufficiencyPage} of {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setInsufficiencyPage(p => Math.min(totalPages, p + 1))}
                  disabled={insufficiencyPage >= totalPages}
                  data-testid="insufficiency-next-btn"
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInsufficiencyPage(totalPages)}
                  disabled={insufficiencyPage >= totalPages}
                  data-testid="insufficiency-last-btn"
                  title="Last page"
                >
                  Last »
                </Button>
              </div>
            </div>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        data-testid="insufficiency-select-all"
                        checked={paginatedInsufficiencies.length > 0 && paginatedInsufficiencies.every(i => selectedInsufficiencies.includes(i.insufficiency_id))}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedInsufficiencies(prev => {
                              const newIds = paginatedInsufficiencies.map(i => i.insufficiency_id).filter(id => !prev.includes(id));
                              return [...prev, ...newIds];
                            });
                          } else {
                            const pageIds = paginatedInsufficiencies.map(i => i.insufficiency_id);
                            setSelectedInsufficiencies(prev => prev.filter(id => !pageIds.includes(id)));
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Client / Entity</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Employee Email</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Raised At ↓</TableHead>
                    <TableHead>Ageing</TableHead>
                    <TableHead>Response</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedInsufficiencies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                        No insufficiencies found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedInsufficiencies.map((insuff, idx) => (
                      <TableRow key={idx} className={selectedInsufficiencies.includes(insuff.insufficiency_id) ? 'bg-rose-50' : ''}>
                        <TableCell>
                          <Checkbox
                            data-testid={`insufficiency-select-${idx}`}
                            checked={selectedInsufficiencies.includes(insuff.insufficiency_id)}
                            onCheckedChange={(checked) => {
                              setSelectedInsufficiencies(prev =>
                                checked
                                  ? [...prev, insuff.insufficiency_id]
                                  : prev.filter(id => id !== insuff.insufficiency_id)
                              );
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{insuff.case_id}</TableCell>
                        <TableCell>
                          <p className="font-medium text-slate-800">{insuff.client_name || '-'}</p>
                          <p className="text-xs text-slate-500">{insuff.entity_name || '-'}</p>
                        </TableCell>
                        <TableCell>{insuff.employee_name}</TableCell>
                        <TableCell>
                          <span className={`text-sm ${insuff.employee_email ? 'text-blue-600' : 'text-slate-400 italic'}`}>
                            {insuff.employee_email || 'No email'}
                          </span>
                        </TableCell>
                        <TableCell><Badge variant="outline">{insuff.check_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Badge></TableCell>
                        <TableCell className="max-w-xs">
                          <div className="text-xs font-semibold text-rose-600 uppercase">{(insuff.reason || '').replace(/_/g, ' ')}</div>
                          <div className="text-xs text-slate-600 mt-0.5 line-clamp-2" title={insuff.insufficiency_detail || insuff.description || ''}>
                            {insuff.insufficiency_detail || insuff.description || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDateDDMMYYYY(insuff.date_raised || insuff.raised_at)}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const raised = new Date(insuff.date_raised || insuff.raised_at);
                            const ageDays = isNaN(raised.getTime()) ? null : Math.floor((new Date() - raised) / (1000 * 60 * 60 * 24));
                            if (ageDays === null) return <span className="text-slate-400">-</span>;
                            return (
                              <span className={`text-sm font-semibold ${ageDays >= 30 ? 'text-red-600' : ageDays >= 15 ? 'text-amber-600' : 'text-slate-700'}`} data-testid={`insufficiency-ageing-${idx}`}>
                                {ageDays} day{ageDays !== 1 ? 's' : ''}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {insuff.candidate_response ? (
                            <div>
                              <Badge className="bg-emerald-100 text-emerald-700 mb-1">Response Received</Badge>
                              <p className="text-xs text-slate-600 max-w-xs truncate" title={insuff.candidate_response}>
                                {insuff.candidate_response}
                              </p>
                            </div>
                          ) : (
                            <Badge className="bg-rose-100 text-rose-700">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              // Find the case in allCases and open detail view
                              const caseData = allCases.find(c => c.id === insuff.case_id);
                              if (caseData) {
                                setShowCaseDetail(caseData);
                                fetchCaseDetail(caseData.id);
                              } else {
                                // If not found in allCases, fetch directly
                                fetchCaseDetail(insuff.case_id);
                                setShowCaseDetail({ id: insuff.case_id });
                              }
                            }}
                            className="text-blue-600"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-emerald-600"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Clear
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Clear Insufficiency?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  <div className="space-y-2">
                                    <p>Are you sure you want to clear this insufficiency?</p>
                                    <div className="bg-slate-50 p-3 rounded-lg text-sm">
                                      <p><strong>Case:</strong> {insuff.case_id}</p>
                                      <p><strong>Employee:</strong> {insuff.employee_name}</p>
                                      <p><strong>Check Type:</strong> {insuff.check_type}</p>
                                      <p><strong>Reason:</strong> {insuff.reason}</p>
                                      {insuff.candidate_response && (
                                        <p className="text-emerald-600"><strong>Response:</strong> {insuff.candidate_response}</p>
                                      )}
                                    </div>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleClearInsufficiency(insuff.case_id, insuff.check_id)}
                                >
                                  Clear Insufficiency
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>


          {/* Amber Notice Confirmation Dialog — May 24, 2026 */}
          <Dialog open={showAmberConfirm} onOpenChange={setShowAmberConfirm}>
            <DialogContent className="max-w-md" data-testid="amber-notice-confirm-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-5 h-5" />
                  Send AMBER Closure Notice
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="bg-amber-50 border-l-4 border-amber-600 p-3 rounded">
                  <strong className="text-amber-900">{eligibleAmberCount}</strong> insufficienc{eligibleAmberCount === 1 ? 'y has' : 'ies have'} crossed the 45-day stipulated timeline. A closure-warning email will be sent stating these checks will be <strong>closed as AMBER</strong>; re-initiation possible if cleared later.
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div><strong>To:</strong> Entity HR users + Entity contacts + Candidate(s)</div>
                  <div><strong>CC:</strong> QC team + QC Manager (bgv@veecheck.com) + Admin</div>
                  <div><strong>From:</strong> QC Manager (bgv@veecheck.com)</div>
                  <div className="pt-1 text-slate-500">Items aged &lt; 45 days in your selection will be skipped by the server.</div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setShowAmberConfirm(false)} data-testid="amber-notice-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-gradient-to-r from-orange-600 to-amber-700 hover:from-orange-700 hover:to-amber-800 text-white"
                  onClick={handleSendAmberNotice}
                  disabled={sendingAmber}
                  data-testid="amber-notice-confirm-send"
                >
                  {sendingAmber ? 'Sending...' : 'Send Amber Notice'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>


          {/* Candidate Email Selection Dialog */}
          <Dialog open={showCandidateEmailDialog} onOpenChange={setShowCandidateEmailDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Select Candidate Emails to Send</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <p className="text-sm text-slate-500 mb-3">
                  Uncheck any dummy or invalid email addresses before sending. Only checked emails will receive the insufficiency reminder.
                </p>
                {candidateEmailList.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No candidate emails found for the selected insufficiencies.</p>
                ) : (
                  candidateEmailList.map((item) => (
                    <div key={item.email} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 border border-slate-100">
                      <Checkbox
                        data-testid={`candidate-email-check-${item.email}`}
                        checked={candidateEmailSelections[item.email] || false}
                        onCheckedChange={(checked) => {
                          setCandidateEmailSelections(prev => ({ ...prev, [item.email]: !!checked }));
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{item.employee_name}</p>
                        <p className="text-sm text-blue-600 truncate">{item.email}</p>
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">{item.count} item{item.count > 1 ? 's' : ''}</Badge>
                    </div>
                  ))
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCandidateEmailDialog(false)}>Cancel</Button>
                <Button 
                  data-testid="confirm-send-candidate-emails-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={candidateEmailList.length === 0 || !Object.values(candidateEmailSelections).some(v => v)}
                  onClick={handleSendCandidateEmails}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send to {Object.values(candidateEmailSelections).filter(v => v).length} Candidate{Object.values(candidateEmailSelections).filter(v => v).length !== 1 ? 's' : ''}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    if (activeTab === 'final-report-qc') {
      // Apply filters to final report cases
      let filteredFinalReport = [...finalReportCases];
      
      // Search
      if (finalReportSearch) {
        const search = finalReportSearch.toLowerCase();
        filteredFinalReport = filteredFinalReport.filter(c =>
          c.id?.toLowerCase().includes(search) ||
          c.employee_name?.toLowerCase().includes(search) ||
          c.client_name?.toLowerCase().includes(search) ||
          c.entity_name?.toLowerCase().includes(search)
        );
      }
      
      // Client filter
      if (finalReportClientFilter !== 'all') {
        filteredFinalReport = filteredFinalReport.filter(c => c.client_name === finalReportClientFilter);
      }
      
      // Entity filter
      if (finalReportEntityFilter !== 'all') {
        filteredFinalReport = filteredFinalReport.filter(c => c.entity_name === finalReportEntityFilter);
      }
      
      // Date filter
      if (finalReportDateFilter !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        filteredFinalReport = filteredFinalReport.filter(c => {
          if (!c.created_at) return false;
          const caseDate = new Date(c.created_at);
          switch (finalReportDateFilter) {
            case 'today': return caseDate >= today;
            case 'week':
              const weekAgo = new Date(today);
              weekAgo.setDate(weekAgo.getDate() - 7);
              return caseDate >= weekAgo;
            case 'month':
              const monthAgo = new Date(today);
              monthAgo.setMonth(monthAgo.getMonth() - 1);
              return caseDate >= monthAgo;
            default: return true;
          }
        });
      }
      
      // Sort
      if (finalReportSortBy === 'recent') {
        filteredFinalReport.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      } else {
        filteredFinalReport.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      }
      
      // Pagination
      const totalFinalReportPages = Math.ceil(filteredFinalReport.length / finalReportPageSize);
      const finalReportStartIndex = (finalReportPage - 1) * finalReportPageSize;
      const paginatedFinalReport = filteredFinalReport.slice(finalReportStartIndex, finalReportStartIndex + finalReportPageSize);
      
      // Get unique clients and entities for filters
      const uniqueFinalReportClients = [...new Set(finalReportCases.map(c => c.client_name).filter(Boolean))];
      const uniqueFinalReportEntities = [...new Set(finalReportCases.map(c => c.entity_name).filter(Boolean))];
      
      return (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Final Report QC</h1>
              <p className="text-slate-600">Review completed verification reports before sending to client</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Badge className="bg-purple-100 text-purple-700 text-lg px-4 py-2">
                {filteredFinalReport.length} of {finalReportCases.length} Reports
              </Badge>
            </div>
          </div>

          {/* Search and Filters */}
          <Card className="border-slate-200 shadow-sm mb-4">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-[250px]">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Search by Case ID, Employee, Client..."
                      value={finalReportSearch}
                      onChange={(e) => { setFinalReportSearch(e.target.value); setFinalReportPage(1); }}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Client:</label>
                  <Select value={finalReportClientFilter} onValueChange={(v) => { setFinalReportClientFilter(v); setFinalReportPage(1); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {uniqueFinalReportClients.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Entity:</label>
                  <Select value={finalReportEntityFilter} onValueChange={(v) => { setFinalReportEntityFilter(v); setFinalReportPage(1); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Entities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Entities</SelectItem>
                      {uniqueFinalReportEntities.map(e => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Received:</label>
                  <Select value={finalReportDateFilter} onValueChange={(v) => { setFinalReportDateFilter(v); setFinalReportPage(1); }}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Sort:</label>
                  <Select value={finalReportSortBy} onValueChange={(v) => setFinalReportSortBy(v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Latest First" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Latest First</SelectItem>
                      <SelectItem value="oldest">Oldest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setFinalReportSearch(''); setFinalReportClientFilter('all'); setFinalReportEntityFilter('all'); setFinalReportDateFilter('all'); setFinalReportSortBy('recent'); setFinalReportPage(1); }}
                  className="text-slate-500"
                >
                  <X className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client / Entity</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Checks Completed</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedFinalReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-purple-300" />
                        <p>No reports pending QC review</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedFinalReport.map((caseItem) => {
                      const completedChecks = caseItem.checks?.filter(c => c.status === 'verified' || c.status === 'failed').length || 0;
                      const totalChecks = caseItem.checks?.length || 0;
                      return (
                        <TableRow key={caseItem.id} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm font-medium">{caseItem.id}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{caseItem.employee_name || 'N/A'}</p>
                              <p className="text-xs text-slate-500">{caseItem.employee_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{caseItem.client_name || 'N/A'}</p>
                              <p className="text-xs text-slate-500">{caseItem.entity_name}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{caseItem.package_name || getPackageName(caseItem.package_id)}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-emerald-500" 
                                  style={{ width: `${totalChecks > 0 ? (completedChecks / totalChecks) * 100 : 0}%` }}
                                />
                              </div>
                              <span className="text-sm text-slate-500">{completedChecks}/{totalChecks}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {caseItem.created_at ? formatDateDDMMYYYY(caseItem.created_at) : '-'}
                          </TableCell>
                          <TableCell>{getStatusBadge(caseItem.status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={() => handleOpenCaseDetail(caseItem)}>
                                <Eye className="w-4 h-4 mr-1" />
                                Review
                              </Button>
                              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={async () => {
                                try {
                                  await axios.put(`/cases/${caseItem.id}/status`, { status: 'report_approved' });
                                  toast.success('Report approved and sent to client');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to approve report');
                                }
                              }}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve Report
                              </Button>
                              {canStopCheck(caseItem) && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setShowStopCheck(caseItem)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                >
                                  <StopCircle className="w-4 h-4 mr-1" />
                                  Stop
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
            
            {/* Pagination */}
            {filteredFinalReport.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                <span className="text-sm text-slate-600">
                  Showing {finalReportStartIndex + 1} to {Math.min(finalReportStartIndex + finalReportPageSize, filteredFinalReport.length)} of {filteredFinalReport.length} reports
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setFinalReportPage(1)} disabled={finalReportPage === 1}>First</Button>
                  <Button variant="outline" size="sm" onClick={() => setFinalReportPage(p => Math.max(1, p - 1))} disabled={finalReportPage === 1}>Previous</Button>
                  <span className="px-3 py-1 text-sm font-medium">Page {finalReportPage} of {totalFinalReportPages || 1}</span>
                  <Button variant="outline" size="sm" onClick={() => setFinalReportPage(p => Math.min(totalFinalReportPages, p + 1))} disabled={finalReportPage >= totalFinalReportPages}>Next</Button>
                  <Button variant="outline" size="sm" onClick={() => setFinalReportPage(totalFinalReportPages)} disabled={finalReportPage >= totalFinalReportPages}>Last</Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      );
    }

    if (activeTab === 'edit-requests') {
      return (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Edit Requests</h1>
              <p className="text-slate-500">Review and approve/reject employee data edit requests from HR</p>
            </div>
            <div className="flex items-center gap-4">
              <Select value={editRequestFilter} onValueChange={setEditRequestFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Requests</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchEditRequests}>
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-700">{editRequests.filter(r => r.status === 'pending').length}</div>
                <div className="text-sm text-amber-600">Pending Review</div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-emerald-700">{editRequests.filter(r => r.status === 'approved').length}</div>
                <div className="text-sm text-emerald-600">Approved</div>
              </CardContent>
            </Card>
            <Card className="border-rose-200 bg-rose-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-rose-700">{editRequests.filter(r => r.status === 'rejected').length}</div>
                <div className="text-sm text-rose-600">Rejected</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-slate-700">{editRequests.length}</div>
                <div className="text-sm text-slate-600">Total Requests</div>
              </CardContent>
            </Card>
          </div>

          {/* Requests Table */}
          <Card>
            <CardContent className="p-0">
              {editRequestsLoading ? (
                <div className="p-8 text-center text-slate-500">Loading edit requests...</div>
              ) : getFilteredEditRequests().length === 0 ? (
                <div className="p-8 text-center text-slate-500">No edit requests found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Changed Fields</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredEditRequests().map((request) => {
                      const changedFields = Object.keys(request.updated_data || {}).filter(
                        key => request.updated_data[key] !== request.original_data?.[key]
                      );
                      return (
                        <TableRow key={request.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{request.original_data?.name || 'N/A'}</p>
                              <p className="text-xs text-slate-500">{request.original_data?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {changedFields.map(field => (
                                <Badge key={field} variant="outline" className="text-xs">
                                  {field.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{request.reason}</TableCell>
                          <TableCell className="text-xs">{request.requested_by_name || request.requested_by}</TableCell>
                          <TableCell className="text-xs">{formatDateDDMMYYYY(request.created_at)}</TableCell>
                          <TableCell>
                            <Badge className={
                              request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-rose-100 text-rose-700'
                            }>
                              {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {request.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" onClick={() => setReviewingRequest({ ...request, action: 'approve' })}>
                                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => setReviewingRequest({ ...request, action: 'reject' })}>
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => setReviewingRequest(request)}>
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Review Dialog */}
          <Dialog open={!!reviewingRequest} onOpenChange={(o) => { if (!o) { setReviewingRequest(null); setReviewNotes(''); } }}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5" />
                  {reviewingRequest?.status === 'pending' ? 'Review Edit Request' : 'Edit Request Details'}
                </DialogTitle>
              </DialogHeader>
              
              {/* Action Buttons at Top for Pending Requests */}
              {reviewingRequest?.status === 'pending' && (
                <div className="flex justify-between items-center py-3 px-4 bg-slate-50 rounded-lg border">
                  <div>
                    <Label className="text-sm">Review Notes (Optional)</Label>
                    <Input 
                      value={reviewNotes} 
                      onChange={(e) => setReviewNotes(e.target.value)} 
                      placeholder="Add notes..."
                      className="w-64 mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => handleReviewEditRequest(reviewingRequest.id, 'reject')}>
                      <XCircle className="w-4 h-4 mr-2" /> Reject
                    </Button>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleReviewEditRequest(reviewingRequest.id, 'approve')}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                  </div>
                </div>
              )}
              
              {reviewingRequest && (
                <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Employee: {reviewingRequest.original_data?.name}</h4>
                    <p className="text-sm text-slate-500">{reviewingRequest.original_data?.email}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                      <h4 className="font-medium text-rose-700 mb-2">Original Data</h4>
                      <div className="space-y-1 text-sm">
                        {Object.entries(reviewingRequest.original_data || {}).filter(([k]) => !['id', 'case_id', 'entity_id', 'created_at', 'updated_at'].includes(k)).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                            <span className="font-medium">{value || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <h4 className="font-medium text-emerald-700 mb-2">Updated Data</h4>
                      <div className="space-y-1 text-sm">
                        {Object.entries(reviewingRequest.updated_data || {}).filter(([k]) => !['id', 'case_id', 'entity_id', 'created_at', 'updated_at'].includes(k)).map(([key, value]) => {
                          const isChanged = value !== reviewingRequest.original_data?.[key];
                          return (
                            <div key={key} className={`flex justify-between ${isChanged ? 'bg-emerald-100 px-1 rounded' : ''}`}>
                              <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                              <span className={`font-medium ${isChanged ? 'text-emerald-700' : ''}`}>{value || '-'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-amber-700 mb-1">Reason for Edit</h4>
                    <p className="text-sm">{reviewingRequest.reason}</p>
                  </div>

                  {reviewingRequest.status !== 'pending' && reviewingRequest.review_notes && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-1">Review Notes</h4>
                      <p className="text-sm">{reviewingRequest.review_notes}</p>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="border-t pt-4 mt-4">
                <Button variant="outline" onClick={() => { setReviewingRequest(null); setReviewNotes(''); }}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    if (activeTab === 'candidate-drafts') {
      return <CandidateDraftsTab 
        onEditDraft={(draft) => {
          // Navigate to Check Details Entry with the draft's case pre-loaded
          setCaseForCheckEntry({ id: draft.case_id, employee_id: draft.id, employee_name: draft.name, _fromDrafts: true });
          setActiveTab('data-entry');
          setDataEntrySubTab('check-details');
        }}
      />;
    }

    if (activeTab === 'stopped-cases') {
      const filteredStoppedCases = getFilteredStoppedCases();
      
      return (
        <div>
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Stopped Cases</h1>
              <p className="text-slate-600">View all stopped verification cases</p>
            </div>
            <Badge className="bg-red-100 text-red-700 text-lg px-4 py-2">
              {stoppedCases.length} Total Stopped
            </Badge>
          </div>

          {/* Filters */}
          <Card className="border-slate-200 shadow-sm mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Stop Reason</Label>
                  <Select value={stoppedReasonFilter} onValueChange={setStoppedReasonFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Reasons" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Reasons</SelectItem>
                      <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                      <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                      <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">From Date</Label>
                  <Input 
                    type="date" 
                    value={stoppedDateFrom} 
                    onChange={(e) => setStoppedDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">To Date</Label>
                  <Input 
                    type="date" 
                    value={stoppedDateTo} 
                    onChange={(e) => setStoppedDateTo(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={() => { setStoppedReasonFilter('all'); setStoppedDateFrom(''); setStoppedDateTo(''); }}
                    className="w-full"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stopped Cases Table */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <StopCircle className="w-5 h-5 text-red-600" />
                Stopped Cases ({filteredStoppedCases.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Stop Reason</TableHead>
                    <TableHead>Stopped At</TableHead>
                    <TableHead>Checks Stopped</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStoppedCases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        <StopCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No stopped cases found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStoppedCases.map((caseItem) => {
                      const stoppedChecks = (caseItem.checks || []).filter(c => c.status === 'stop_check').length;
                      return (
                        <TableRow key={caseItem.id} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm">{caseItem.id}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{caseItem.employee_name || 'N/A'}</p>
                              <p className="text-xs text-slate-500">{caseItem.employee_email || ''}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{caseItem.client_name || 'N/A'}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-red-100 text-red-700">
                              {getStopReasonLabel(caseItem.stop_reason)}
                            </Badge>
                            {caseItem.stop_description && (
                              <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate" title={caseItem.stop_description}>
                                {caseItem.stop_description}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {caseItem.stopped_at ? new Date(caseItem.stopped_at).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-slate-100 text-slate-700">{stoppedChecks} checks</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => handleOpenCaseDetail(caseItem)}>
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      );
    }

    return null;
  };

  // Render Case Detail Modal Content
  const renderCaseDetailContent = () => {
    if (!caseDetailData) return <div className="p-8 text-center">Loading...</div>;
    
    const { employee, case: caseInfo } = caseDetailData;
    
    // Get documents relevant to the current view for quick access
    const getRelevantDocs = (checkType) => {
      const allDocs = caseDetailData?.checkDocuments || {};
      // Find check by type and get its documents
      const check = caseInfo?.checks?.find(c => c.check_type === checkType);
      return check ? (allDocs[check.id || check.check_id] || []) : [];
    };

    const idDocs = getRelevantDocs('id_check');
    const employmentDocs = getRelevantDocs('employment_check');
    const educationDocs = getRelevantDocs('education_check');
    const addressDocs = getRelevantDocs('address_check');
    const criminalCourtDocs = getRelevantDocs('criminal_court_check');
    const creditDocs = getRelevantDocs('credit_check');
    const worldDbDocs = getRelevantDocs('world_criminal_db');
    const advocateDocs = getRelevantDocs('court_advocate_check');
    
    // Get check by type from case (handles both string and object check_type shapes)
    const getCheckByType = (checkType) => caseInfo?.checks?.find(c => {
      const t = typeof c.check_type === 'object' ? c.check_type?.check_type : c.check_type;
      return t === checkType;
    });
    
    // Get insufficiency for a check
    const getCheckInsufficiency = (checkId) => {
      return caseDetailData?.check_insufficiencies?.find(i => i.check_id === checkId && i.status === 'open');
    };
    
    // Render insufficiency alert for a check
    const renderCheckInsufficiencyAlert = (checkType) => {
      const check = getCheckByType(checkType);
      if (!check) return null;
      const checkInsuff = getCheckInsufficiency(check.check_id);
      if (!checkInsuff) return null;
      
      return (
        <div className="mt-4 p-4 bg-rose-50 border border-rose-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-rose-700">Insufficiency Raised</p>
                  <p className="text-sm text-rose-600 font-medium"><strong>Reason:</strong> {checkInsuff.reason}</p>
                </div>
                <Badge variant="outline" className={`${checkInsuff.status === 'waiting_for_clearance' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-rose-100 text-rose-700 border-rose-300'}`}>
                  {checkInsuff.status === 'waiting_for_clearance' ? 'Awaiting Review' : checkInsuff.status}
                </Badge>
              </div>
              {checkInsuff.description && (
                <p className="text-sm text-rose-600 mt-1"><strong>Description:</strong> {checkInsuff.description}</p>
              )}
              <p className="text-xs text-rose-400 mt-2">
                Raised on: {checkInsuff.created_at ? formatDateDDMMYYYY(checkInsuff.created_at) : 'N/A'}
              </p>
              
              {/* Employee Response */}
              {(checkInsuff.candidate_response || checkInsuff.employee_response) && (
                <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm font-medium text-blue-800">Employee Response:</p>
                  <p className="text-sm text-blue-700">{checkInsuff.candidate_response || checkInsuff.employee_response}</p>
                </div>
              )}
              
              {/* Uploaded Documents */}
              {checkInsuff.uploaded_documents?.length > 0 && (
                <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800 mb-2">📎 Uploaded Documents ({checkInsuff.uploaded_documents.length}):</p>
                  <div className="space-y-1">
                    {checkInsuff.uploaded_documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-emerald-600" />
                        <a 
                          href={doc.file_url?.startsWith('http') ? doc.file_url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-700 hover:underline font-medium"
                        >
                          {doc.filename}
                        </a>
                        <span className="text-xs text-emerald-500">
                          ({doc.uploaded_at ? formatDateDDMMYYYY(doc.uploaded_at) : ''})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            {checkInsuff.uploaded_documents?.length > 0 && (
              <span className="text-sm text-amber-600 mr-2 flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                Review documents before clearing
              </span>
            )}
            <Button 
              size="sm" 
              variant="outline"
              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
              onClick={() => handleClearInsufficiency(caseInfo.id, check.check_id)}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Clear Insufficiency
            </Button>
          </div>
        </div>
      );
    };
    
    // Render insufficiency history trail for a check
    const renderInsufficiencyHistory = (checkType) => {
      const check = getCheckByType(checkType);
      if (!check || !check.insufficiency_history || check.insufficiency_history.length === 0) return null;
      
      return (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-slate-600" />
            <p className="font-semibold text-slate-700 text-sm">Insufficiency History ({check.insufficiency_history.length})</p>
          </div>
          <div className="space-y-3">
            {check.insufficiency_history.map((hist, idx) => (
              <div key={idx} className="p-3 bg-white border border-slate-200 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" /> Cleared
                    </Badge>
                    {hist.entry_name && (
                      <span className="ml-2 text-xs text-slate-500">{hist.entry_name}</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-700"><strong>Reason:</strong> {hist.reason}</p>
                {hist.description && (
                  <p className="text-sm text-slate-600"><strong>Details:</strong> {hist.description}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                  <span>Raised: {hist.raised_at ? formatDateDDMMYYYY(hist.raised_at) : 'N/A'}{hist.raised_by_name ? ` by ${hist.raised_by_name}` : ''}{hist.raised_by_role ? ` (${hist.raised_by_role.replace(/_/g, ' ')})` : ''}</span>
                  <span>Cleared: {hist.cleared_at ? formatDateDDMMYYYY(hist.cleared_at) : 'N/A'}{hist.cleared_by_name ? ` by ${hist.cleared_by_name}` : ''}{hist.cleared_by_role ? ` (${hist.cleared_by_role.replace(/_/g, ' ')})` : ''}</span>
                </div>
                
                {/* Employee Response in History */}
                {hist.candidate_response && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
                    <p className="text-xs font-medium text-blue-700">Employee Response:</p>
                    <p className="text-xs text-blue-600">{hist.candidate_response}</p>
                  </div>
                )}
                
                {/* Uploaded Documents in History */}
                {hist.uploaded_documents?.length > 0 && (
                  <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-100">
                    <p className="text-xs font-medium text-emerald-700 mb-1">📎 Documents ({hist.uploaded_documents.length}):</p>
                    <div className="space-y-1">
                      {hist.uploaded_documents.map((doc, docIdx) => (
                        <div key={docIdx} className="flex items-center gap-2 text-xs">
                          <FileText className="w-3 h-3 text-emerald-600" />
                          <a 
                            href={doc.file_url?.startsWith('http') ? doc.file_url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:underline"
                          >
                            {doc.filename}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };
    
    // Check if a check type has insufficiency
    const hasCheckInsufficiency = (checkType) => {
      const check = getCheckByType(checkType);
      return check && getCheckInsufficiency(check.check_id);
    };
    
    // Render entry-level insufficiency details box
    const renderEntryInsufficiencyDetails = (entryInsuff) => {
      if (!entryInsuff) return null;
      
      // Look up full insufficiency details from check_insufficiencies (includes uploaded docs)
      const fullInsuff = caseDetailData?.check_insufficiencies?.find(
        i => i.entry_type === entryInsuff.entry_type && i.entry_index === entryInsuff.entry_index
      ) || entryInsuff;
      
      return (
        <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <p className="font-semibold text-rose-700 text-sm">Insufficiency Details</p>
                <Badge variant="outline" className={`text-xs ${fullInsuff.status === 'waiting_for_clearance' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-rose-100 text-rose-700 border-rose-300'}`}>
                  {fullInsuff.status === 'waiting_for_clearance' ? 'Awaiting Review' : fullInsuff.status || entryInsuff.status}
                </Badge>
              </div>
              <p className="text-sm text-rose-600"><strong>Reason:</strong> {entryInsuff.reason}</p>
              {entryInsuff.description && (
                <p className="text-sm text-rose-600"><strong>Description:</strong> {entryInsuff.description}</p>
              )}
              <p className="text-xs text-rose-400 mt-1">
                Raised on: {entryInsuff.raised_at ? formatDateDDMMYYYY(entryInsuff.raised_at) : entryInsuff.created_at ? formatDateDDMMYYYY(entryInsuff.created_at) : 'N/A'}
              </p>
              
              {/* Employee Response */}
              {(fullInsuff.candidate_response || fullInsuff.employee_response) && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs font-medium text-blue-800">Employee Response:</p>
                  <p className="text-xs text-blue-700">{fullInsuff.candidate_response || fullInsuff.employee_response}</p>
                </div>
              )}
              
              {/* Uploaded Documents */}
              {fullInsuff.uploaded_documents?.length > 0 && (
                <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-200">
                  <p className="text-xs font-medium text-emerald-800 mb-1">📎 Uploaded Documents ({fullInsuff.uploaded_documents.length}):</p>
                  <div className="space-y-1">
                    {fullInsuff.uploaded_documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <FileText className="w-3 h-3 text-emerald-600" />
                        <a 
                          href={doc.file_url?.startsWith('http') ? doc.file_url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-700 hover:underline font-medium"
                        >
                          {doc.filename}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start p-4 bg-slate-50 rounded-lg">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold">{employee?.name}</h3>
              <Badge variant="outline" className={
                employee?.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                employee?.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                employee?.employee_category === 'trainee' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                'bg-blue-50 text-blue-700 border-blue-300'
              }>
                {(employee?.employee_category || (employee?.is_fresher ? 'fresher' : 'experienced')).charAt(0).toUpperCase() + (employee?.employee_category || (employee?.is_fresher ? 'fresher' : 'experienced')).slice(1)}
              </Badge>
            </div>
            <p className="text-sm text-slate-500">{employee?.email} • {employee?.phone}</p>
            <div className="flex gap-2 mt-2">
              <Badge variant="outline">{caseInfo?.id}</Badge>
              {getStatusBadge(caseInfo?.status)}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Client: <span className="font-medium text-slate-900">{caseDetailData.client_name}</span></p>
            <p className="text-sm text-slate-500">Entity: <span className="font-medium text-slate-900">{caseDetailData.entity_name}</span></p>
          </div>
        </div>

        {/* Edit Mode Controls */}
        <div className="flex justify-between items-center p-3 bg-slate-100 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch 
                checked={editMode} 
                onCheckedChange={(checked) => {
                  setEditMode(checked);
                  if (checked) initEditForm();
                }} 
              />
              <span className="text-sm font-medium">Edit Mode</span>
              {editMode && <Badge className="bg-purple-100 text-purple-700">Editing</Badge>}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-slate-600 border-slate-300"
              onClick={() => fetchAuditTrail(caseDetailData?.employee?.id)}
              disabled={auditLoading}
            >
              <History className="w-4 h-4 mr-1" />
              {auditLoading ? 'Loading...' : 'Audit Trail'}
            </Button>
          </div>
          {editMode && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={saveEditedData} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : <><Save className="w-4 h-4 mr-1" /> Save Changes</>}
              </Button>
            </div>
          )}
        </div>

        {/* Split View: Data + Document Preview */}
        <div className="flex gap-4">
          {/* Left Side: Tabs for different sections */}
          <div className={previewDoc ? 'w-1/2' : 'w-full'}>
            <Tabs defaultValue="personal" className="w-full">
              <TabsList className="w-full justify-start flex-wrap h-auto gap-1 p-1">
                <TabsTrigger value="personal" className="text-xs"><User className="w-3 h-3 mr-1" /> Personal</TabsTrigger>
                {caseInfo?.checks?.some(c => c.check_type === 'address_check') && <TabsTrigger value="address" className="text-xs"><MapPin className="w-3 h-3 mr-1" /> Address</TabsTrigger>}
                {caseInfo?.checks?.some(c => c.check_type === 'employment_check') && <TabsTrigger value="employment" className="text-xs"><Briefcase className="w-3 h-3 mr-1" /> Employment</TabsTrigger>}
                {caseInfo?.checks?.some(c => c.check_type === 'education_check') && <TabsTrigger value="education" className="text-xs"><GraduationCap className="w-3 h-3 mr-1" /> Education</TabsTrigger>}
                {getCheckByType('id_check') && <TabsTrigger value="id_check" className="text-xs bg-blue-50"><CreditCard className="w-3 h-3 mr-1" /> ID Check</TabsTrigger>}
                {getCheckByType('world_criminal_db') && <TabsTrigger value="world_db" className="text-xs bg-purple-50"><Globe className="w-3 h-3 mr-1" /> World DB</TabsTrigger>}
                {getCheckByType('credit_check') && <TabsTrigger value="credit_check" className="text-xs bg-emerald-50"><CreditCard className="w-3 h-3 mr-1" /> Credit</TabsTrigger>}
                {getCheckByType('criminal_court_check') && <TabsTrigger value="criminal_court" className="text-xs bg-amber-50"><Scale className="w-3 h-3 mr-1" /> Criminal Court</TabsTrigger>}
                {(getCheckByType('court_advocate_check') || getCheckByType('criminal_advocate_check')) && <TabsTrigger value="advocate_check" className="text-xs bg-rose-50"><Scale className="w-3 h-3 mr-1" /> Advocate</TabsTrigger>}
                {getCheckByType('india_criminal_db') && <TabsTrigger value="india_db" className="text-xs bg-orange-50"><Globe className="w-3 h-3 mr-1" /> India DB</TabsTrigger>}
                {getCheckByType('global_database') && <TabsTrigger value="global_db" className="text-xs bg-indigo-50"><Globe className="w-3 h-3 mr-1" /> Global DB</TabsTrigger>}
                {getCheckByType('reference_check') && <TabsTrigger value="reference_check" className="text-xs bg-cyan-50"><Users className="w-3 h-3 mr-1" /> Reference</TabsTrigger>}
                {getCheckByType('drug_test') && <TabsTrigger value="drug_test" className="text-xs bg-pink-50"><AlertTriangle className="w-3 h-3 mr-1" /> Drug Test</TabsTrigger>}
                {getCheckByType('professional_reference') && <TabsTrigger value="professional_ref" className="text-xs bg-indigo-50"><Briefcase className="w-3 h-3 mr-1" /> Prof Ref</TabsTrigger>}
                {getCheckByType('personal_reference') && <TabsTrigger value="personal_ref" className="text-xs bg-teal-50"><User className="w-3 h-3 mr-1" /> Personal Ref</TabsTrigger>}
              </TabsList>

          <TabsContent value="personal" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">Personal Details</h4>
                  <div className="flex gap-2">
                    {idDocs.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPreviewDoc(idDocs[0])}
                        className="text-blue-600"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View ID Documents ({idDocs.length})
                      </Button>
                    )}
                    {editMode && (
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => {
                          if (e.target.files[0]) uploadCheckDocument(e.target.files[0], 'id_check', 'id_document');
                        }} />
                        <Button variant="outline" size="sm" className="text-purple-600" asChild>
                          <span><Upload className="w-4 h-4 mr-1" /> Upload Document</span>
                        </Button>
                      </label>
                    )}
                  </div>
                </div>
                {editMode ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Full Name</Label><Input value={editFormData.name || ''} onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} /></div>
                    <div><Label>Father's Name</Label><Input value={editFormData.father_name || ''} onChange={(e) => setEditFormData({...editFormData, father_name: e.target.value})} /></div>
                    <div><Label>Date of Birth</Label><Input type="date" value={editFormData.dob || ''} onChange={(e) => setEditFormData({...editFormData, dob: e.target.value})} /></div>
                    <div><Label>Gender</Label>
                      <Select value={editFormData.gender || ''} onValueChange={(v) => setEditFormData({...editFormData, gender: v})}>
                        <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Email</Label><Input type="email" value={editFormData.email || ''} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} /></div>
                    <div><Label>Phone</Label><Input value={editFormData.phone || ''} onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})} /></div>
                    <div><Label>Alternate Phone</Label><Input value={editFormData.alternate_phone || ''} onChange={(e) => setEditFormData({...editFormData, alternate_phone: e.target.value})} /></div>
                    <div><Label>PAN Number</Label><Input value={editFormData.pan_number || ''} onChange={(e) => setEditFormData({...editFormData, pan_number: e.target.value.toUpperCase()})} placeholder="ABCDE1234F" /></div>
                    <div><Label>Aadhar Number</Label><Input value={editFormData.aadhar_number || ''} onChange={(e) => setEditFormData({...editFormData, aadhar_number: e.target.value})} placeholder="1234 5678 9012" /></div>
                    <div><Label>UAN Number</Label><Input value={editFormData.uan_number || ''} onChange={(e) => setEditFormData({...editFormData, uan_number: e.target.value})} /></div>
                    <div><Label>Passport Number</Label><Input value={editFormData.passport_number || ''} onChange={(e) => setEditFormData({...editFormData, passport_number: e.target.value.toUpperCase()})} /></div>
                    <div><Label>Driving License</Label><Input value={editFormData.driving_license || ''} onChange={(e) => setEditFormData({...editFormData, driving_license: e.target.value.toUpperCase()})} /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-500">Full Name</Label><p className="font-medium">{employee?.name || '-'}</p></div>
                    <div><Label className="text-slate-500">Father's Name</Label><p className="font-medium">{employee?.father_name || '-'}</p></div>
                    <div><Label className="text-slate-500">Date of Birth</Label><p className="font-medium">{employee?.dob || '-'}</p></div>
                    <div><Label className="text-slate-500">Gender</Label><p className="font-medium">{employee?.gender || '-'}</p></div>
                    <div><Label className="text-slate-500">Email</Label><p className="font-medium">{employee?.email || '-'}</p></div>
                    <div><Label className="text-slate-500">Phone</Label><p className="font-medium">{employee?.phone || '-'}</p></div>
                    <div><Label className="text-slate-500">Alternate Contact</Label><p className="font-medium">{employee?.alternate_phone || employee?.alternate_contact || '-'}</p></div>
                    <div><Label className="text-slate-500">Employee ID</Label><p className="font-medium">{employee?.employee_code || '-'}</p></div>
                    <div><Label className="text-slate-500">PAN Number</Label><p className="font-medium bg-yellow-50 px-2 py-1 rounded">{employee?.pan_number || '-'}</p></div>
                    <div><Label className="text-slate-500">Aadhar Number</Label><p className="font-medium bg-yellow-50 px-2 py-1 rounded">{employee?.aadhar_number || '-'}</p></div>
                    <div><Label className="text-slate-500">UAN Number</Label><p className="font-medium">{employee?.uan_number || '-'}</p></div>
                    <div><Label className="text-slate-500">Passport Number</Label><p className="font-medium">{employee?.passport_number || '-'}</p></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="address" className="mt-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Address Details</h4>
                {addressDocs.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setPreviewDoc(addressDocs[0])}
                    className="text-blue-600"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Address Documents ({addressDocs.length})
                  </Button>
                )}
              </div>
              
              {/* Check-level insufficiencies for address checks - using consistent format */}
              {caseInfo?.checks?.filter(c => c.check_type === 'address_check').map((check) => {
                const checkInsuff = caseDetailData?.check_insufficiencies?.find(i => i.check_id === check.check_id && i.status === 'open');
                if (!checkInsuff) return null;
                return (
                  <Card key={check.check_id} className="mb-4 border-rose-200 bg-rose-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <p className="font-semibold text-rose-700">Insufficiency Raised</p>
                            <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300">
                              {checkInsuff.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-rose-600 mt-2"><strong>Reason:</strong> {checkInsuff.reason}</p>
                          {checkInsuff.description && (
                            <p className="text-sm text-rose-600 mt-1"><strong>Description:</strong> {checkInsuff.description}</p>
                          )}
                          <p className="text-xs text-rose-400 mt-2">
                            Raised on: {checkInsuff.created_at ? formatDateDDMMYYYY(checkInsuff.created_at) : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          onClick={() => handleClearInsufficiency(caseInfo.id, check.check_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Clear Insufficiency
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Address Check Insufficiency History */}
              {renderInsufficiencyHistory('address_check')}
              
              {/* Present Address */}
              {(() => {
                const entryInsuff = caseInfo?.entry_insufficiencies?.find(
                  i => i.entry_type === 'address' && i.entry_index === 0
                );
                const entryApproved = caseInfo?.entry_approvals?.find(
                  a => a.entry_type === 'address' && a.entry_index === 0
                );
                // Support both flat fields and nested present_address object
                const presentAddr = employee?.present_address || {};
                const hasAddress = employee?.present_house_no || employee?.present_area || employee?.present_city || 
                                   presentAddr.complete_address || presentAddr.city;
                
                return (
                  <Card className={`border-l-4 ${entryInsuff ? 'border-l-rose-500 bg-rose-50' : entryApproved ? 'border-l-emerald-500 bg-emerald-50' : 'border-l-purple-500'}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-purple-600" />
                          <span>Present Address</span>
                          {entryInsuff && (
                            <Badge className="bg-rose-100 text-rose-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Insufficiency: {entryInsuff.reason}
                            </Badge>
                          )}
                          {entryApproved && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {hasAddress && !entryApproved && !entryInsuff && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                onClick={() => {
                                  setInsuffData({ 
                                    entry_type: 'address', 
                                    entry_index: 0,
                                    entry_name: 'Present Address',
                                    reason: '', 
                                    description: '' 
                                  });
                                  setShowEntryInsuff(showCaseDetail);
                                }}
                              >
                                <AlertTriangle className="w-4 h-4 mr-1" />
                                Raise Insuff
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleApproveEntry(caseInfo.id, 'address', 0, 'Present Address')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            </>
                          )}
                          {entryInsuff && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-emerald-600 border-emerald-200"
                              onClick={() => handleClearEntryInsufficiency(caseInfo.id, 'address', 0)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Clear Insuff
                            </Button>
                          )}
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {editMode ? (
                        <div className="grid grid-cols-4 gap-3 text-sm">
                          <div className="col-span-2">
                            <Label className="text-xs">House No / Building</Label>
                            <Input 
                              value={editFormData.present_house_no || ''} 
                              onChange={(e) => setEditFormData({...editFormData, present_house_no: e.target.value})}
                              placeholder="House/Building/Flat No."
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Landmark</Label>
                            <Input 
                              value={editFormData.present_landmark || ''} 
                              onChange={(e) => setEditFormData({...editFormData, present_landmark: e.target.value})}
                              placeholder="Landmark"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Area / Locality</Label>
                            <Input 
                              value={editFormData.present_area || ''} 
                              onChange={(e) => setEditFormData({...editFormData, present_area: e.target.value})}
                              placeholder="Area/Locality"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-purple-600">City *</Label>
                            <Input 
                              value={editFormData.present_city || ''} 
                              onChange={(e) => setEditFormData({...editFormData, present_city: e.target.value})}
                              placeholder="City"
                              className="h-8 border-purple-200"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-purple-600">State *</Label>
                            <Input 
                              value={editFormData.present_state || ''} 
                              onChange={(e) => setEditFormData({...editFormData, present_state: e.target.value})}
                              placeholder="State"
                              className="h-8 border-purple-200"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-purple-600">Pincode *</Label>
                            <Input 
                              value={editFormData.present_pincode || ''} 
                              onChange={(e) => setEditFormData({...editFormData, present_pincode: e.target.value})}
                              placeholder="Pincode"
                              className="h-8 border-purple-200"
                              maxLength={6}
                            />
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Address Proof {editMode && <Badge className="ml-1 bg-purple-100 text-purple-700 text-xs">Replace</Badge>}</Label>
                            {presentAddr.address_proof_url || employee?.present_address_proof_url ? (
                              <div className="flex items-center justify-between">
                                <a href={(presentAddr.address_proof_url || employee?.present_address_proof_url).startsWith('http') ? (presentAddr.address_proof_url || employee?.present_address_proof_url) : `${process.env.REACT_APP_BACKEND_URL}${presentAddr.address_proof_url || employee?.present_address_proof_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View Document</a>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'present_address_proof')} disabled={docUploadProgress} />
                                  <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                    <Upload className="w-3 h-3" /> Replace
                                  </span>
                                </label>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-amber-600 text-xs">{presentAddr.address_proof_reason || 'Not uploaded'}</p>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'present_address_proof')} disabled={docUploadProgress} />
                                  <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                    <Upload className="w-3 h-3" /> Upload
                                  </span>
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : hasAddress ? (
                        <div className="grid grid-cols-4 gap-3 text-sm">
                          <div className="bg-slate-50 p-2 rounded col-span-2">
                            <Label className="text-slate-500 text-xs">Complete Address</Label>
                            <p className="font-medium">{presentAddr.complete_address || employee?.present_house_no || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Landmark</Label>
                            <p className="font-medium">{presentAddr.landmark || employee?.present_landmark || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Area / Locality</Label>
                            <p className="font-medium">{employee?.present_area || '-'}</p>
                          </div>
                          <div className="bg-purple-50 p-2 rounded border border-purple-100">
                            <Label className="text-purple-600 text-xs">City</Label>
                            <p className="font-semibold text-purple-800">{presentAddr.city || employee?.present_city || '-'}</p>
                          </div>
                          <div className="bg-purple-50 p-2 rounded border border-purple-100">
                            <Label className="text-purple-600 text-xs">State</Label>
                            <p className="font-semibold text-purple-800">{presentAddr.state || employee?.present_state || '-'}</p>
                          </div>
                          <div className="bg-purple-50 p-2 rounded border border-purple-100">
                            <Label className="text-purple-600 text-xs">Pincode</Label>
                            <p className="font-semibold text-purple-800">{presentAddr.pincode || employee?.present_pincode || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Address Proof</Label>
                            {presentAddr.address_proof_url ? (
                              <a href={presentAddr.address_proof_url.startsWith('http') ? presentAddr.address_proof_url : `${process.env.REACT_APP_BACKEND_URL}${presentAddr.address_proof_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View Document</a>
                            ) : (
                              <p className="font-medium text-amber-600">{presentAddr.address_proof_reason || 'Not uploaded'}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-4">Present address not provided</p>
                      )}
                      {/* Entry-level insufficiency details */}
                      {renderEntryInsufficiencyDetails(entryInsuff)}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Permanent Address */}
              {(() => {
                const entryInsuff = caseInfo?.entry_insufficiencies?.find(
                  i => i.entry_type === 'address' && i.entry_index === 1
                );
                const entryApproved = caseInfo?.entry_approvals?.find(
                  a => a.entry_type === 'address' && a.entry_index === 1
                );
                // Support both flat fields and nested permanent_address object
                const permAddr = employee?.permanent_address || {};
                const hasAddress = employee?.permanent_house_no || employee?.permanent_area || employee?.permanent_city ||
                                   permAddr.complete_address || permAddr.city;
                const isSameAsPresent = employee?.permanent_same_as_present || editFormData?.permanent_same_as_present || false;
                
                return (
                  <Card className={`border-l-4 ${isSameAsPresent ? 'border-l-blue-400 bg-blue-50/50' : entryInsuff ? 'border-l-rose-500 bg-rose-50' : entryApproved ? 'border-l-emerald-500 bg-emerald-50' : 'border-l-orange-500'}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-orange-600" />
                          <span>Permanent Address</span>
                          {isSameAsPresent && (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              Same as Present - Only Present will be processed
                            </Badge>
                          )}
                          {!isSameAsPresent && entryInsuff && (
                            <Badge className="bg-rose-100 text-rose-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Insufficiency: {entryInsuff.reason}
                            </Badge>
                          )}
                          {!isSameAsPresent && entryApproved && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {hasAddress && !entryApproved && !entryInsuff && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                onClick={() => {
                                  setInsuffData({ 
                                    entry_type: 'address', 
                                    entry_index: 1,
                                    entry_name: 'Permanent Address',
                                    reason: '', 
                                    description: '' 
                                  });
                                  setShowEntryInsuff(showCaseDetail);
                                }}
                              >
                                <AlertTriangle className="w-4 h-4 mr-1" />
                                Raise Insuff
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleApproveEntry(caseInfo.id, 'address', 1, 'Permanent Address')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            </>
                          )}
                          {entryInsuff && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-emerald-600 border-emerald-200"
                              onClick={() => handleClearEntryInsufficiency(caseInfo.id, 'address', 1)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Clear Insuff
                            </Button>
                          )}
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Same as Present Address Toggle */}
                      {editMode && (
                        <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <Switch
                            data-testid="same-as-present-switch"
                            checked={editFormData?.permanent_same_as_present || false}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setEditFormData({
                                  ...editFormData,
                                  permanent_same_as_present: true,
                                  permanent_house_no: editFormData.present_house_no || '',
                                  permanent_landmark: editFormData.present_landmark || '',
                                  permanent_area: editFormData.present_area || '',
                                  permanent_city: editFormData.present_city || '',
                                  permanent_state: editFormData.present_state || '',
                                  permanent_pincode: editFormData.present_pincode || '',
                                });
                              } else {
                                setEditFormData({...editFormData, permanent_same_as_present: false});
                              }
                            }}
                          />
                          <Label className="text-sm font-medium text-blue-800 cursor-pointer">
                            Permanent address same as present address
                          </Label>
                          {editFormData?.permanent_same_as_present && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs ml-auto">
                              Only Present Address will be processed
                            </Badge>
                          )}
                        </div>
                      )}
                      {!editMode && isSameAsPresent && (
                        <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded border border-blue-200 text-sm text-blue-700">
                          <CheckCircle className="w-4 h-4 text-blue-500" />
                          Permanent address is same as present. Verification status mirrors present address check.
                        </div>
                      )}
                      {editMode ? (
                        <div className={`grid grid-cols-4 gap-3 text-sm ${editFormData?.permanent_same_as_present ? 'opacity-50 pointer-events-none' : ''}`}>
                          <div className="col-span-2">
                            <Label className="text-xs">House No / Building</Label>
                            <Input 
                              value={editFormData.permanent_house_no || ''} 
                              onChange={(e) => setEditFormData({...editFormData, permanent_house_no: e.target.value})}
                              placeholder="House/Building/Flat No."
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Landmark</Label>
                            <Input 
                              value={editFormData.permanent_landmark || ''} 
                              onChange={(e) => setEditFormData({...editFormData, permanent_landmark: e.target.value})}
                              placeholder="Landmark"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Area / Locality</Label>
                            <Input 
                              value={editFormData.permanent_area || ''} 
                              onChange={(e) => setEditFormData({...editFormData, permanent_area: e.target.value})}
                              placeholder="Area/Locality"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-600">City *</Label>
                            <Input 
                              value={editFormData.permanent_city || ''} 
                              onChange={(e) => setEditFormData({...editFormData, permanent_city: e.target.value})}
                              placeholder="City"
                              className="h-8 border-orange-200"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-600">State *</Label>
                            <Input 
                              value={editFormData.permanent_state || ''} 
                              onChange={(e) => setEditFormData({...editFormData, permanent_state: e.target.value})}
                              placeholder="State"
                              className="h-8 border-orange-200"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-600">Pincode *</Label>
                            <Input 
                              value={editFormData.permanent_pincode || ''} 
                              onChange={(e) => setEditFormData({...editFormData, permanent_pincode: e.target.value})}
                              placeholder="Pincode"
                              className="h-8 border-orange-200"
                              maxLength={6}
                            />
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Address Proof {editMode && <Badge className="ml-1 bg-orange-100 text-orange-700 text-xs">Replace</Badge>}</Label>
                            {permAddr.address_proof_url || employee?.permanent_address_proof_url ? (
                              <div className="flex items-center justify-between">
                                <a href={(permAddr.address_proof_url || employee?.permanent_address_proof_url).startsWith('http') ? (permAddr.address_proof_url || employee?.permanent_address_proof_url) : `${process.env.REACT_APP_BACKEND_URL}${permAddr.address_proof_url || employee?.permanent_address_proof_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View Document</a>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'permanent_address_proof')} disabled={docUploadProgress} />
                                  <span className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1">
                                    <Upload className="w-3 h-3" /> Replace
                                  </span>
                                </label>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-amber-600 text-xs">{permAddr.address_proof_reason || 'Not uploaded'}</p>
                                <label className="cursor-pointer">
                                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'permanent_address_proof')} disabled={docUploadProgress} />
                                  <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                    <Upload className="w-3 h-3" /> Upload
                                  </span>
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : hasAddress ? (
                        <div className="grid grid-cols-4 gap-3 text-sm">
                          <div className="bg-slate-50 p-2 rounded col-span-2">
                            <Label className="text-slate-500 text-xs">Complete Address</Label>
                            <p className="font-medium">{permAddr.complete_address || employee?.permanent_house_no || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Landmark</Label>
                            <p className="font-medium">{permAddr.landmark || employee?.permanent_landmark || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Area / Locality</Label>
                            <p className="font-medium">{employee?.permanent_area || '-'}</p>
                          </div>
                          <div className="bg-orange-50 p-2 rounded border border-orange-100">
                            <Label className="text-orange-600 text-xs">City</Label>
                            <p className="font-semibold text-orange-800">{permAddr.city || employee?.permanent_city || '-'}</p>
                          </div>
                          <div className="bg-orange-50 p-2 rounded border border-orange-100">
                            <Label className="text-orange-600 text-xs">State</Label>
                            <p className="font-semibold text-orange-800">{permAddr.state || employee?.permanent_state || '-'}</p>
                          </div>
                          <div className="bg-orange-50 p-2 rounded border border-orange-100">
                            <Label className="text-orange-600 text-xs">Pincode</Label>
                            <p className="font-semibold text-orange-800">{permAddr.pincode || employee?.permanent_pincode || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Address Proof</Label>
                            {permAddr.address_proof_url ? (
                              <a href={permAddr.address_proof_url.startsWith('http') ? permAddr.address_proof_url : `${process.env.REACT_APP_BACKEND_URL}${permAddr.address_proof_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View Document</a>
                            ) : (
                              <p className="font-medium text-amber-600">{permAddr.address_proof_reason || 'Not uploaded'}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-4">Permanent address not provided</p>
                      )}
                      {/* Entry-level insufficiency details */}
                      {renderEntryInsufficiencyDetails(entryInsuff)}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Additional Addresses */}
              {employee?.additional_addresses?.length > 0 && employee.additional_addresses.map((addr, idx) => {
                const entryInsuff = caseInfo?.entry_insufficiencies?.find(
                  i => i.entry_type === 'address' && i.entry_index === idx + 2
                );
                const entryApproved = caseInfo?.entry_approvals?.find(
                  a => a.entry_type === 'address' && a.entry_index === idx + 2
                );
                
                return (
                  <Card key={idx} className={`border-l-4 ${entryInsuff ? 'border-l-rose-500 bg-rose-50' : entryApproved ? 'border-l-emerald-500 bg-emerald-50' : 'border-l-teal-500'}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-teal-600" />
                          <span>Additional Address {idx + 1}</span>
                          <Badge variant="outline">{addr.address_type || 'Other'}</Badge>
                          {entryInsuff && (
                            <Badge className="bg-rose-100 text-rose-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Insufficiency: {entryInsuff.reason}
                            </Badge>
                          )}
                          {entryApproved && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!entryApproved && !entryInsuff && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                onClick={() => {
                                  setInsuffData({ 
                                    entry_type: 'address', 
                                    entry_index: idx + 2,
                                    entry_name: `Additional Address ${idx + 1} (${addr.address_type || 'Other'})`,
                                    reason: '', 
                                    description: '' 
                                  });
                                  setShowEntryInsuff(showCaseDetail);
                                }}
                              >
                                <AlertTriangle className="w-4 h-4 mr-1" />
                                Raise Insuff
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleApproveEntry(caseInfo.id, 'address', idx + 2, `Additional Address ${idx + 1}`)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            </>
                          )}
                          {entryInsuff && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-emerald-600 border-emerald-200"
                              onClick={() => handleClearEntryInsufficiency(caseInfo.id, 'address', idx + 2)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Clear Insuff
                            </Button>
                          )}
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div className="bg-slate-50 p-2 rounded col-span-2">
                          <Label className="text-slate-500 text-xs">Complete Address</Label>
                          <p className="font-medium">{addr.complete_address || addr.house_no || '-'}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <Label className="text-slate-500 text-xs">Landmark</Label>
                          <p className="font-medium">{addr.landmark || '-'}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <Label className="text-slate-500 text-xs">Address Type</Label>
                          <p className="font-medium capitalize">{addr.address_type || '-'}</p>
                        </div>
                        <div className="bg-teal-50 p-2 rounded border border-teal-100">
                          <Label className="text-teal-600 text-xs">City</Label>
                          <p className="font-semibold text-teal-800">{addr.city || '-'}</p>
                        </div>
                        <div className="bg-teal-50 p-2 rounded border border-teal-100">
                          <Label className="text-teal-600 text-xs">State</Label>
                          <p className="font-semibold text-teal-800">{addr.state || '-'}</p>
                        </div>
                        <div className="bg-teal-50 p-2 rounded border border-teal-100">
                          <Label className="text-teal-600 text-xs">Pincode</Label>
                          <p className="font-semibold text-teal-800">{addr.pincode || '-'}</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <Label className="text-slate-500 text-xs">Duration</Label>
                          <p className="font-medium">{addr.duration_from || '-'} to {addr.duration_to || '-'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="employment" className="mt-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Employment History</h4>
                {employmentDocs.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setPreviewDoc(employmentDocs[0])}
                    className="text-blue-600"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Employment Documents ({employmentDocs.length})
                  </Button>
                )}
              </div>
              
              {/* Employment Check Insufficiency History */}
              {renderInsufficiencyHistory('employment_check')}
              
              {employee?.employment_entries?.length > 0 ? (
                employee.employment_entries.map((emp, idx) => {
                  const entryInsuff = caseInfo?.entry_insufficiencies?.find(
                    i => i.entry_type === 'employment' && i.entry_index === idx
                  );
                  const entryApproved = caseInfo?.entry_approvals?.find(
                    a => a.entry_type === 'employment' && a.entry_index === idx
                  );
                  
                  // Get check name from case checks (package-based name)
                  const empChecks = caseInfo?.checks?.filter(c => c.check_type === 'employment_check') || [];
                  const matchedCheck = empChecks.find(c => c.check_index === idx + 1);
                  const SUB_OPTION_LABELS = {
                    'current': 'Current Employment',
                    'past_1': 'Past Employment 1', 'past_2': 'Past Employment 2', 'past_3': 'Past Employment 3',
                    'past_4': 'Past Employment 4', 'past_5': 'Past Employment 5',
                    'previous': 'Past Employment 1', 'previous_1': 'Past Employment 1',
                    'previous_2': 'Past Employment 2', 'previous_3': 'Past Employment 3',
                  };
                  const checkLabel = matchedCheck 
                    ? (SUB_OPTION_LABELS[matchedCheck.sub_option] || matchedCheck.sub_option_label || matchedCheck.sub_option || `Employment Check ${idx + 1}`)
                    : (emp.employment_type === 'current' || emp.is_current ? 'Current Employment' : `Past Employment ${idx}`);
                  
                  return (
                    <Card key={idx} className={`border-l-4 ${entryInsuff ? 'border-l-rose-500 bg-rose-50' : entryApproved ? 'border-l-emerald-500 bg-emerald-50' : 'border-l-blue-500'}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span>{checkLabel}</span>
                            {entryInsuff && (
                              <Badge className="bg-rose-100 text-rose-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Insufficiency: {entryInsuff.reason}
                              </Badge>
                            )}
                            {entryApproved && (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {!entryApproved && !entryInsuff && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                  onClick={() => {
                                    setInsuffData({ 
                                      entry_type: 'employment', 
                                      entry_index: idx,
                                      entry_name: checkLabel,
                                      reason: '', 
                                      description: '' 
                                    });
                                    setShowEntryInsuff(showCaseDetail);
                                  }}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  Raise Insuff
                                </Button>
                                <Button 
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleApproveEntry(caseInfo.id, 'employment', idx, checkLabel)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              </>
                            )}
                            {entryInsuff && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-emerald-600 border-emerald-200"
                                onClick={() => handleClearEntryInsufficiency(caseInfo.id, 'employment', idx)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Clear Insuff
                              </Button>
                            )}
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {editMode ? (
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            {/* Row 1: Employment Type, Company Name, Employee ID */}
                            <div>
                              <Label className="text-xs">Employment Type</Label>
                              <Select 
                                value={editFormData.employment_entries?.[idx]?.employment_type || emp.employment_type || 'full-time'}
                                onValueChange={(v) => updateEmploymentEntry(idx, 'employment_type', v)}
                              >
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="full-time">Full-time</SelectItem>
                                  <SelectItem value="part-time">Part-time</SelectItem>
                                  <SelectItem value="contract">Contract</SelectItem>
                                  <SelectItem value="internship">Internship</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Company Name *</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.employer_name || editFormData.employment_entries?.[idx]?.company_name || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'company_name', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Employee ID</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.employee_code || editFormData.employment_entries?.[idx]?.employee_id || editFormData.employment_entries?.[idx]?.emp_id || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'employee_id', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            
                            {/* Row 2: Location, Designation, Salary */}
                            <div>
                              <Label className="text-xs">Location / Address</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.location_address || editFormData.employment_entries?.[idx]?.location || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'location_address', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Designation *</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.designation || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'designation', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">CTC / Salary</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.salary_per_month || editFormData.employment_entries?.[idx]?.ctc || editFormData.employment_entries?.[idx]?.salary || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'salary_per_month', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            
                            {/* Row 3: Dates */}
                            <div>
                              <Label className="text-xs text-blue-600">Employed From *</Label>
                              <Input 
                                type="date"
                                value={editFormData.employment_entries?.[idx]?.date_of_joining || editFormData.employment_entries?.[idx]?.employed_from || editFormData.employment_entries?.[idx]?.from_date || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'employed_from', e.target.value)}
                                className="h-8 border-blue-200"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-blue-600">Employed To</Label>
                              <Input 
                                type="date"
                                value={editFormData.employment_entries?.[idx]?.date_of_relieving || editFormData.employment_entries?.[idx]?.employed_to || editFormData.employment_entries?.[idx]?.to_date || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'employed_to', e.target.value)}
                                className="h-8 border-blue-200"
                                placeholder="Leave empty if current"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Reason for Leaving</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.reason_for_leaving || editFormData.employment_entries?.[idx]?.gap_reason || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'reason_for_leaving', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            
                            {/* Row 4: Reporting Manager */}
                            <div>
                              <Label className="text-xs text-amber-600">RM Name</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.rm_name || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'rm_name', e.target.value)}
                                className="h-8 border-amber-200"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-amber-600">RM Email</Label>
                              <Input 
                                type="email"
                                value={editFormData.employment_entries?.[idx]?.rm_email || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'rm_email', e.target.value)}
                                className="h-8 border-amber-200"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-amber-600">RM Phone</Label>
                              <Input 
                                value={editFormData.employment_entries?.[idx]?.rm_contact || editFormData.employment_entries?.[idx]?.rm_contact_number || editFormData.employment_entries?.[idx]?.rm_phone || ''}
                                onChange={(e) => updateEmploymentEntry(idx, 'rm_contact_number', e.target.value)}
                                className="h-8 border-amber-200"
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          {/* Row 1: Employment Type, Company Name, Employee ID */}
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Employment Type</Label>
                            <p className="font-medium capitalize">{emp.employment_type || 'Full-time'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Company Name</Label>
                            <p className="font-medium">{emp.employer_name || emp.company_name || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Employee ID</Label>
                            <p className="font-medium">{emp.employee_code || emp.employee_id || emp.emp_id || '-'}</p>
                          </div>
                          
                          {/* Row 2: Location/Address, Designation, CTC */}
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Location / Address</Label>
                            <p className="font-medium">{emp.location_address || emp.location || emp.office_address || emp.company_address || emp.city || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Designation</Label>
                            <p className="font-medium">{emp.designation || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">CTC / Salary</Label>
                            <p className="font-medium">{emp.salary_per_month || emp.ctc || emp.salary || '-'}</p>
                          </div>
                          
                          {/* Row 3: Employed From, Employed To, Duration */}
                          <div className="bg-blue-50 p-2 rounded border border-blue-100">
                            <Label className="text-blue-600 text-xs">Employed From</Label>
                            <p className="font-semibold text-blue-800">{formatDateDDMonYYYY(emp.date_of_joining || emp.employed_from || emp.from_date || emp.start_date || emp.joining_date) || '-'}</p>
                          </div>
                          <div className="bg-blue-50 p-2 rounded border border-blue-100">
                            <Label className="text-blue-600 text-xs">Employed To</Label>
                            <p className="font-semibold text-blue-800">{(emp.date_of_relieving || emp.employed_to || emp.to_date || emp.end_date || emp.leaving_date) ? formatDateDDMonYYYY(emp.date_of_relieving || emp.employed_to || emp.to_date || emp.end_date || emp.leaving_date) : 'Present'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Reason for Leaving</Label>
                            <p className="font-medium">{emp.gap_reason || emp.reason_for_leaving || emp.leaving_reason || '-'}</p>
                          </div>
                        </div>
                          </>
                        )}
                        
                        {/* Reporting Manager / References Section */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">Reporting Manager / References</Label>
                          <div className="grid grid-cols-2 gap-4">
                            {/* Reference 1 */}
                            <div className="bg-amber-50 p-3 rounded border border-amber-100">
                              <p className="text-xs text-amber-600 uppercase mb-1">Reference 1 (Reporting Manager)</p>
                              <div className="space-y-1 text-sm">
                                <p><span className="text-slate-500">Name:</span> <span className="font-medium">{emp.rm_name || emp.reporting_manager || emp.reporting_manager_name || '-'}</span></p>
                                <p><span className="text-slate-500">Designation:</span> <span className="font-medium">{emp.rm_designation || emp.reporting_manager_designation || '-'}</span></p>
                                <p><span className="text-slate-500">Email:</span> <span className="font-medium">{emp.rm_email || emp.reporting_manager_email || '-'}</span></p>
                                <p><span className="text-slate-500">Phone:</span> <span className="font-medium">{emp.rm_contact_number || emp.rm_phone || emp.rm_contact || emp.reporting_manager_phone || '-'}</span></p>
                              </div>
                            </div>
                            
                            {/* Reference 2 */}
                            <div className="bg-amber-50 p-3 rounded border border-amber-100">
                              <p className="text-xs text-amber-600 uppercase mb-1">Reference 2</p>
                              <div className="space-y-1 text-sm">
                                <p><span className="text-slate-500">Name:</span> <span className="font-medium">{emp.hr_name || emp.second_reference_name || emp.ref2_name || '-'}</span></p>
                                <p><span className="text-slate-500">Designation:</span> <span className="font-medium">{emp.hr_designation || emp.second_reference_designation || emp.ref2_designation || '-'}</span></p>
                                <p><span className="text-slate-500">Email:</span> <span className="font-medium">{emp.hr_email || emp.second_reference_email || emp.ref2_email || '-'}</span></p>
                                <p><span className="text-slate-500">Phone:</span> <span className="font-medium">{emp.hr_contact_number || emp.hr_contact || emp.hr_phone || emp.second_reference_phone || emp.ref2_phone || '-'}</span></p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* HR Details - Third Row */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">
                            HR Details {editMode && <Badge className="ml-2 bg-purple-100 text-purple-700">Editable</Badge>}
                          </Label>
                          {editMode ? (
                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <Label className="text-xs text-purple-600">HR Name</Label>
                                <Input
                                  value={editFormData.employment_entries?.[idx]?.hr_name || ''}
                                  onChange={(e) => updateEmploymentEntry(idx, 'hr_name', e.target.value)}
                                  className="h-8"
                                  placeholder="HR Name"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-purple-600">HR Designation</Label>
                                <Input
                                  value={editFormData.employment_entries?.[idx]?.hr_designation || ''}
                                  onChange={(e) => updateEmploymentEntry(idx, 'hr_designation', e.target.value)}
                                  className="h-8"
                                  placeholder="HR Designation"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-purple-600">HR Email</Label>
                                <Input
                                  type="email"
                                  value={editFormData.employment_entries?.[idx]?.hr_email || ''}
                                  onChange={(e) => updateEmploymentEntry(idx, 'hr_email', e.target.value)}
                                  className="h-8"
                                  placeholder="hr@company.com"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-purple-600">HR Contact Number</Label>
                                <Input
                                  value={editFormData.employment_entries?.[idx]?.hr_contact_number || ''}
                                  onChange={(e) => updateEmploymentEntry(idx, 'hr_contact_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                  className="h-8"
                                  placeholder="10-digit phone"
                                  maxLength={10}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-3">
                              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                                <p className="text-xs text-purple-600 uppercase mb-1">HR Name</p>
                                <p className="font-medium text-sm">{emp.hr_name || '-'}</p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                                <p className="text-xs text-purple-600 uppercase mb-1">HR Designation</p>
                                <p className="font-medium text-sm">{emp.hr_designation || '-'}</p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                                <p className="text-xs text-purple-600 uppercase mb-1">HR Email</p>
                                <p className="font-medium text-sm">{emp.hr_email || '-'}</p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                                <p className="text-xs text-purple-600 uppercase mb-1">HR Contact Number</p>
                                <p className="font-medium text-sm">{emp.hr_contact_number || '-'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Employment Documents Section */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">
                            Employment Documents {editMode && <Badge className="ml-2 bg-purple-100 text-purple-700">Click to Replace</Badge>}
                          </Label>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-blue-50 p-3 rounded border border-blue-100">
                              <p className="text-xs text-blue-600 uppercase mb-1">Relieving Letter</p>
                              {(emp.relieving_letter_url || emp.relieving_letter_file) ? (
                                <div className="flex items-center justify-between">
                                  {emp.relieving_letter_url ? (
                                    <a href={emp.relieving_letter_url.startsWith('http') ? emp.relieving_letter_url : `${process.env.REACT_APP_BACKEND_URL}${emp.relieving_letter_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline text-sm flex items-center gap-1">
                                      <FileText className="w-4 h-4" /> View Document
                                    </a>
                                  ) : (
                                    <p className="text-emerald-600 text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> {emp.relieving_letter_file}</p>
                                  )}
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'relieving_letter', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Replace
                                      </span>
                                    </label>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <p className="text-amber-600 text-sm">{emp.relieving_letter_reason || 'Not uploaded'}</p>
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'relieving_letter', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Upload
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="bg-blue-50 p-3 rounded border border-blue-100">
                              <p className="text-xs text-blue-600 uppercase mb-1">Experience Letter</p>
                              {(emp.experience_letter_url || emp.experience_letter_file) ? (
                                <div className="flex items-center justify-between">
                                  {emp.experience_letter_url ? (
                                    <a href={emp.experience_letter_url.startsWith('http') ? emp.experience_letter_url : `${process.env.REACT_APP_BACKEND_URL}${emp.experience_letter_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline text-sm flex items-center gap-1">
                                      <FileText className="w-4 h-4" /> View Document
                                    </a>
                                  ) : (
                                    <p className="text-emerald-600 text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> {emp.experience_letter_file}</p>
                                  )}
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'experience_letter', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Replace
                                      </span>
                                    </label>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <p className="text-amber-600 text-sm">{emp.experience_letter_reason || 'Not uploaded'}</p>
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'experience_letter', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Upload
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="bg-blue-50 p-3 rounded border border-blue-100">
                              <p className="text-xs text-blue-600 uppercase mb-1">Latest Payslip</p>
                              {(emp.latest_payslip_url || emp.payslip_file) ? (
                                <div className="flex items-center justify-between">
                                  {emp.latest_payslip_url ? (
                                    <a href={emp.latest_payslip_url.startsWith('http') ? emp.latest_payslip_url : `${process.env.REACT_APP_BACKEND_URL}${emp.latest_payslip_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline text-sm flex items-center gap-1">
                                      <FileText className="w-4 h-4" /> View Document
                                    </a>
                                  ) : (
                                    <p className="text-emerald-600 text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> {emp.payslip_file}</p>
                                  )}
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'latest_payslip', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Replace
                                      </span>
                                    </label>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <p className="text-amber-600 text-sm">{emp.latest_payslip_reason || 'Not uploaded'}</p>
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'latest_payslip', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Upload
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Entry-level insufficiency details */}
                        {renderEntryInsufficiencyDetails(entryInsuff)}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Card><CardContent className="py-8 text-center text-slate-400">No employment history provided</CardContent></Card>
              )}
              {/* Declared-short banner cards: one per N/A-by-declaration check slot */}
              {(() => {
                const declaredChecks = (caseInfo?.checks || []).filter(c => {
                  const t = typeof c.check_type === 'object' ? c.check_type?.check_type : c.check_type;
                  return t === 'employment_check' && c.status === 'not_applicable_by_declaration';
                });
                if (declaredChecks.length === 0) return null;
                const filledCount = employee?.employment_entries?.length || 0;
                const totalRequired = (caseInfo?.checks || []).filter(c => {
                  const t = typeof c.check_type === 'object' ? c.check_type?.check_type : c.check_type;
                  return t === 'employment_check';
                }).length;
                const canReinstate = ['pending_qc', 'basic_initiated'].includes(caseInfo?.status);
                return declaredChecks.map((ch, i) => (
                  <Card key={`emp-decl-${ch.check_id}`} className="border-l-4 border-l-amber-500 bg-amber-50" data-testid={`employment-declared-short-${ch.check_id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span>Employment {filledCount + i + 1}</span>
                          <Badge className="bg-amber-100 text-amber-800">N/A by Declaration</Badge>
                        </div>
                        {canReinstate && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-700 hover:bg-rose-50"
                            onClick={async () => {
                              if (!window.confirm('Reinstate this check? An insufficiency will be raised asking the candidate to provide the missing employment record.')) return;
                              try {
                                const res = await axios.put(`/cases/${caseInfo.id}/checks/${ch.check_id}/reinstate`);
                                toast.success(res.data?.message || 'Check reinstated');
                                fetchData();
                                loadData();
                              } catch (e) {
                                toast.error(getErrorMessage(e, 'Failed to reinstate'));
                              }
                            }}
                            data-testid={`reinstate-check-${ch.check_id}`}
                          >
                            Reinstate Check
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-amber-800">
                        Candidate did not update the required employment details as per package.
                        Proceeding with <strong>{filledCount} of {totalRequired}</strong> employment record(s) per candidate declaration.
                      </p>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          </TabsContent>

          <TabsContent value="education" className="mt-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Education Details</h4>
                {educationDocs.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setPreviewDoc(educationDocs[0])}
                    className="text-blue-600"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Education Documents ({educationDocs.length})
                  </Button>
                )}
              </div>
              
              {/* Check-level insufficiencies for education checks - using consistent format */}
              {caseInfo?.checks?.filter(c => c.check_type === 'education_check').map((check) => {
                const checkInsuff = caseDetailData?.check_insufficiencies?.find(i => i.check_id === check.check_id && i.status === 'open');
                if (!checkInsuff) return null;
                return (
                  <Card key={check.check_id} className="mb-4 border-rose-200 bg-rose-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <p className="font-semibold text-rose-700">Insufficiency Raised</p>
                            <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300">
                              {checkInsuff.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-rose-600 mt-2"><strong>Reason:</strong> {checkInsuff.reason}</p>
                          {checkInsuff.description && (
                            <p className="text-sm text-rose-600 mt-1"><strong>Description:</strong> {checkInsuff.description}</p>
                          )}
                          <p className="text-xs text-rose-400 mt-2">
                            Raised on: {checkInsuff.created_at ? formatDateDDMMYYYY(checkInsuff.created_at) : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          onClick={() => handleClearInsufficiency(caseInfo.id, check.check_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Clear Insufficiency
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Education Check Insufficiency History */}
              {renderInsufficiencyHistory('education_check')}
              
              {employee?.education_entries?.length > 0 ? (
                employee.education_entries.map((edu, idx) => {
                  const entryInsuff = caseInfo?.entry_insufficiencies?.find(
                    i => i.entry_type === 'education' && i.entry_index === idx
                  );
                  const entryApproved = caseInfo?.entry_approvals?.find(
                    a => a.entry_type === 'education' && a.entry_index === idx
                  );
                  // Get check name from case checks (package-based name)
                  const eduChecks = caseInfo?.checks?.filter(c => c.check_type === 'education_check') || [];
                  const matchedEduCheck = eduChecks.find(c => c.check_index === idx + 1);
                  const EDU_SUB_OPTION_LABELS = {
                    'highest': 'Highest Qualification',
                    'prior_ssc': 'Prior to Highest - SSC', 'ssc': 'Prior to Highest - SSC',
                    'prior_hsc': 'Prior to Highest - HSC', 'hsc': 'Prior to Highest - HSC',
                    'iti': 'ITI', 'diploma': 'Diploma', 'professional': 'Professional Course',
                    'prior_1': 'Prior Education 1', 'prior_2': 'Prior Education 2',
                    'graduation': 'Graduation', 'post_graduation': 'Post Graduation',
                  };
                  const eduCheckLabel = matchedEduCheck 
                    ? (EDU_SUB_OPTION_LABELS[matchedEduCheck.sub_option] || matchedEduCheck.sub_option_label || matchedEduCheck.sub_option || `Education Check ${idx + 1}`)
                    : (idx === 0 ? 'Highest Qualification' : `Prior Education ${idx}`);
                  
                  return (
                    <Card key={idx} className={`border-l-4 ${entryInsuff ? 'border-l-rose-500 bg-rose-50' : entryApproved ? 'border-l-emerald-500 bg-emerald-50' : 'border-l-green-500'}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span>{eduCheckLabel}</span>
                            {entryInsuff && (
                              <Badge className="bg-rose-100 text-rose-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Insufficiency: {entryInsuff.reason}
                              </Badge>
                            )}
                            {entryApproved && (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {!entryApproved && !entryInsuff && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                  onClick={() => {
                                    setInsuffData({ 
                                      entry_type: 'education', 
                                      entry_index: idx,
                                      entry_name: eduCheckLabel,
                                      reason: '', 
                                      description: '' 
                                    });
                                    setShowEntryInsuff(showCaseDetail);
                                  }}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  Raise Insuff
                                </Button>
                                <Button 
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleApproveEntry(caseInfo.id, 'education', idx, eduCheckLabel)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              </>
                            )}
                            {entryInsuff && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-emerald-600 border-emerald-200"
                                onClick={() => handleClearEntryInsufficiency(caseInfo.id, 'education', idx)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Clear Insuff
                              </Button>
                            )}
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {editMode ? (
                          <div className="grid grid-cols-4 gap-3 text-sm">
                            {/* Row 1 */}
                            <div>
                              <Label className="text-xs">Education Type</Label>
                              <Select
                                value={editFormData.education_entries?.[idx]?.education_type || edu.education_type || 'graduation'}
                                onValueChange={(v) => updateEducationEntry(idx, 'education_type', v)}
                              >
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="10th">10th</SelectItem>
                                  <SelectItem value="12th">12th</SelectItem>
                                  <SelectItem value="diploma">Diploma</SelectItem>
                                  <SelectItem value="graduation">Graduation</SelectItem>
                                  <SelectItem value="post_graduation">Post Graduation</SelectItem>
                                  <SelectItem value="phd">PhD</SelectItem>
                                  <SelectItem value="certification">Certification</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">University Name *</Label>
                              <Input
                                value={editFormData.education_entries?.[idx]?.university_name || ''}
                                onChange={(e) => updateEducationEntry(idx, 'university_name', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">College / Institution</Label>
                              <Input
                                value={editFormData.education_entries?.[idx]?.college || editFormData.education_entries?.[idx]?.institution_name || ''}
                                onChange={(e) => updateEducationEntry(idx, 'college', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Course / Degree *</Label>
                              <Input
                                value={editFormData.education_entries?.[idx]?.course || ''}
                                onChange={(e) => updateEducationEntry(idx, 'course', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            
                            {/* Row 2 */}
                            <div>
                              <Label className="text-xs">Specialization</Label>
                              <Input
                                value={editFormData.education_entries?.[idx]?.specialization || ''}
                                onChange={(e) => updateEducationEntry(idx, 'specialization', e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-green-600">Year of Passing *</Label>
                              <Input
                                type="number"
                                min="1950"
                                max="2030"
                                value={editFormData.education_entries?.[idx]?.year_of_passing || ''}
                                onChange={(e) => updateEducationEntry(idx, 'year_of_passing', e.target.value)}
                                className="h-8 border-green-200"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-green-600">Roll No / Regd No</Label>
                              <Input
                                value={editFormData.education_entries?.[idx]?.roll_no || editFormData.education_entries?.[idx]?.registration_number || ''}
                                onChange={(e) => updateEducationEntry(idx, 'roll_no', e.target.value)}
                                className="h-8 border-green-200"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Percentage / CGPA</Label>
                              <Input
                                value={editFormData.education_entries?.[idx]?.percentage || editFormData.education_entries?.[idx]?.cgpa || ''}
                                onChange={(e) => updateEducationEntry(idx, 'percentage', e.target.value)}
                                className="h-8"
                              />
                            </div>
                          </div>
                        ) : (
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          {/* Row 1: Education Type, Name as Per Document, University Name, College */}
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Education Type</Label>
                            <p className="font-medium">{edu.education_type || edu.qualification_type || edu.qualification || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Name as Per Document</Label>
                            <p className="font-medium">{edu.name_as_per_document || edu.name_as_per_doc || edu.candidate_name || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">University Name</Label>
                            <p className="font-medium">{edu.university_name || edu.university || edu.board || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">College / Institution</Label>
                            <p className="font-medium">{edu.college || edu.institution_name || edu.school || '-'}</p>
                          </div>
                          
                          {/* Row 2: Course, Specialization, Year of Passing, Roll No */}
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Course</Label>
                            <p className="font-medium">{edu.course || edu.degree || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Specialization</Label>
                            <p className="font-medium">{edu.specialization || edu.branch || edu.stream || '-'}</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded border border-green-100">
                            <Label className="text-green-600 text-xs">Year of Passing</Label>
                            <p className="font-semibold text-green-800">{edu.year_of_passing || edu.passing_year || '-'}</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded border border-green-100">
                            <Label className="text-green-600 text-xs">Roll No / Regd No</Label>
                            <p className="font-semibold text-green-800">{edu.roll_no || edu.roll_number || edu.registration_number || edu.regd_no || '-'}</p>
                          </div>
                          
                          {/* Row 3: Additional fields */}
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Percentage / CGPA</Label>
                            <p className="font-medium">{edu.percentage || edu.cgpa || edu.marks || edu.class_degree || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Grade / Division</Label>
                            <p className="font-medium">{edu.grade || edu.division || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Medium of Instruction</Label>
                            <p className="font-medium">{edu.medium || edu.medium_of_instruction || '-'}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <Label className="text-slate-500 text-xs">Duration</Label>
                            <p className="font-medium">{edu.start_year || edu.from_year || '-'} - {edu.end_year || edu.to_year || edu.year_of_passing || '-'}</p>
                          </div>
                        </div>
                        )}
                        
                        {/* Education Documents Section */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">
                            Education Documents {editMode && <Badge className="ml-2 bg-purple-100 text-purple-700">Click to Replace</Badge>}
                          </Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-green-50 p-3 rounded border border-green-100">
                              <p className="text-xs text-green-600 uppercase mb-1">Degree Certificate</p>
                              {(edu.degree_certificate_url || edu.certificate_file) ? (
                                <div className="flex items-center justify-between">
                                  {edu.degree_certificate_url ? (
                                    <a href={edu.degree_certificate_url.startsWith('http') ? edu.degree_certificate_url : `${process.env.REACT_APP_BACKEND_URL}${edu.degree_certificate_url}`} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline text-sm flex items-center gap-1">
                                      <FileText className="w-4 h-4" /> View Document
                                    </a>
                                  ) : (
                                    <p className="text-emerald-600 text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> {edu.certificate_file}</p>
                                  )}
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'degree_certificate', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Replace
                                      </span>
                                    </label>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <p className="text-amber-600 text-sm">{edu.degree_certificate_reason || 'Not uploaded'}</p>
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'degree_certificate', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Upload
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="bg-green-50 p-3 rounded border border-green-100">
                              <p className="text-xs text-green-600 uppercase mb-1">Marks Card / Transcript</p>
                              {(edu.marks_card_url || edu.marksheet_file) ? (
                                <div className="flex items-center justify-between">
                                  {edu.marks_card_url ? (
                                    <a href={edu.marks_card_url.startsWith('http') ? edu.marks_card_url : `${process.env.REACT_APP_BACKEND_URL}${edu.marks_card_url}`} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline text-sm flex items-center gap-1">
                                      <FileText className="w-4 h-4" /> View Document
                                    </a>
                                  ) : (
                                    <p className="text-emerald-600 text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> {edu.marksheet_file}</p>
                                  )}
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'marks_card', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Replace
                                      </span>
                                    </label>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <p className="text-amber-600 text-sm">{edu.marks_card_reason || 'Not uploaded'}</p>
                                  {editMode && (
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'marks_card', idx)} disabled={docUploadProgress} />
                                      <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                        <Upload className="w-3 h-3" /> Upload
                                      </span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Entry-level insufficiency details */}
                        {renderEntryInsufficiencyDetails(entryInsuff)}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Card><CardContent className="py-8 text-center text-slate-400">No education details provided</CardContent></Card>
              )}
              {/* Declared-short banner cards for Education */}
              {(() => {
                const declaredChecks = (caseInfo?.checks || []).filter(c => {
                  const t = typeof c.check_type === 'object' ? c.check_type?.check_type : c.check_type;
                  return t === 'education_check' && c.status === 'not_applicable_by_declaration';
                });
                if (declaredChecks.length === 0) return null;
                const filledCount = employee?.education_entries?.length || 0;
                const totalRequired = (caseInfo?.checks || []).filter(c => {
                  const t = typeof c.check_type === 'object' ? c.check_type?.check_type : c.check_type;
                  return t === 'education_check';
                }).length;
                const canReinstate = ['pending_qc', 'basic_initiated'].includes(caseInfo?.status);
                return declaredChecks.map((ch, i) => (
                  <Card key={`edu-decl-${ch.check_id}`} className="border-l-4 border-l-amber-500 bg-amber-50" data-testid={`education-declared-short-${ch.check_id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span>Prior Education {i + 1}</span>
                          <Badge className="bg-amber-100 text-amber-800">N/A by Declaration</Badge>
                        </div>
                        {canReinstate && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-700 hover:bg-rose-50"
                            onClick={async () => {
                              if (!window.confirm('Reinstate this check? An insufficiency will be raised asking the candidate to provide the missing education record.')) return;
                              try {
                                const res = await axios.put(`/cases/${caseInfo.id}/checks/${ch.check_id}/reinstate`);
                                toast.success(res.data?.message || 'Check reinstated');
                                fetchData();
                                loadData();
                              } catch (e) {
                                toast.error(getErrorMessage(e, 'Failed to reinstate'));
                              }
                            }}
                            data-testid={`reinstate-check-${ch.check_id}`}
                          >
                            Reinstate Check
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-amber-800">
                        Candidate did not update the required education details as per package.
                        Proceeding with <strong>{filledCount} of {totalRequired}</strong> education record(s) per candidate declaration.
                      </p>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          </TabsContent>

          {/* ID Check Tab (PAN Card / Aadhar Card) */}
          {getCheckByType('id_check') && (
            <TabsContent value="id_check" className="mt-4">
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      ID Verification (PAN Card / Aadhar Card)
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('id_check')?.status)}
                      <Badge className="bg-blue-100 text-blue-700">#{getCheckByType('id_check')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-blue-700">Copied from: Personal Details</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for Verification</h4>
                    {editMode ? (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs">Full Name</Label>
                          <Input
                            value={editFormData.name || ''}
                            onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Date of Birth</Label>
                          <Input
                            type="date"
                            value={editFormData.dob || ''}
                            onChange={(e) => setEditFormData({...editFormData, dob: e.target.value})}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Father's Name</Label>
                          <Input
                            value={editFormData.father_name || ''}
                            onChange={(e) => setEditFormData({...editFormData, father_name: e.target.value})}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-yellow-600">PAN Number *</Label>
                          <Input
                            value={editFormData.pan_number || ''}
                            onChange={(e) => setEditFormData({...editFormData, pan_number: e.target.value.toUpperCase()})}
                            className="h-8 border-yellow-200"
                            maxLength={10}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-yellow-600">Aadhar Number *</Label>
                          <Input
                            value={editFormData.aadhar_number || ''}
                            onChange={(e) => setEditFormData({...editFormData, aadhar_number: e.target.value.replace(/\D/g, '')})}
                            className="h-8 border-yellow-200"
                            maxLength={12}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Gender</Label>
                          <Select
                            value={editFormData.gender || ''}
                            onValueChange={(v) => setEditFormData({...editFormData, gender: v})}
                          >
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Passport Number</Label>
                          <Input
                            value={editFormData.passport_number || ''}
                            onChange={(e) => setEditFormData({...editFormData, passport_number: e.target.value.toUpperCase()})}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Driving License No</Label>
                          <Input
                            value={editFormData.driving_license || editFormData.dl_number || ''}
                            onChange={(e) => setEditFormData({...editFormData, driving_license: e.target.value.toUpperCase()})}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Voter ID</Label>
                          <Input
                            value={editFormData.voter_id || ''}
                            onChange={(e) => setEditFormData({...editFormData, voter_id: e.target.value.toUpperCase()})}
                            className="h-8"
                          />
                        </div>
                      </div>
                    ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-600 uppercase">PAN Number</p>
                        <p className="font-bold text-lg">{employee?.pan_number || '-'}</p>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-600 uppercase">Aadhar Number</p>
                        <p className="font-bold text-lg">{employee?.aadhar_number || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Gender</p>
                        <p className="font-semibold">{employee?.gender || '-'}</p>
                      </div>
                    </div>
                    )}
                  </div>
                  
                  {/* ID Documents - Direct URLs */}
                  <div className="mt-4 p-4 bg-white rounded-lg border border-blue-100">
                    <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center">ID Documents {editMode && <Badge className="ml-2 bg-purple-100 text-purple-700">Click to Replace</Badge>}</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-600 uppercase mb-1">PAN Card</p>
                        {employee?.pan_card_url ? (
                          <div className="flex items-center justify-between">
                            <a href={employee.pan_card_url.startsWith('http') ? employee.pan_card_url : `${process.env.REACT_APP_BACKEND_URL}${employee.pan_card_url}`} target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:underline text-sm flex items-center gap-1">
                              <FileText className="w-4 h-4" /> View PAN Card
                            </a>
                            {editMode && (
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'pan_card')} disabled={docUploadProgress} />
                                <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                  <Upload className="w-3 h-3" /> Replace
                                </span>
                              </label>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <p className="text-amber-600 text-sm">Not uploaded</p>
                            {editMode && (
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'pan_card')} disabled={docUploadProgress} />
                                <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                  <Upload className="w-3 h-3" /> Upload
                                </span>
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-600 uppercase mb-1">Aadhar Card</p>
                        {employee?.aadhar_card_url ? (
                          <div className="flex items-center justify-between">
                            <a href={employee.aadhar_card_url.startsWith('http') ? employee.aadhar_card_url : `${process.env.REACT_APP_BACKEND_URL}${employee.aadhar_card_url}`} target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:underline text-sm flex items-center gap-1">
                              <FileText className="w-4 h-4" /> View Aadhar Card
                            </a>
                            {editMode && (
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'aadhar_card')} disabled={docUploadProgress} />
                                <span className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                  <Upload className="w-3 h-3" /> Replace
                                </span>
                              </label>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <p className="text-amber-600 text-sm">Not uploaded</p>
                            {editMode && (
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replaceDocument(e.target.files[0], 'aadhar_card')} disabled={docUploadProgress} />
                                <span className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                                  <Upload className="w-3 h-3" /> Upload
                                </span>
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Legacy Documents support */}
                  {idDocs.length > 0 && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100">
                      <p className="text-xs text-slate-500 uppercase mb-2">Additional Uploaded Documents</p>
                      <div className="grid grid-cols-2 gap-2">
                        {idDocs.map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm font-medium">{doc.document_type || doc.original_filename}</span>
                            <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(doc)}><Eye className="w-4 h-4" /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('id_check')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('id_check')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('id_check') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-rose-600" onClick={() => { setInsuffData({ check_id: getCheckByType('id_check')?.check_id, check_type: 'id_check', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('id_check')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* World Database Check Tab */}
          {getCheckByType('world_criminal_db') && (
            <TabsContent value="world_db" className="mt-4">
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="w-5 h-5 text-purple-600" />
                      World Database Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('world_criminal_db')?.status)}
                      <Badge className="bg-purple-100 text-purple-700">#{getCheckByType('world_criminal_db')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-purple-700">Copied from: Personal Details (Name, DOB, Father Name)</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for Verification</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-purple-50 p-3 rounded border border-purple-100">
                        <p className="text-xs text-purple-600 uppercase">Full Name</p>
                        <p className="font-bold text-lg">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded border border-purple-100">
                        <p className="text-xs text-purple-600 uppercase">Date of Birth</p>
                        <p className="font-bold text-lg">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded border border-purple-100">
                        <p className="text-xs text-purple-600 uppercase">Father&apos;s Name</p>
                        <p className="font-bold text-lg">{employee?.father_name || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('world_criminal_db')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('world_criminal_db')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('world_criminal_db') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-rose-600" onClick={() => { setInsuffData({ check_id: getCheckByType('world_criminal_db')?.check_id, check_type: 'world_criminal_db', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('world_criminal_db')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Credit Check Tab */}
          {getCheckByType('credit_check') && (
            <TabsContent value="credit_check" className="mt-4">
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                      Credit Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('credit_check')?.status)}
                      <Badge className="bg-emerald-100 text-emerald-700">#{getCheckByType('credit_check')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-emerald-700">Copied from: Personal Details + PAN Card</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for Verification</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-emerald-100 p-3 rounded border border-emerald-200">
                        <p className="text-xs text-emerald-600 uppercase">PAN Number</p>
                        <p className="font-bold text-lg">{employee?.pan_number || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded col-span-2">
                        <p className="text-xs text-slate-500 uppercase">Present Address</p>
                        <p className="font-medium">{[employee?.present_house_no, employee?.present_area, employee?.present_city, employee?.present_state, employee?.present_pincode].filter(Boolean).join(', ') || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('credit_check')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('credit_check')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('credit_check') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-rose-600" onClick={() => { setInsuffData({ check_id: getCheckByType('credit_check')?.check_id, check_type: 'credit_check', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('credit_check')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Criminal Court Check Tab */}
          {getCheckByType('criminal_court_check') && (
            <TabsContent value="criminal_court" className="mt-4">
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Scale className="w-5 h-5 text-amber-600" />
                      Criminal Court Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('criminal_court_check')?.status)}
                      <Badge className="bg-amber-100 text-amber-700">#{getCheckByType('criminal_court_check')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-amber-700">Copied from: Personal Details + All Addresses</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-amber-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for Verification</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-amber-800 mb-2">Addresses for Court Verification</h4>
                    <div className="space-y-2">
                      <div className="bg-amber-100 p-3 rounded border border-amber-200">
                        <p className="text-xs text-amber-600 uppercase">Present Address</p>
                        <p className="font-medium">{formatAddress(employee, 'present')}</p>
                      </div>
                      <div className="bg-amber-100 p-3 rounded border border-amber-200">
                        <p className="text-xs text-amber-600 uppercase">Permanent Address</p>
                        <p className="font-medium">{formatAddress(employee, 'permanent')}</p>
                      </div>
                      {employee?.additional_addresses?.map((addr, idx) => (
                        <div key={idx} className="bg-amber-100 p-3 rounded border border-amber-200">
                          <p className="text-xs text-amber-600 uppercase">Additional Address {idx + 1} ({addr.address_type})</p>
                          <p className="font-medium">{[addr.complete_address || addr.house_no, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('criminal_court_check')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('criminal_court_check')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('criminal_court_check') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-rose-600" onClick={() => { setInsuffData({ check_id: getCheckByType('criminal_court_check')?.check_id, check_type: 'criminal_court_check', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('criminal_court_check')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Criminal Advocate Check Tab */}
          {(getCheckByType('court_advocate_check') || getCheckByType('criminal_advocate_check')) && (
            <TabsContent value="advocate_check" className="mt-4">
              <Card className="border-rose-200 bg-rose-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Scale className="w-5 h-5 text-rose-600" />
                      Criminal Advocate Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge((getCheckByType('court_advocate_check') || getCheckByType('criminal_advocate_check'))?.status)}
                      <Badge className="bg-rose-100 text-rose-700">#{(getCheckByType('court_advocate_check') || getCheckByType('criminal_advocate_check'))?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-rose-700">Copied from: Personal Details + All Addresses</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-rose-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for Verification</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-rose-800 mb-2">Addresses for Advocate Verification</h4>
                    <div className="space-y-2">
                      {employee?.present_address?.complete_address ? (
                        <div className="bg-rose-100 p-3 rounded border border-rose-200">
                          <p className="text-xs text-rose-600 uppercase">Present Address</p>
                          <p className="font-medium">{employee.present_address.complete_address}</p>
                          <p className="text-sm text-slate-600">{[employee.present_address.city, employee.present_address.state, employee.present_address.pincode].filter(Boolean).join(', ')}</p>
                        </div>
                      ) : (
                        <div className="bg-rose-100 p-3 rounded border border-rose-200">
                          <p className="text-xs text-rose-600 uppercase">Present Address</p>
                          <p className="font-medium">{[employee?.present_house_no, employee?.present_area, employee?.present_city, employee?.present_state, employee?.present_pincode].filter(Boolean).join(', ') || 'Not provided'}</p>
                        </div>
                      )}
                      {employee?.permanent_address?.complete_address ? (
                        <div className="bg-rose-100 p-3 rounded border border-rose-200">
                          <p className="text-xs text-rose-600 uppercase">Permanent Address</p>
                          <p className="font-medium">{employee.permanent_address.complete_address}</p>
                          <p className="text-sm text-slate-600">{[employee.permanent_address.city, employee.permanent_address.state, employee.permanent_address.pincode].filter(Boolean).join(', ')}</p>
                        </div>
                      ) : (
                        <div className="bg-rose-100 p-3 rounded border border-rose-200">
                          <p className="text-xs text-rose-600 uppercase">Permanent Address</p>
                          <p className="font-medium">{[employee?.permanent_house_no, employee?.permanent_area, employee?.permanent_city, employee?.permanent_state, employee?.permanent_pincode].filter(Boolean).join(', ') || 'Not provided'}</p>
                        </div>
                      )}
                      {employee?.additional_addresses?.map((addr, idx) => (
                        <div key={idx} className="bg-rose-100 p-3 rounded border border-rose-200">
                          <p className="text-xs text-rose-600 uppercase">Additional Address {idx + 1} ({addr.address_type})</p>
                          <p className="font-medium">{addr.complete_address || [addr.house_no, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert(getCheckByType('criminal_advocate_check') ? 'criminal_advocate_check' : 'court_advocate_check')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory(getCheckByType('criminal_advocate_check') ? 'criminal_advocate_check' : 'court_advocate_check')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency(getCheckByType('criminal_advocate_check') ? 'criminal_advocate_check' : 'court_advocate_check') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-rose-600" onClick={() => { 
                        const checkType = getCheckByType('criminal_advocate_check') ? 'criminal_advocate_check' : 'court_advocate_check';
                        setInsuffData({ check_id: (getCheckByType('court_advocate_check') || getCheckByType('criminal_advocate_check'))?.check_id, check_type: checkType, reason: '', description: '' }); 
                        setShowRaiseInsuff(showCaseDetail); 
                      }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, (getCheckByType('court_advocate_check') || getCheckByType('criminal_advocate_check'))?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* India Criminal Database Check Tab */}
          {getCheckByType('india_criminal_db') && (
            <TabsContent value="india_db" className="mt-4">
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="w-5 h-5 text-orange-600" />
                      India Criminal Database Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('india_criminal_db')?.status)}
                      <Badge className="bg-orange-100 text-orange-700">#{getCheckByType('india_criminal_db')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-orange-700">Copied from: Personal Details</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-orange-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for India DB Verification</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-600 uppercase">PAN Number</p>
                        <p className="font-bold">{employee?.pan_number || '-'}</p>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-600 uppercase">Aadhar Number</p>
                        <p className="font-bold">{employee?.aadhar_number || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Gender</p>
                        <p className="font-semibold">{employee?.gender || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('india_criminal_db')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('india_criminal_db')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('india_criminal_db') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-orange-600" onClick={() => { setInsuffData({ check_id: getCheckByType('india_criminal_db')?.check_id, check_type: 'india_criminal_db', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('india_criminal_db')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Global Database Check Tab */}
          {getCheckByType('global_database') && (
            <TabsContent value="global_db" className="mt-4">
              <Card className="border-indigo-200 bg-indigo-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="w-5 h-5 text-indigo-600" />
                      Global Database Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('global_database')?.status)}
                      <Badge className="bg-indigo-100 text-indigo-700">#{getCheckByType('global_database')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-indigo-700">Copied from: Personal Details (Name, DOB, Father Name, PAN, Aadhar)</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Data for Global Database Verification</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                      <div className="bg-indigo-100 p-3 rounded border border-indigo-200">
                        <p className="text-xs text-indigo-600 uppercase">PAN Number</p>
                        <p className="font-bold">{employee?.pan_number || '-'}</p>
                      </div>
                      <div className="bg-indigo-100 p-3 rounded border border-indigo-200">
                        <p className="text-xs text-indigo-600 uppercase">Aadhar Number</p>
                        <p className="font-bold">{employee?.aadhar_number || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Gender</p>
                        <p className="font-semibold">{employee?.gender || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('global_database')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('global_database')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('global_database') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-indigo-600" onClick={() => { setInsuffData({ check_id: getCheckByType('global_database')?.check_id, check_type: 'global_database', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('global_database')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Reference Check Tab */}
          {getCheckByType('reference_check') && (
            <TabsContent value="reference_check" className="mt-4">
              <Card className="border-cyan-200 bg-cyan-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-cyan-600" />
                      Reference Check
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('reference_check')?.status)}
                      <Badge className="bg-cyan-100 text-cyan-700">#{getCheckByType('reference_check')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-cyan-700">Personal References provided by Candidate</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-cyan-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Personal Reference Contact Details</h4>
                    {employee?.personal_references?.length > 0 ? (
                      <div className="space-y-3">
                        {employee.personal_references.map((ref, idx) => (
                          <div key={idx} className="bg-cyan-100 p-3 rounded border border-cyan-200">
                            <p className="text-xs text-cyan-600 uppercase mb-1">Personal Reference {idx + 1}</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                              <div><span className="text-slate-500">Name:</span> <span className="font-medium">{ref.name || '-'}</span></div>
                              <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{ref.phone || '-'}</span></div>
                              <div><span className="text-slate-500">Relation:</span> <span className="font-medium">{ref.relation || '-'}</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No personal references available</p>
                    )}
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('reference_check')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('reference_check')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('reference_check') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-cyan-600" onClick={() => { setInsuffData({ check_id: getCheckByType('reference_check')?.check_id, check_type: 'reference_check', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('reference_check')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Drug Test Tab */}
          {getCheckByType('drug_test') && (
            <TabsContent value="drug_test" className="mt-4">
              <Card className="border-pink-200 bg-pink-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-pink-600" />
                      Drug Test
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('drug_test')?.status)}
                      <Badge className="bg-pink-100 text-pink-700">#{getCheckByType('drug_test')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-pink-700">Copied from: Personal Details</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-pink-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Candidate Details for Drug Test</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Full Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                        <p className="font-semibold">{employee?.dob || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Gender</p>
                        <p className="font-semibold">{employee?.gender || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Phone</p>
                        <p className="font-semibold">{employee?.phone || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded col-span-2">
                        <p className="text-xs text-slate-500 uppercase">Present Address</p>
                        <p className="font-semibold text-sm">{[employee?.present_house_no, employee?.present_area, employee?.present_city, employee?.present_state].filter(Boolean).join(', ') || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('drug_test')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('drug_test')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('drug_test') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-pink-600" onClick={() => { setInsuffData({ check_id: getCheckByType('drug_test')?.check_id, check_type: 'drug_test', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('drug_test')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Professional Reference Tab */}
          {getCheckByType('professional_reference') && (
            <TabsContent value="professional_ref" className="mt-4">
              <Card className="border-indigo-200 bg-indigo-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-indigo-600" />
                      Professional Reference
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('professional_reference')?.status)}
                      <Badge className="bg-indigo-100 text-indigo-700">#{getCheckByType('professional_reference')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-indigo-700">Copied from: Employment History (Supervisors/Managers)</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Professional References</h4>
                    {employee?.employment_entries?.length > 0 ? (
                      <div className="space-y-3">
                        {employee.employment_entries.map((emp, idx) => (
                          <div key={idx} className="bg-indigo-100 p-3 rounded border border-indigo-200">
                            <p className="text-xs text-indigo-600 uppercase mb-1">{emp.company_name || 'Previous Employer'}</p>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div><span className="text-slate-500">Supervisor Name:</span> <span className="font-medium">{emp.rm_name || emp.supervisor_name || '-'}</span></div>
                              <div><span className="text-slate-500">Supervisor Phone:</span> <span className="font-medium">{emp.rm_contact_number || emp.supervisor_phone || '-'}</span></div>
                              <div><span className="text-slate-500">HR Email:</span> <span className="font-medium">{emp.hr_email || '-'}</span></div>
                              <div><span className="text-slate-500">HR Phone:</span> <span className="font-medium">{emp.hr_contact_number || emp.hr_phone || '-'}</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No professional references available</p>
                    )}
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('professional_reference')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('professional_reference')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('professional_reference') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-indigo-600" onClick={() => { setInsuffData({ check_id: getCheckByType('professional_reference')?.check_id, check_type: 'professional_reference', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('professional_reference')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Personal Reference Tab */}
          {getCheckByType('personal_reference') && (
            <TabsContent value="personal_ref" className="mt-4">
              <Card className="border-teal-200 bg-teal-50">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-teal-600" />
                      Personal Reference
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(getCheckByType('personal_reference')?.status)}
                      <Badge className="bg-teal-100 text-teal-700">#{getCheckByType('personal_reference')?.check_id}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-teal-700">Copied from: Personal Details</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-teal-100">
                    <h4 className="font-semibold text-slate-800 mb-3">Personal Reference Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Candidate Name</p>
                        <p className="font-semibold">{employee?.name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                        <p className="font-semibold">{employee?.father_name || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Phone</p>
                        <p className="font-semibold">{employee?.phone || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs text-slate-500 uppercase">Alternate Contact</p>
                        <p className="font-semibold">{employee?.alternate_contact || '-'}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded col-span-2">
                        <p className="text-xs text-slate-500 uppercase">Permanent Address</p>
                        <p className="font-semibold text-sm">{[employee?.permanent_house_no, employee?.permanent_area, employee?.permanent_city, employee?.permanent_state].filter(Boolean).join(', ') || '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Insufficiency Alert */}
                  {renderCheckInsufficiencyAlert('personal_reference')}
                  
                  {/* Insufficiency History Trail */}
                  {renderInsufficiencyHistory('personal_reference')}
                  
                  {/* Actions - Only show if no insufficiency */}
                  {!hasCheckInsufficiency('personal_reference') && (
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="text-teal-600" onClick={() => { setInsuffData({ check_id: getCheckByType('personal_reference')?.check_id, check_type: 'personal_reference', reason: '', description: '' }); setShowRaiseInsuff(showCaseDetail); }}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Raise Insufficiency
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveSingleCheck(caseInfo.id, getCheckByType('personal_reference')?.check_id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

            </Tabs>
          </div>

          {/* Right Side: Inline Document Preview Panel */}
          {previewDoc && (
            <div className="w-1/2 border-l pl-4">
              <div className="sticky top-0 bg-white">
                <div className="flex justify-between items-center mb-3 pb-2 border-b">
                  <div>
                    <h4 className="font-semibold text-lg">Document Preview</h4>
                    <p className="text-sm text-slate-500">{previewDoc.original_filename || previewDoc.document_type || 'Document'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => window.open(previewDoc.file_url, '_blank')}>
                      <ExternalLink className="w-4 h-4 mr-1" />
                      New Tab
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Document Switcher */}
                {(() => {
                  const allDocs = [];
                  Object.values(caseDetailData?.checkDocuments || {}).forEach(docs => {
                    allDocs.push(...docs);
                  });
                  if (allDocs.length > 1) {
                    return (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {allDocs.map((doc, idx) => (
                          <Button
                            key={idx}
                            variant={previewDoc.id === doc.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPreviewDoc(doc)}
                            className="text-xs"
                          >
                            {doc.document_type || `Doc ${idx + 1}`}
                          </Button>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Preview Area */}
                <div className="bg-slate-100 rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 400px)', minHeight: '400px' }}>
                  {previewDoc.file_url ? (
                    previewDoc.file_type?.includes('pdf') || previewDoc.file_name?.includes('.pdf') ? (
                      <iframe 
                        src={previewDoc.file_url} 
                        className="w-full h-full"
                        title="Document Preview"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center p-4">
                        <img 
                          src={previewDoc.file_url} 
                          alt={previewDoc.original_filename || 'Document'} 
                          className="max-w-full max-h-full object-contain rounded shadow-lg"
                        />
                      </div>
                    )
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <FileText className="w-16 h-16 mx-auto mb-4" />
                        <p>No preview available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => {
            // Store the case data before closing the dialog so CheckDetailsEntry can auto-load it
            setCaseForCheckEntry(showCaseDetail);
            setShowCaseDetail(null);
            setCaseDetailData(null);
            setPreviewDoc(null);
            // Navigate to data-entry tab and then to the check-details sub-tab
            setDataEntrySubTab('check-details');
            setActiveTab('data-entry');
          }} className="text-purple-600 border-purple-200 hover:bg-purple-50">
            <FileEdit className="w-4 h-4 mr-2" />
            Edit Check Details
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => {
              setInsuffData({ check_id: '', check_type: '', entry_type: '', entry_index: -1, entry_name: '', reason: '', description: '' });
              setShowRaiseInsuff(showCaseDetail);
            }} className="text-rose-600">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Raise Insufficiency
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => prepareAssignConfirmation(showCaseDetail)}>
              <UserCheck className="w-4 h-4 mr-2" />
              Approve & Assign
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render all dialogs - shared between embedded and non-embedded modes
  const renderDialogs = () => (
    <>
      {/* Duplicate Confirmation Dialog (Multi-Client Bulk Import) */}
      <Dialog open={showDuplicateDialog} onOpenChange={(open) => { if (!open) handleSkipDuplicates(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              {duplicateGroups.length} candidate{duplicateGroups.length === 1 ? '' : 's'} already exist in this entity
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 text-sm text-slate-700">
            <p className="mb-3">
              The following row{duplicateGroups.length === 1 ? '' : 's'} match an existing candidate in the same entity
              (by employee code or name). Same candidate is often re-imported for a fresh BGV cycle — so this is informational.
            </p>
            <div className="max-h-72 overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">CSV Row</TableHead>
                    <TableHead className="text-xs">Emp Code</TableHead>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Client / Entity</TableHead>
                    <TableHead className="text-xs">Match Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicateGroups.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{(d.rows || []).join(', ')}</TableCell>
                      <TableCell className="font-mono text-xs">{d.employee_code || '-'}</TableCell>
                      <TableCell className="text-xs">{d.name || '-'}</TableCell>
                      <TableCell className="text-xs">{d.client_name}{d.entity_name ? ` / ${d.entity_name}` : ''}</TableCell>
                      <TableCell className="text-xs text-amber-700">{d.match_reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              <strong>Import Anyway</strong> creates a fresh employee record and a new verification case for each row above.
              <br/>
              <strong>Skip</strong> keeps only the non-duplicate rows that were already imported in this batch.
            </p>
          </div>
          <DialogFooter className="mt-4 flex gap-2">
            <Button variant="outline" onClick={handleSkipDuplicates} data-testid="duplicate-skip-btn">
              Skip Duplicates
            </Button>
            <Button
              onClick={handleImportDuplicatesAnyway}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={bulkUploading}
              data-testid="duplicate-import-anyway-btn"
            >
              {bulkUploading ? 'Importing…' : 'Import Anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Detail Dialog */}
      <Dialog open={!!showCaseDetail} onOpenChange={(open) => { if (!open && !previewDoc) { setShowCaseDetail(null); setCaseDetailData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Case Review - {typeof showCaseDetail === 'string' ? showCaseDetail : (showCaseDetail?.id || caseDetailData?.case?.id || '')}</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="p-8 text-center">Loading case details...</div>
          ) : (
            renderCaseDetailContent()
          )}
        </DialogContent>
      </Dialog>

      {/* Identity-Change Confirmation Modal — anti-corruption guard.
          Triggered when QC Quick Edit is about to mutate name/father_name/dob/email
          on a submitted candidate. Shows a side-by-side OLD → NEW diff and forces
          a typed reason (≥5 chars) before the PUT goes through. */}
      <AlertDialog open={identityConfirm.open} onOpenChange={(o) => !o && setIdentityConfirm({ ...identityConfirm, open: false })}>
        <AlertDialogContent className="max-w-2xl border-rose-300" data-testid="identity-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-700 flex items-center gap-2">
              <span>⚠ Confirm Identity Change</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              You are about to change <strong>identity fields</strong> on a candidate who has already submitted their form.
              This is exactly the pattern that caused the <code>RTX290426_00973</code> data swap. Please review the diff below
              carefully — if this is the wrong candidate, click Cancel and re-open the correct case.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="my-3 border rounded overflow-hidden">
            <div className="bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-900 border-b">
              Identity field changes ({identityConfirm.diffs.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left px-3 py-1.5 w-32">Field</th>
                  <th className="text-left px-3 py-1.5">CURRENT (will be replaced)</th>
                  <th className="text-left px-3 py-1.5">NEW (will be saved)</th>
                </tr>
              </thead>
              <tbody>
                {identityConfirm.diffs.map((d) => (
                  <tr key={d.field} className="border-t" data-testid={`identity-diff-row-${d.field}`}>
                    <td className="px-3 py-2 text-xs font-medium font-mono">{d.field}</td>
                    <td className="px-3 py-2 text-xs text-rose-700 break-all">{String(d.from || '—')}</td>
                    <td className="px-3 py-2 text-xs text-emerald-700 break-all">{String(d.to || '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <Label htmlFor="identity-change-reason" className="text-xs">
              Reason for change <span className="text-rose-600">*</span>
              <span className="text-slate-500 font-normal"> (min 5 characters — recorded in audit log)</span>
            </Label>
            <Textarea
              id="identity-change-reason"
              data-testid="identity-change-reason-input"
              placeholder='e.g. "Candidate confirmed legal name spelling — passport copy attached"'
              value={identityConfirm.reason}
              onChange={(e) => setIdentityConfirm({ ...identityConfirm, reason: e.target.value })}
              className="mt-1"
              rows={2}
            />
          </div>

          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel data-testid="identity-confirm-cancel" disabled={savingEdit}>Cancel — wrong candidate</AlertDialogCancel>
            <AlertDialogAction
              data-testid="identity-confirm-save"
              disabled={savingEdit || (identityConfirm.reason || '').trim().length < 5}
              onClick={(e) => { e.preventDefault(); performSave(identityConfirm.reason.trim()); }}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {savingEdit ? 'Saving…' : 'I confirm — save identity change'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mirror Unlink Confirmation — May 21, 2026 */}
      <AlertDialog open={mirrorUnlinkConfirm.open} onOpenChange={(o) => !o && setMirrorUnlinkConfirm({ open: false })}>
        <AlertDialogContent className="max-w-xl" data-testid="mirror-unlink-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-700 flex items-center gap-2">
              <span>⚠ Permanent address now differs from present</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This candidate's permanent address was set as <strong>same as present</strong>. Saving this edit will
              <strong> unlink the mirror</strong>: the permanent address verification check will be reset to <strong>pending</strong>
              and re-routed for independent vendor work (the agent will see an &quot;Updated address&quot; hint on the affected
              check). Continue if this is intentional — typically when the candidate has confirmed their two addresses
              actually differ.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="my-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            If this was a typo or you only meant to fix the present address, click Cancel and re-check the permanent
            block — leave it identical to present and the mirror will stay intact.
          </div>

          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel data-testid="mirror-unlink-cancel" disabled={savingEdit}>Cancel — keep mirror</AlertDialogCancel>
            <AlertDialogAction
              data-testid="mirror-unlink-confirm-save"
              disabled={savingEdit}
              onClick={(e) => { e.preventDefault(); setMirrorUnlinkConfirm({ open: false }); performSave(); }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingEdit ? 'Saving…' : 'Unlink mirror & save'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!showRaiseInsuff} onOpenChange={() => setShowRaiseInsuff(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Raise Insufficiency
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Check *</Label>
              <Select value={insuffData.check_id} onValueChange={(v) => {
                const check = caseDetailData?.case?.checks?.find(c => c.check_id === v);
                setInsuffData({...insuffData, check_id: v, check_type: check?.check_type || ''});
              }}>
                <SelectTrigger><SelectValue placeholder="Select check type" /></SelectTrigger>
                <SelectContent>
                  {(caseDetailData?.case?.checks || [])
                    .filter(c => c && c.check_id && !c.has_insufficiency)
                    .map((check) => (
                      <SelectItem key={check.check_id} value={check.check_id}>
                        {String(check.check_type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Select value={insuffData.reason} onValueChange={(v) => setInsuffData({...insuffData, reason: v})}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Missing Document">Missing Document</SelectItem>
                  <SelectItem value="Unclear Document">Unclear Document</SelectItem>
                  <SelectItem value="Incomplete Information">Incomplete Information</SelectItem>
                  <SelectItem value="Data Mismatch">Data Mismatch</SelectItem>
                  <SelectItem value="Invalid Document">Invalid Document</SelectItem>
                  <SelectItem value="Expired Document">Expired Document</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={insuffData.description}
                onChange={(e) => setInsuffData({...insuffData, description: e.target.value})}
                placeholder="Provide details about what is missing or incorrect..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRaiseInsuff(null)}>Cancel</Button>
            <Button onClick={handleRaiseInsufficiency} className="bg-rose-600 hover:bg-rose-700">
              Raise Insufficiency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Agent Dialog */}
      {/* Approve & Assign Confirmation Dialog */}
      <Dialog open={!!showAssignConfirm} onOpenChange={() => { setShowAssignConfirm(null); setAssignConfirmData(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Before Assigning
            </DialogTitle>
          </DialogHeader>
          
          {assignConfirmData && (
            <div className="space-y-4">
              {/* Warning Banner if issues found */}
              {assignConfirmData.hasIssues && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    Issues Found - Please Review Before Proceeding
                  </div>
                  {assignConfirmData.missingFields.length > 0 && (
                    <p className="text-sm text-amber-600">• Missing data: {assignConfirmData.missingFields.join(', ')}</p>
                  )}
                  {assignConfirmData.insufficiencies.length > 0 && (
                    <p className="text-sm text-amber-600">• {assignConfirmData.insufficiencies.length} entry insufficiency(ies) pending</p>
                  )}
                  {assignConfirmData.checkInsufficiencies.length > 0 && (
                    <p className="text-sm text-amber-600">• {assignConfirmData.checkInsufficiencies.length} check insufficiency(ies) pending</p>
                  )}
                  {assignConfirmData.hasOpenInsufficiency && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bypassInsuffAssign}
                          onChange={(e) => setBypassInsuffAssign(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                          data-testid="bypass-insuff-assign-checkbox"
                        />
                        <span className="text-sm text-amber-800">
                          I confirm: proceed to assign <strong>only non-insufficient checks</strong>. Insufficient checks will remain in the Insufficiency queue until cleared.
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Case & Employee Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-500">Case Information</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p><strong>Case ID:</strong> <span className="font-mono">{assignConfirmData.caseItem?.id}</span></p>
                    <p><strong>Client:</strong> {assignConfirmData.clientName}</p>
                    <p><strong>Entity:</strong> {assignConfirmData.entityName}</p>
                    <div className="flex items-center gap-2"><strong>Status:</strong> <Badge className="bg-amber-100 text-amber-700">{assignConfirmData.caseInfo?.status}</Badge></div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-500">Employee Details</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p><strong>Name:</strong> {assignConfirmData.employee?.name || <span className="text-rose-500">Missing</span>}</p>
                    <div className="flex items-center gap-2"><strong>Category:</strong> <Badge variant="outline" className={
                      assignConfirmData.employee?.employee_category === 'fresher' ? 'bg-green-50 text-green-700' :
                      assignConfirmData.employee?.employee_category === 'intern' ? 'bg-purple-50 text-purple-700' :
                      'bg-blue-50 text-blue-700'
                    }>{assignConfirmData.employee?.employee_category || 'experienced'}</Badge></div>
                    <p><strong>Email:</strong> {assignConfirmData.employee?.email}</p>
                    <p><strong>Phone:</strong> {assignConfirmData.employee?.phone}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Checks Summary */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500">Verification Checks ({assignConfirmData.checks?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {assignConfirmData.checks?.map((check, idx) => {
                      const checkType = typeof check.check_type === 'object' ? check.check_type?.check_type : check.check_type;
                      return (
                        <Badge 
                          key={idx} 
                          variant="outline" 
                          className={check.insufficiency ? 'bg-rose-50 text-rose-700 border-rose-300' : 'bg-slate-50'}
                        >
                          {(checkType || 'unknown').replace(/_/g, ' ')}
                          {check.insufficiency && <AlertTriangle className="w-3 h-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Key Data Points */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-500">Key Data Points</CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`p-2 rounded ${assignConfirmData.employee?.pan_number ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                      <span className="text-slate-500">PAN:</span>
                      <p className="font-medium">{assignConfirmData.employee?.pan_number || 'Not Provided'}</p>
                    </div>
                    <div className={`p-2 rounded ${assignConfirmData.employee?.aadhar_number ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                      <span className="text-slate-500">Aadhar:</span>
                      <p className="font-medium">{assignConfirmData.employee?.aadhar_number || 'Not Provided'}</p>
                    </div>
                    <div className={`p-2 rounded ${(() => {
                      const presentAddr = assignConfirmData.employee?.present_address || {};
                      return assignConfirmData.employee?.present_city || presentAddr.city ? 'bg-emerald-50' : 'bg-rose-50';
                    })()}`}>
                      <span className="text-slate-500">Address:</span>
                      <p className="font-medium">{(() => {
                        const presentAddr = assignConfirmData.employee?.present_address || {};
                        return presentAddr.city || assignConfirmData.employee?.present_city || 'Not Provided';
                      })()}</p>
                    </div>
                    <div className={`p-2 rounded ${assignConfirmData.employee?.employment_entries?.length > 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                      <span className="text-slate-500">Employment:</span>
                      <p className="font-medium">{assignConfirmData.employee?.employment_entries?.length || 0} entries</p>
                    </div>
                    <div className={`p-2 rounded ${assignConfirmData.employee?.education_entries?.length > 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                      <span className="text-slate-500">Education:</span>
                      <p className="font-medium">{assignConfirmData.employee?.education_entries?.length || 0} entries</p>
                    </div>
                    <div className="p-2 rounded bg-slate-50">
                      <span className="text-slate-500">Declaration:</span>
                      <p className="font-medium">{assignConfirmData.employee?.declaration_signed ? '✓ Signed' : 'Not Signed'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="flex justify-between border-t pt-4">
            <Button variant="outline" onClick={() => { setShowAssignConfirm(null); setAssignConfirmData(null); }}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {assignConfirmData?.hasIssues && (
                <Button 
                  variant="outline" 
                  className="text-amber-600 border-amber-300"
                  onClick={() => {
                    setShowCaseDetail(showAssignConfirm);
                    setShowAssignConfirm(null);
                  }}
                >
                  Review Case First
                </Button>
              )}
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={assignConfirmData?.hasOpenInsufficiency && !bypassInsuffAssign}
                onClick={() => {
                  setShowAssignAgent(showAssignConfirm);
                  setShowAssignConfirm(null);
                }}
                data-testid="proceed-to-assign-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Proceed to Assign
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Agent Dialog */}
      <Dialog open={!!showAssignAgent} onOpenChange={() => setShowAssignAgent(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <UserCheck className="w-5 h-5" />
              Approve & Assign to Agent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Case ID: <span className="font-mono font-medium">{showAssignAgent?.id}</span></p>
              <p className="text-sm text-slate-500">Employee: <span className="font-medium">{showAssignAgent?.employee_name}</span></p>
            </div>
            
            <div>
              <Label>Assign to Agent</Label>
              <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                {teamMembers.filter(m => ['verification_team', 'inhouse_verification', 'vendor_management'].includes(m.role) && m.is_active !== false).map((member) => (
                  <div 
                    key={member.id}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => handleApproveAndAssign(showAssignAgent.id, member.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <UserCheck className="w-4 h-4 mr-1" />
                        Assign
                      </Button>
                    </div>
                  </div>
                ))}
                {teamMembers.filter(m => ['verification_team', 'inhouse_verification', 'vendor_management'].includes(m.role) && m.is_active !== false).length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No verification agents available</p>
                )}
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveAll(showAssignAgent.id)}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Auto-Approve & Assign
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">Automatically assigns to available agents based on workload</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignAgent(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Employee (Offline Entry)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEmployee}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client *</Label>
                  <Select 
                    value={selectedClient} 
                    onValueChange={(v) => { 
                      setSelectedClient(v); 
                      setSelectedEntity('');
                      setSelectedContact('');
                      setEntityContacts([]);
                      setNewEmployee({...newEmployee, entity_id: '', entity_contact_id: ''});
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Entity *</Label>
                  <Select 
                    value={newEmployee.entity_id} 
                    onValueChange={handleEntityChange}
                    disabled={!selectedClient}
                  >
                    <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                    <SelectContent>
                      {getClientEntities(selectedClient).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Entity Contact / HR</Label>
                <Select 
                  value={newEmployee.entity_contact_id} 
                  onValueChange={(v) => {
                    setSelectedContact(v);
                    setNewEmployee({...newEmployee, entity_contact_id: v});
                  }}
                  disabled={!newEmployee.entity_id}
                >
                  <SelectTrigger><SelectValue placeholder="Select entity contact (optional)" /></SelectTrigger>
                  <SelectContent>
                    {entityContacts.length === 0 ? (
                      <SelectItem value="none" disabled>No contacts found</SelectItem>
                    ) : (
                      entityContacts.map(contact => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name} ({contact.email})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Package Selection - Always visible after entity selection */}
              {newEmployee.entity_id && (
                <div>
                  <Label>Package *</Label>
                  {(() => {
                    const selectedEntity = entities.find(e => e.id === newEmployee.entity_id);
                    const entityPackageId = selectedEntity?.package_id || selectedEntity?.default_package_id;
                    const entityPackage = packages.find(p => p.id === entityPackageId);
                    const availablePackages = entityPackage ? [entityPackage] : getFilteredPackages(selectedClient, packageSearch);
                    
                    return (
                      <Select 
                        value={newEmployee.package_id} 
                        onValueChange={(v) => {
                          const selectedPkg = packages.find(p => p.id === v);
                          const initialChecks = selectedPkg?.checks?.map(c => ({
                            check_type: typeof c === 'string' ? c : c.check_type,
                            enabled: true,
                            quantity: (typeof c === 'object' ? c.quantity : 1) || 1,
                            selectedSubOptions: typeof c === 'object' ? (c.sub_options || []) : [],
                            sub_checks: typeof c === 'object' ? (c.sub_options || []) : []
                          })) || [];
                          setNewEmployee({...newEmployee, package_id: v, custom_checks: initialChecks});
                        }}
                      >
                        <SelectTrigger data-testid="qc-package-select"><SelectValue placeholder="Select package" /></SelectTrigger>
                        <SelectContent>
                          {availablePackages.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                              {p.id === entityPackageId ? ' (Entity Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                  {newEmployee.entity_id && (
                    <p className="text-xs text-blue-600 mt-1">
                      {entities.find(e => e.id === newEmployee.entity_id)?.package_id 
                        ? 'Showing package assigned to selected entity'
                        : 'Select an entity to see its assigned package'}
                    </p>
                  )}
                </div>
              )}

              {/* Customize Verification Checks */}
              {newEmployee.package_id && newEmployee.custom_checks?.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-lg border" data-testid="qc-customize-checks">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-slate-700 font-medium">Customize Verification Checks</Label>
                    <span className="text-xs text-slate-500">
                      {newEmployee.custom_checks.filter(c => c.enabled).reduce((sum, c) => sum + (c.quantity || 1), 0)} checks selected
                    </span>
                  </div>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {newEmployee.custom_checks.map((check, idx) => {
                      const checkTypeInfo = CHECK_TYPES_WITH_SUB_OPTIONS[check.check_type];
                      const hasSubOptions = !!checkTypeInfo?.subOptions;
                      const selectedSubOptions = check.selectedSubOptions || [];
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${
                            check.enabled ? 'bg-white border-blue-200' : 'bg-slate-100 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-3 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={check.enabled}
                                onChange={(e) => {
                                  const updatedChecks = [...newEmployee.custom_checks];
                                  updatedChecks[idx].enabled = e.target.checked;
                                  if (!e.target.checked) {
                                    updatedChecks[idx].selectedSubOptions = [];
                                    updatedChecks[idx].quantity = 1;
                                  }
                                  setNewEmployee({...newEmployee, custom_checks: updatedChecks});
                                }}
                                className="rounded border-slate-300"
                              />
                              <span className={`font-medium ${check.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                                {checkTypeInfo?.name || CHECK_DISPLAY_NAMES[check.check_type] || check.check_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            </label>
                            {check.enabled && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 font-semibold">
                                Qty: {check.quantity || 1}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Sub-Options */}
                          {check.enabled && hasSubOptions && (
                            <div className="mt-3 ml-8 pl-4 border-l-2 border-blue-200">
                              <p className="text-xs text-slate-500 mb-2">Select options (quantity auto-calculated):</p>
                              <div className="flex flex-wrap gap-2">
                                {checkTypeInfo.subOptions.map((subOption) => {
                                  const isSelected = selectedSubOptions.includes(subOption.id);
                                  return (
                                    <label 
                                      key={subOption.id}
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-colors text-sm ${
                                        isSelected
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-white border border-slate-300 text-slate-700 hover:border-blue-400'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const updatedChecks = [...newEmployee.custom_checks];
                                          const currentSubOptions = updatedChecks[idx].selectedSubOptions || [];
                                          let newSubOptions;
                                          if (e.target.checked) {
                                            newSubOptions = [...currentSubOptions, subOption.id];
                                          } else {
                                            newSubOptions = currentSubOptions.filter(id => id !== subOption.id);
                                          }
                                          updatedChecks[idx].selectedSubOptions = newSubOptions;
                                          updatedChecks[idx].sub_checks = newSubOptions;
                                          updatedChecks[idx].quantity = newSubOptions.length > 0 ? newSubOptions.length : 1;
                                          setNewEmployee({...newEmployee, custom_checks: updatedChecks});
                                        }}
                                        className="sr-only"
                                      />
                                      {subOption.name}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Manual Qty for checks without sub-options */}
                          {check.enabled && !hasSubOptions && (
                            <div className="mt-2 ml-8 flex items-center gap-2">
                              <span className="text-xs text-slate-500">Quantity:</span>
                              <select
                                value={check.quantity || 1}
                                onChange={(e) => {
                                  const updatedChecks = [...newEmployee.custom_checks];
                                  updatedChecks[idx].quantity = parseInt(e.target.value);
                                  setNewEmployee({...newEmployee, custom_checks: updatedChecks});
                                }}
                                className="w-16 text-sm border rounded px-2 py-1"
                              >
                                {[1, 2, 3, 4, 5].map(n => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Toggle checks on/off and select sub-options. Only enabled checks will be created in the case.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={newEmployee.name} onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Father's Name *</Label>
                  <Input value={newEmployee.father_name} onChange={(e) => setNewEmployee({...newEmployee, father_name: e.target.value})} required />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date of Birth *</Label>
                  <Input type="date" value={newEmployee.dob} onChange={(e) => setNewEmployee({...newEmployee, dob: e.target.value})} required />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={newEmployee.email} onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} required />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input value={newEmployee.phone} onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})} required />
                </div>
              </div>

              <div>
                <Label>Employee ID / Offer ID</Label>
                <Input value={newEmployee.employee_code} onChange={(e) => setNewEmployee({...newEmployee, employee_code: e.target.value})} placeholder="Optional" />
              </div>

              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-medium">Options</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Bypass Declaration</Label>
                    <p className="text-xs text-slate-500">Skip online declaration requirement</p>
                  </div>
                  <Switch checked={newEmployee.bypass_declaration} onCheckedChange={(v) => setNewEmployee({...newEmployee, bypass_declaration: v})} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Send Offline Signing Email</Label>
                    <p className="text-xs text-slate-500">Email candidate for offline declaration signing</p>
                  </div>
                  <Switch checked={newEmployee.send_offline_signing} onCheckedChange={(v) => setNewEmployee({...newEmployee, send_offline_signing: v})} />
                </div>
                
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Send Invitation Email to Candidate</Label>
                      <p className="text-xs text-slate-500">Email candidate with portal login credentials</p>
                    </div>
                    <Switch 
                      checked={newEmployee.send_invitation}
                      onCheckedChange={(v) => {
                        setNewEmployee({...newEmployee, send_invitation: v});
                      }}
                    />
                  </div>
                  
                  {newEmployee.send_invitation && (
                    <div className="p-3 bg-white rounded border space-y-2">
                      <Label className="text-sm font-medium">Candidate Login Password</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="qc_generate_password"
                          checked={newEmployee.generate_password}
                          onChange={(e) => setNewEmployee({...newEmployee, generate_password: e.target.checked, password: ''})}
                          className="w-4 h-4"
                        />
                        <label htmlFor="qc_generate_password" className="text-xs text-slate-600 cursor-pointer">Auto-generate</label>
                      </div>
                      {!newEmployee.generate_password && (
                        <Input
                          type="text"
                          value={newEmployee.password}
                          onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                          placeholder="Enter custom password"
                          className="text-sm"
                        />
                      )}
                      <p className="text-xs text-slate-400">
                        {newEmployee.generate_password 
                          ? "Default password: Welcome@2026 (candidates must reset on first login)" 
                          : "Set a custom password for candidate login"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddEmployee(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Employee</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={(open) => { setShowBulkImport(open); if (!open) { setBulkData([]); setBulkClientId(''); setBulkEntityId(''); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Import Employees
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Multi-Client Mode Toggle (Veecheck Feb 2026) */}
            <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <div>
                <div className="font-semibold text-emerald-900 text-sm">Multi-Client Bulk Import</div>
                <div className="text-xs text-emerald-700 mt-0.5">
                  Toggle ON to import candidates for many clients/entities in ONE file. Each row carries its own Client / Entity / Package. Up to 3 address rows per candidate fold into one case.
                </div>
              </div>
              <label className="inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  data-testid="multi-client-mode-toggle"
                  checked={multiClientMode}
                  onChange={(e) => { setMultiClientMode(e.target.checked); setBulkData([]); setBulkErrors([]); }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-300 peer-checked:bg-emerald-600 rounded-full peer relative transition-colors">
                  <div className={`absolute top-0.5 left-0.5 bg-white border border-slate-300 rounded-full h-5 w-5 transition-transform ${multiClientMode ? 'translate-x-5' : ''}`} />
                </div>
              </label>
            </div>

            {/* Step 1: Select Client & Entity (single-client mode only) */}
            {!multiClientMode && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Step 1: Select Client & Entity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Client *</Label>
                    <Select value={bulkClientId} onValueChange={(v) => { setBulkClientId(v); setBulkEntityId(''); setBulkPackageId(''); }}>
                      <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Entity *</Label>
                    <Select value={bulkEntityId} onValueChange={(v) => {
                      setBulkEntityId(v);
                      const ent = entities.find(e => e.id === v);
                      const entPkgId = ent?.package_id || ent?.default_package_id;
                      setBulkPackageId(entPkgId || '');
                    }} disabled={!bulkClientId}>
                      <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                      <SelectContent>
                        {getClientEntities(bulkClientId).map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Package *</Label>
                    <Select value={bulkPackageId} onValueChange={setBulkPackageId} disabled={!bulkEntityId}>
                      <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                      <SelectContent>
                        {getFilteredPackages(bulkClientId).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 2: Download Template & Upload */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{multiClientMode ? 'Download Multi-Client Template & Upload CSV' : 'Step 2: Download Template & Upload CSV'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <Button variant="outline" onClick={downloadBulkTemplate}>
                    <Download className="w-4 h-4 mr-2" />
                    {multiClientMode ? 'Download Multi-Client Template' : 'Download Template'}
                  </Button>
                  <span className="text-sm text-slate-500">{multiClientMode ? 'One row per address — multiple addresses for same candidate share employee_code. All fields optional except Client Name.' : 'CSV with all required columns'}</span>
                </div>
                
                <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkFileUpload}
                    ref={fileInputRef}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500 mt-2">Upload CSV file with employee data including basic details and addresses</p>
                </div>

                {/* Template Columns Info */}
                {multiClientMode ? (
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                    <p className="text-sm font-medium text-emerald-900 mb-2">Multi-Client CSV Columns (all optional except Client Name):</p>
                    <div className="grid grid-cols-3 gap-2 text-xs text-emerald-800">
                      <div>
                        <p className="font-medium">Tenant Resolution:</p>
                        <p>Client Name, Entity name, Package</p>
                      </div>
                      <div>
                        <p className="font-medium">Candidate Details:</p>
                        <p>employee_code, joining_company_name, name, father_name, dob, phone, alternate_number, case_received_date</p>
                      </div>
                      <div>
                        <p className="font-medium">Address (one row per address):</p>
                        <p>address_type <span className="text-emerald-600">(Present / Permanent / Past 1 / Past 2)</span>, address_line1, address_line2, city, state, pincode, duration</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-emerald-700 mt-2">Multiple address rows with the same Client + employee_code fold into ONE case. Missing address sub-checks are auto-marked Not Applicable.</p>
                  </div>
                ) : (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-2">CSV Columns:</p>
                    <div className="grid grid-cols-3 gap-2 text-xs text-blue-700">
                      <div>
                        <p className="font-medium">Basic Details:</p>
                        <p>employee_code, name, father_name, dob, gender, email, phone, alternate_contact, pan_number, aadhar_number</p>
                      </div>
                      <div>
                        <p className="font-medium">Present Address:</p>
                        <p>present_address_line1, present_address_line2, present_city, present_state, present_pincode, present_duration</p>
                      </div>
                      <div>
                        <p className="font-medium">Permanent Address:</p>
                        <p>permanent_address_line1, permanent_address_line2, permanent_city, permanent_state, permanent_pincode</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 3: Preview Data */}
            {bulkData.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Step 3: Preview Data ({bulkData.length} rows)</span>
                    <div className="flex gap-2">
                      <Badge className="bg-emerald-100 text-emerald-700">{bulkData.filter(r => r.valid).length} Valid</Badge>
                      <Badge className="bg-rose-100 text-rose-700">{bulkData.filter(r => !r.valid).length} Invalid</Badge>
                      <Badge className="bg-amber-100 text-amber-700">{bulkData.filter(r => r.insufficiencies?.length > 0).length} With Warnings</Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-80 overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Status</TableHead>
                          {multiClientMode ? (
                            <>
                              <TableHead>Client</TableHead>
                              <TableHead>Entity</TableHead>
                              <TableHead>Package</TableHead>
                              <TableHead>Emp Code</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Joining Company</TableHead>
                              <TableHead>Alternate #</TableHead>
                              <TableHead>Address Type</TableHead>
                              <TableHead>City / State</TableHead>
                            </>
                          ) : (
                            <>
                              <TableHead>Employee Code</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead>Present City</TableHead>
                            </>
                          )}
                          <TableHead>Errors/Warnings</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bulkData.map((row, idx) => (
                          <TableRow key={idx} className={!row.valid ? 'bg-rose-50' : row.insufficiencies?.length > 0 ? 'bg-amber-50' : ''}>
                            <TableCell>{row.row_number}</TableCell>
                            <TableCell>
                              {row.valid ? (
                                row.insufficiencies?.length > 0 ? (
                                  <Badge className="bg-amber-100 text-amber-700">Warning</Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700">Valid</Badge>
                                )
                              ) : (
                                <Badge className="bg-rose-100 text-rose-700">Invalid</Badge>
                              )}
                            </TableCell>
                            {multiClientMode ? (
                              <>
                                <TableCell className="text-sm">{row.client_name || '-'}</TableCell>
                                <TableCell className="text-sm">{row.entity_name || '-'}</TableCell>
                                <TableCell className="text-sm">{row.package || '-'}</TableCell>
                                <TableCell className="font-mono text-xs">{row.employee_code || '-'}</TableCell>
                                <TableCell className="font-medium">{row.name || '-'}</TableCell>
                                <TableCell className="text-sm">{row.joining_company_name || '-'}</TableCell>
                                <TableCell className="text-sm font-mono">{row.alternate_number || '-'}</TableCell>
                                <TableCell>
                                  {row.address_type ? (
                                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">{row.address_type}</Badge>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {(row.city || row.state) ? `${row.city || ''}${row.city && row.state ? ', ' : ''}${row.state || ''}` : '-'}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>{row.employee_code || '-'}</TableCell>
                                <TableCell className="font-medium">{row.name || '-'}</TableCell>
                                <TableCell>{row.email || '-'}</TableCell>
                                <TableCell>{row.phone || '-'}</TableCell>
                                <TableCell>{row.present_city || '-'}</TableCell>
                              </>
                            )}
                            <TableCell>
                              <div className="text-xs space-y-1">
                                {row.errors?.map((err, i) => (
                                  <p key={i} className="text-rose-600">{err}</p>
                                ))}
                                {row.insufficiencies?.map((ins, i) => (
                                  <p key={i} className="text-amber-600">{ins}</p>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {bulkData.length > 0 && !multiClientMode && (
            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="sendBulkEmails"
                  checked={sendBulkEmails}
                  onCheckedChange={setSendBulkEmails}
                  data-testid="send-bulk-emails-checkbox"
                />
                <label htmlFor="sendBulkEmails" className="text-sm font-medium cursor-pointer">
                  Send invitation emails with login credentials
                </label>
              </div>
              <p className="text-xs text-slate-600 mt-2 ml-6">
                Each candidate will receive an email with:
                <br />&#8226; Default password: <strong>Welcome@2026</strong> (candidates must reset on first login)
                <br />&#8226; Instructions to change password on first login
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkImport(false); setBulkData([]); }}>Cancel</Button>
            <Button 
              onClick={multiClientMode ? handleBulkImportMulti : handleBulkImport} 
              data-testid="bulk-import-submit-btn"
              disabled={bulkUploading || bulkData.filter(r => r.valid !== false).length === 0 || (!multiClientMode && !bulkEntityId)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {bulkUploading
                ? 'Importing...'
                : multiClientMode
                  ? `Import ${bulkData.filter(r => r.valid !== false).length} Rows (Multi-Client)`
                  : `Import ${bulkData.filter(r => r.valid).length} Employees`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-Down Data Modal */}
      <Dialog open={!!showDrillDown} onOpenChange={() => setShowDrillDown(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              {drillDownTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {drillDownData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No data found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDownData.map((item) => (
                    <TableRow key={item.id || item.case_id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">{item.id || item.case_id || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.employee_name || item.name || '-'}</p>
                          <p className="text-xs text-slate-500">{item.employee_email || item.email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.client_name || '-'}</p>
                          <p className="text-xs text-slate-500">{item.entity_name || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {item.created_at ? formatDateDDMMYYYY(item.created_at) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setShowDrillDown(null);
                            if (item.id || item.case_id) {
                              handleOpenCaseDetail({ id: item.id || item.case_id, ...item });
                            }
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDrillDown(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insufficiency Aging Drilldown Dialog */}
      <Dialog open={!!insuffAgingDrilldown} onOpenChange={(o) => { if (!o) setInsuffAgingDrilldown(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Insufficiency Aging — {insuffAgingDrilldown?.bucket?.label}
              <span className="text-sm font-normal text-slate-500">({insuffAgingDrilldown?.bucket?.count || 0} case{insuffAgingDrilldown?.bucket?.count === 1 ? '' : 's'})</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {(!insuffAgingDrilldown?.bucket?.cases || insuffAgingDrilldown.bucket.cases.length === 0) ? (
              <p className="text-sm text-slate-500 text-center py-8">No cases in this bucket</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-center">Insuff Count</TableHead>
                    <TableHead className="text-right">Age (days)</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insuffAgingDrilldown.bucket.cases.map(item => (
                    <TableRow key={item.case_id} data-testid={`insuff-aging-row-${item.case_id}`}>
                      <TableCell className="font-mono text-xs">{item.case_id}</TableCell>
                      <TableCell className="font-medium">{item.employee_name}</TableCell>
                      <TableCell className="text-slate-600">{item.client_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-rose-100 text-rose-700">{item.insufficiency_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <span className={item.age_days > 45 ? 'text-rose-700' : (item.age_days > 20 ? 'text-amber-700' : 'text-emerald-700')}>
                          {item.age_days}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setInsuffAgingDrilldown(null);
                            setActiveTab('qc-cases');
                            setPendingQCSearch(item.case_id);
                          }}
                          data-testid={`insuff-aging-open-${item.case_id}`}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setInsuffAgingDrilldown(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stop Check Dialog */}
      <Dialog open={!!showStopCheck} onOpenChange={(o) => { if (!o) { setShowStopCheck(null); setStopCheckReason(''); setStopCheckDescription(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <StopCircle className="w-5 h-5" />
              Stop Verification Check
            </DialogTitle>
          </DialogHeader>
          
          {showStopCheck && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                This action will stop all in-progress verification checks for this case.
                This action cannot be undone.
              </div>
              
              <div>
                <Label>Case ID</Label>
                <p className="text-sm font-medium text-slate-900 font-mono">{showStopCheck.id}</p>
              </div>
              
              <div>
                <Label>Employee</Label>
                <p className="text-sm font-medium text-slate-900">{showStopCheck.employee_name || 'N/A'}</p>
                <p className="text-xs text-slate-500">{showStopCheck.employee_email || ''}</p>
              </div>
              
              <div>
                <Label>Reason for Stopping *</Label>
                <Select value={stopCheckReason} onValueChange={setStopCheckReason}>
                  <SelectTrigger data-testid="qc-stop-check-reason-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                    <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                    <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                    <SelectItem value="other">Other (Specify)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {stopCheckReason === 'other' && (
                <div>
                  <Label>Please Specify *</Label>
                  <Textarea
                    placeholder="Provide details for stopping the check..."
                    value={stopCheckDescription}
                    onChange={(e) => setStopCheckDescription(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
              
              {stopCheckReason && stopCheckReason !== 'other' && (
                <div>
                  <Label>Additional Notes (Optional)</Label>
                  <Textarea
                    placeholder="Any additional details..."
                    value={stopCheckDescription}
                    onChange={(e) => setStopCheckDescription(e.target.value)}
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowStopCheck(null); setStopCheckReason(''); setStopCheckDescription(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleStopCheck} 
              disabled={stoppingCheck || !stopCheckReason || (stopCheckReason === 'other' && !stopCheckDescription)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="qc-confirm-stop-check-btn"
            >
              {stoppingCheck ? 'Stopping...' : 'Stop Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry-Level Insufficiency Dialog */}
      <Dialog open={!!showEntryInsuff} onOpenChange={(o) => { if (!o) { setShowEntryInsuff(null); setInsuffData({ check_id: '', check_type: '', entry_type: '', entry_index: -1, entry_name: '', reason: '', description: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500" />
              Raise Entry Insufficiency
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Entry</p>
              <p className="font-semibold">{insuffData.entry_name}</p>
              <p className="text-sm text-slate-500 capitalize">{insuffData.entry_type?.replace('_', ' ')}</p>
            </div>

            <div>
              <Label>Reason *</Label>
              <Select value={insuffData.reason} onValueChange={(v) => setInsuffData(prev => ({ ...prev, reason: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="missing_document">Missing Document</SelectItem>
                  <SelectItem value="incorrect_data">Incorrect Data</SelectItem>
                  <SelectItem value="incomplete_data">Incomplete Data</SelectItem>
                  <SelectItem value="document_mismatch">Document Mismatch</SelectItem>
                  <SelectItem value="unverifiable_info">Unverifiable Information</SelectItem>
                  <SelectItem value="contact_not_reachable">Contact Not Reachable</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the issue in detail..."
                value={insuffData.description}
                onChange={(e) => setInsuffData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEntryInsuff(null); setInsuffData({ check_id: '', check_type: '', entry_type: '', entry_index: -1, entry_name: '', reason: '', description: '' }); }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleRaiseEntryInsufficiency(showEntryInsuff.id)}
              disabled={!insuffData.reason}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Raise Insufficiency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={showAuditTrail} onOpenChange={setShowAuditTrail}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Audit Trail - Change History
            </DialogTitle>
          </DialogHeader>
          {auditTrailData.length > 0 ? (
            <div className="space-y-3">
              {auditTrailData.map((entry, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={
                        entry.change_type === 'personal' ? 'bg-blue-100 text-blue-700' :
                        entry.change_type === 'address' ? 'bg-purple-100 text-purple-700' :
                        entry.change_type === 'employment' ? 'bg-amber-100 text-amber-700' :
                        entry.change_type === 'education' ? 'bg-green-100 text-green-700' :
                        entry.change_type === 'id_check' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {entry.change_type?.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <span className="text-sm font-medium">{entry.field_name?.replace(/_/g, ' ')}</span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {entry.created_at ? new Date(entry.created_at).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-2 bg-rose-50 rounded border border-rose-100">
                      <p className="text-xs text-rose-600 uppercase mb-1">Old Value</p>
                      <p className="font-medium text-rose-800">{entry.old_value || '-'}</p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded border border-emerald-100">
                      <p className="text-xs text-emerald-600 uppercase mb-1">New Value</p>
                      <p className="font-medium text-emerald-800">{entry.new_value || '-'}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <User className="w-3 h-3" />
                    <span>{entry.changed_by_name} ({entry.changed_by_role?.replace('_', ' ')})</span>
                    {entry.change_reason && (
                      <Badge variant="outline" className="ml-2 text-xs">{entry.change_reason}</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No changes recorded yet</p>
              <p className="text-sm">Changes made to employee data will be tracked here</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditTrail(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // In embedded mode (used inside QCDashboard), just render the content directly with dialogs
  if (embeddedMode) {
    return <>{renderContent()}{renderDialogs()}</>;
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar
        title="QC & Approval"
        menuItems={menuItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        user={user}
        onLogout={onLogout}
        onSwitchAccount={onSwitchAccount}
        hideUserSection={true}
        groupedMenu={true}
      />
      <main className="flex-1 overflow-auto">
        {/* Header with User Info, Notification & Logout - Matching Agency Admin */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-slate-800">QC & Approval Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user.name}</span>
            <NotificationCenter />
            <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-button" className="text-slate-600 hover:text-slate-800">
              <span className="mr-2">Logout</span>
            </Button>
          </div>
        </div>
        <div className="p-8">
          {renderContent()}
        </div>
      </main>
      {renderDialogs()}
    </div>
  );
}
